import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { api, getApiErrorMessage } from '../api';
import { Button, Card, EmptyState, Input, Loading, Money, PageHeader, Select, Table } from '../ui';
import { SupplierRosterModal } from '../components/supplier-roster-modal';
import type {
  FormalProductMaster,
  ImportBatchRecord,
  ProductFamilyItem,
  DrugCandidateReviewItem,
  DrugEnrichmentJob,
  DrugEnrichmentJobItem,
  DrugEnrichmentKpi,
  TfdaSyncStatus,
  IngredientStagingData,
  ProductCategory,
  ProductSubcategory,
  ProductSupplierTermItem,
  Vendor,
} from '../shared/types';
import {
  HUI_SHENG_CATEGORY_MAP,
  normalizeBarcodeCandidate,
  parseProductMasterTxt,
  type ParsedTxtRow,
} from '../shared/txt-parser';
import type { IngredientSearchPlan, IngredientSearchQuery } from '../shared/product-enrichment';
import type { Notify } from '../pages';

type CandidateSearchRun = {
  id: string;
  provider: string;
  providerStatus: string;
  searchNameNormalized: string;
  supplementalKeyword: string;
  basis: IngredientSearchPlan['basis'];
  queries: Array<
    IngredientSearchQuery & {
      executed: boolean;
      resultCount: number;
      status: string;
    }
  >;
  executedQueryCount: number;
  resultCount: number;
  createdAt?: string;
  completedAt?: string | null;
  productType: string;
  aiProvider: string;
  providerTrace: Array<{
    provider: string;
    state: string;
    fromCache?: boolean;
    reasonCode?: string;
  }>;
  sourceComparison: {
    hasConflict?: boolean;
    summary?: string;
    differences?: Array<{
      field: string;
      ingredient: string;
      sourceA: string;
      sourceB: string;
      valueA: string;
      valueB: string;
    }>;
    warnings?: string[];
  };
  identityDebug: {
    normalizedProductName: string;
    baseProductName: string;
    specification: string;
    salesUnit: string;
    barcodeStatus: string;
    barcodeRawValues: string[];
    validGtins: string[];
    brandCandidate: string;
    brandCandidateMatchedAlias: string;
  };
};

type CandidateDrawerData = {
  product: any;
  sources: any[];
  ingredients: any[];
  ingredientVersions: any[];
  searchPlan: IngredientSearchPlan;
  searchRun: CandidateSearchRun | null;
  staging?: IngredientStagingData | null;
  labelAttachments: Array<{
    id: string;
    original_name: string;
    content_type: string;
    size_bytes: number;
    created_at: string;
    view_url: string;
  }>;
};

const SEARCH_PROVIDER_STATUS_LABELS: Record<string, string> = {
  PLANNED: '已建立搜尋計畫',
  FOUND_COMPLETE: '已找到完整成分候選',
  FOUND_PARTIAL: '已找到可信部分成分',
  FOUND_MULTIPLE: '已找到多個可信來源',
  FOUND_PRODUCT_NO_FULL_INGREDIENTS: '已找到商品，但沒有完整成分',
  NO_MATCH: '來源與商品身分不相符',
  NO_SOURCE: '目前沒有可用來源',
  SOURCE_FETCH_FAILED: '來源暫時讀取失敗',
  SOURCE_CONFLICT: '可信來源內容不一致',
  FOUND_INTERNAL: '已使用內部確認資料',
  FOUND_OFFICIAL: '已查得 TFDA 官方資料',
  FOUND_BRAND_SITE: '已查得品牌官方網站',
  FOUND_MANUAL_URL: '已解析採購提供的來源網址',
  NOT_FOUND: '目前未找到可靠資料',
  SOURCE_RESTRICTED: '來源限制存取或暫時無法讀取',
  AMBIGUOUS: '商品身分有多筆可能資料',
  INGREDIENT_NOT_FOUND: '已找到商品，但來源未載明成分',
  SKIPPED_BATCH: '批次作業未執行外部來源查找',
  ERROR: '查找暫時無法完成',
};

const INGREDIENT_COMPLETENESS_LABELS: Record<string, string> = {
  FULL: '完整成分',
  COMPLETE: '完整成分（舊資料）',
  KEY_INGREDIENTS_ONLY: '僅關鍵成分',
  PARTIAL: '部分成分',
  NUTRITION_ONLY: '僅營養標示',
  MATERIAL_ONLY: '僅材質／組成',
  UNKNOWN: '完整度待確認',
};

const INGREDIENT_SOURCE_LEVEL_LABELS: Record<string, string> = {
  A1_GOVERNMENT: 'A1 政府官方',
  A2_BRAND_OFFICIAL: 'A2 品牌官方',
  A3_MANUFACTURER_OFFICIAL: 'A3 製造商官方',
  B_RETAILER: 'B 公開零售通路',
  C_OTHER: 'C 其他來源',
};

const SEARCH_QUERY_STATUS_LABELS: Record<string, string> = {
  PLANNED: '待執行',
  SUCCESS: '已搜尋',
  NO_RESULTS: '已搜尋／無結果',
  ERROR: '搜尋失敗',
  SKIPPED: '未執行',
};

const MATCHED_BY_LABELS: Record<string, string> = {
  GTIN: '國際條碼',
  LICENSE_NUMBER: '許可證字號',
  NAME_SPEC: '商品名稱與規格',
  NAME_MANUFACTURER: '商品名稱與製造商',
  MANUAL: '人工確認',
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  WESTERN_DRUG: '藥品',
  CHINESE_MEDICINE: '中藥',
  HEALTH_FOOD: '健康食品',
  GENERAL_FOOD: '一般食品',
  SPECIAL_NUTRITION: '特殊營養食品',
  MEDICAL_DEVICE: '醫療器材',
  COSMETICS: '美粧品',
  ENVIRONMENTAL_AGENT: '環境用藥',
  GENERAL_GOODS: '一般用品',
};

const LOOKUP_STAGES = [
  { key: 'INTERNAL', label: '惠生已確認資料', providers: ['InternalIngredientProvider'] },
  {
    key: 'GOVERNMENT',
    label: '政府官方資料',
    providers: ['TfdaOfficialProvider'],
  },
  {
    key: 'COMMONHEALTH',
    label: '康健藥品精準搜尋',
    providers: ['CommonHealthProvider'],
  },
  {
    key: 'BRAND',
    label: '品牌／製造商官網',
    providers: ['BrandSiteProvider'],
  },
  {
    key: 'SUPPLEMENT',
    label: '商品標示／人工網址',
    providers: ['VendorDocumentProvider', 'ManualUrlProvider'],
  },
] as const;

function manualGoogleSearchUrl(query: string): string {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

function displayBarcodeCandidate(value: string | null | undefined): string {
  const result = normalizeBarcodeCandidate(value);
  if (result.status === 'VALID' || result.status === 'REPAIRED') return result.value;
  if (result.status === 'BARCODE_REPAIR_REQUIRED') return '條碼待修復';
  if (result.status === 'INVALID_GTIN') return '條碼格式異常';
  return '無';
}

export interface EditableIngredientItem {
  id: string;
  name: string;
  amount: string;
  unit: string;
}

export const COMMON_UNITS = [
  'mg',
  'g',
  'ml',
  'mcg',
  'IU',
  '%',
  '毫克',
  '公克',
  '毫升',
  '微克',
  '滴',
  '片',
  '粒',
  '億CFU',
];

export const COMMON_INGREDIENT_TEMPLATES = [
  { label: '+ 乙醯胺酚 (500mg)', name: 'Acetaminophen (乙醯胺酚)', amount: '500', unit: 'mg' },
  { label: '+ 布洛芬 (200mg)', name: 'Ibuprofen (布洛芬)', amount: '200', unit: 'mg' },
  { label: '+ 維生素C (1000mg)', name: 'Vitamin C (維生素C)', amount: '1000', unit: 'mg' },
  { label: '+ 咖啡因 (30mg)', name: 'Caffeine (咖啡因)', amount: '30', unit: 'mg' },
  { label: '+ 酒精 (75%)', name: 'Ethanol (酒精 75%)', amount: '75', unit: '%' },
  { label: '+ 葉黃素 (30mg)', name: 'Lutein (葉黃素)', amount: '30', unit: 'mg' },
  { label: '+ 益生菌 (300億)', name: 'Probiotics (複合益生菌)', amount: '300', unit: '億CFU' },
];

export function parseIngredientsFromRawText(
  text: string,
): Array<{ name: string; amount: string; unit: string }> {
  if (!text) return [];
  const parts = text
    .split(/[\n,;；、/／]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const results: Array<{ name: string; amount: string; unit: string }> = [];

  for (const part of parts) {
    const match = part.match(
      /^(.+?)\s*[:：]?\s*([0-9]+(?:\.[0-9]+)?)\s*(mg|g|gm|ml|cc|IU|mcg|μg|微克|毫克|公克|毫升|%|滴|個|粒|片|億CFU)?$/i,
    );
    if (match) {
      results.push({
        name: match[1].trim(),
        amount: match[2].trim(),
        unit: (match[3] || '').trim(),
      });
    } else {
      results.push({
        name: part.trim(),
        amount: '',
        unit: '',
      });
    }
  }
  return results;
}

const SAMPLE_TXT_DATA = `item_id\tcname\tename\tmea\tmcs_no\tmcs_name\tcust_id\tsname\titem_iid\titem_id2\tstock\ton_order\tsa_price\tavg_cost\tin_price\tvip_price\temp_price\treturn_yn\tupdtime
001001\t惠生維他命C發泡錠 20錠\tHui-Sheng Vitamin C Effervescent 20s\t管\tB22\t一般皮膚炎\tV001\t惠生生醫股份有限公司\t4710001001018\t\t150\t20\t280.00\t140.00\t150.00\t250.00\t220.00\tY\t2026-08-01 10:00:00
001002\t惠生金盞花葉黃素膠囊 60粒\tHui-Sheng Lutein 60s\t瓶\tM31\t健康食品類\tV001\t惠生生醫股份有限公司\t4710001001025\t\t80\t0\t890.00\t450.00\t480.00\t799.00\t700.00\tY\t2026-08-01 10:00:00
001003\t高單位益生菌粉末 30包\tProbiotics Daily Powder 30s\t盒\tM31\t健康食品類\tV002\t健康大師生技\t4710002001015\t\t200\t50\t650.00\t310.00\t330.00\t580.00\t500.00\tY\t2026-08-01 10:00:00
002001\t日本進口醫療用滅菌紗布 5片裝\tSterile Gauze Pad 5s\t包\tJ12\t醫療耗材與敷料\tV003\t台灣醫療耗材\t4710003001012\t\t500\t0\t50.00\t20.00\t22.00\t45.00\t38.00\tY\t2026-08-01 10:00:00
002002\t醫用透氣膠帶 1吋×10碼\tMedical Porous Tape 1inch\t卷\tJ12\t醫療耗材與敷料\tV003\t台灣醫療耗材\t4710003001029\t\t-15\t0\t65.00\t28.00\t30.00\t55.00\t48.00\tY\t2026/6/16 10:03:16:29
003001\t深層舒緩護手霜 75ml\tDeep Moisture Hand Cream 75ml\t支\tK05\t美粧護理\tV004\t法系美妝進口商\t4710004001019\t\t60\t0\t350.00\t170.00\t180.00\t320.00\t280.00\tY\t2025/12/22 08:45:03:553
003002\t溫和氨基酸潔顏慕斯 150ml\tAmino Cleansing Mousse 150ml\t瓶\tK05\t美粧護理\tV004\t法系美妝進口商\t4710004001026\t\t45\t15\t480.00\t230.00\t250.00\t420.00\t370.00\tY\t2021/5/1 20:48:10
004001\t健保處方藥調劑服務費\tDispensing Fee\t次\tFEE01\t服務項目\t\t\t\t\t0\t0\t0.00\t0.00\t0.00\t0.00\t0.00\tN\t2026-08-01 10:00:00
005001\t特約門市購物提袋 (大)\tShopping Bag (L)\t個\tPKG01\t耗材包裝\tV005\t印刷包裝材料行\t\t\t1200\t0\t2.00\t0.80\t0.00\t2.00\t2.00\tN\t2026-08-01 10:00:00`;

function downloadTextFile(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const PRODUCT_RECORD_TYPE_OPTIONS = [
  { value: 'PRODUCT', label: '實體商品' },
  { value: 'FEE', label: '手續費／服務費' },
  { value: 'PREPAYMENT', label: '預收款項' },
  { value: 'SERVICE', label: '勞務項目' },
  { value: 'VIRTUAL', label: '虛擬品項' },
  { value: 'NEEDS_REVIEW', label: '待確認' },
] as const;

type EditableSupplierTerm = Pick<
  ProductSupplierTermItem,
  | 'id'
  | 'vendor_id'
  | 'purchase_price'
  | 'minimum_order_quantity'
  | 'giveaway_terms'
  | 'effective_from'
  | 'effective_to'
  | 'is_primary_supplier'
> & { vendor_id: string };

export function FormalProductMastersPage({ notify }: { notify: Notify }) {
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [activeTab, setActiveTab] = useState<
    'masters' | 'pos_export' | 'import' | 'history' | 'quality'
  >('masters');

  // POS 匯出與建檔確認狀態
  const [posBatches, setPosBatches] = useState<import('../shared/types').PosExportBatch[]>([]);
  const [loadingPosBatches, setLoadingPosBatches] = useState(false);
  const [exportingPos, setExportingPos] = useState(false);
  const [confirmingBatch, setConfirmingBatch] = useState<
    import('../shared/types').PosExportBatch | null
  >(null);
  const [confirmedByName, setConfirmedByName] = useState('');
  const [confirmationNote, setConfirmationNote] = useState('');
  const [savingConfirmation, setSavingConfirmation] = useState(false);
  const [selectedPosProductIds, setSelectedPosProductIds] = useState<string[]>([]);

  const loadPosBatches = async () => {
    setLoadingPosBatches(true);
    try {
      const res = await api.productMasters.listPosBatches();
      setPosBatches(res.batches || []);
    } catch (err) {
      notify(getApiErrorMessage(err, '載入 POS 匯出批次失敗'), 'error');
    } finally {
      setLoadingPosBatches(false);
    }
  };

  const handleExportPos = async () => {
    setExportingPos(true);
    try {
      const res = await api.productMasters.exportPos({
        product_master_ids: selectedPosProductIds.length > 0 ? selectedPosProductIds : undefined,
        format_version: '1.0',
        note: `手動匯出 (${selectedPosProductIds.length > 0 ? selectedPosProductIds.length : '全部'} 項商品)`,
      });
      downloadTextFile(res.file_name, res.txt_content);
      notify(
        `POS 匯出檔「${res.file_name}」產生完成並已開始下載！共包含 ${res.items?.length || 0} 筆商品。`,
      );
      setSelectedPosProductIds([]);
      void loadPosBatches();
    } catch (err) {
      notify(getApiErrorMessage(err, '產生 POS 匯出檔失敗'), 'error');
    } finally {
      setExportingPos(false);
    }
  };

  const handleConfirmPosBatch = async () => {
    if (!confirmingBatch) return;
    if (!confirmedByName.trim()) {
      notify('請輸入確認建檔人員姓名。', 'error');
      return;
    }
    setSavingConfirmation(true);
    try {
      await api.productMasters.confirmPos({
        batch_id: confirmingBatch.id,
        confirmed_by_name: confirmedByName.trim(),
        note: confirmationNote.trim() || undefined,
      });
      notify('已成功回報 POS 建檔完成！關聯商品建檔狀態已推進至 CONFIRMED。');
      setConfirmingBatch(null);
      setConfirmedByName('');
      setConfirmationNote('');
      void loadPosBatches();
      void loadItems();
    } catch (err) {
      notify(getApiErrorMessage(err, '回報 POS 建檔失敗'), 'error');
    } finally {
      setSavingConfirmation(false);
    }
  };

  // 商品主檔狀態
  const [items, setItems] = useState<FormalProductMaster[]>([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [categoryCode, setCategoryCode] = useState('');
  const [subcategoryCode, setSubcategoryCode] = useState('');
  const [recordType, setRecordType] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [qualityFilter, setQualityFilter] = useState('');
  const [missingFilter, setMissingFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const listRequestSequence = useRef(0);

  // 供應商基本資料及藥品清冊整合匯出 Modal
  const [rosterModalOpen, setRosterModalOpen] = useState(false);
  const [rosterParams, setRosterParams] = useState<import('../api').SupplierRosterExportParams>({});
  const [rosterScopeTitle, setRosterScopeTitle] = useState('依廠商匯出清冊');

  // 編輯商品主檔基本資料與屬性 Modal
  const [editingItem, setEditingItem] = useState<FormalProductMaster | null>(null);
  const [editProductName, setEditProductName] = useState('');
  const [editProductCode, setEditProductCode] = useState('');
  const [editBarcode, setEditBarcode] = useState('');
  const [editEnglishName, setEditEnglishName] = useState('');
  const [editSpecification, setEditSpecification] = useState('');
  const [editSalesUnit, setEditSalesUnit] = useState('');
  const [giveawayTerms, setGiveawayTerms] = useState('');
  const [ingredientsJson, setIngredientsJson] = useState('');
  const [editableIngredients, setEditableIngredients] = useState<EditableIngredientItem[]>([]);
  const [initialEditableIngredientsJson, setInitialEditableIngredientsJson] = useState('[]');
  const [ingredientInputMode, setIngredientInputMode] = useState<'FORM' | 'TEXT' | 'JSON'>('FORM');
  const [ingredientQuickText, setIngredientQuickText] = useState('');
  const [familyId, setFamilyId] = useState('');
  const [familySearch, setFamilySearch] = useState('');
  const [editCategoryCode, setEditCategoryCode] = useState('');
  const [editSubcategoryCode, setEditSubcategoryCode] = useState('');
  const [editRecordType, setEditRecordType] = useState('PRODUCT');
  const [editSupplierTerms, setEditSupplierTerms] = useState<EditableSupplierTerm[]>([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierVendorToAdd, setSupplierVendorToAdd] = useState('');
  const [officialProductUrl, setOfficialProductUrl] = useState('');
  const [families, setFamilies] = useState<ProductFamilyItem[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [subcategories, setSubcategories] = useState<ProductSubcategory[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [showFamilyManager, setShowFamilyManager] = useState(false);
  const [familyFormId, setFamilyFormId] = useState('');
  const [familyFormCode, setFamilyFormCode] = useState('');
  const [familyFormName, setFamilyFormName] = useState('');
  const [familyFormBrand, setFamilyFormBrand] = useState('');
  const [familyFormDescription, setFamilyFormDescription] = useState('');
  const [familyFormActive, setFamilyFormActive] = useState(true);
  const [savingFamily, setSavingFamily] = useState(false);
  const [deletingProducts, setDeletingProducts] = useState(false);
  const [savingCustom, setSavingCustom] = useState(false);

  // 匯入 TXT 狀態 (極簡化 3 步)
  const [txtContent, setTxtContent] = useState('');
  const [fileName, setFileName] = useState('');
  const encoding = 'CP950';
  const [taxType, setTaxType] = useState<'TAX_INCLUSIVE' | 'TAX_EXCLUSIVE'>('TAX_INCLUSIVE');
  const [isDragging, setIsDragging] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{
    file_name: string;
    file_hash?: string;
    previous_import_warning?: string | null;
    total_rows: number;
    importable_rows: number;
    warning_rows: number;
    invalid_rows_count: number;
    invalid_rows: Array<{ rowNumber: number; itemId?: string; cname?: string; reason: string }>;
    breakdown: {
      inserted: number;
      updated: number;
      unchanged: number;
      price_changed: number;
      high_risk_price: number;
      identity_conflicts: number;
    };
    summary: Record<string, number>;
    header_columns: string[];
    preview_rows: Array<Record<string, any>>;
  } | null>(null);
  const [showInvalidModal, setShowInvalidModal] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ percent: number; text: string } | null>(
    null,
  );
  const [parsedValidRows, setParsedValidRows] = useState<ParsedTxtRow[]>([]);
  const [lastImportResult, setLastImportResult] = useState<any | null>(null);

  // 匯入紀錄狀態
  const [batches, setBatches] = useState<ImportBatchRecord[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  // 品質中心 5 大子頁籤狀態
  const [qualityActiveTab, setQualityActiveTab] = useState<string>('price_changes');
  const [qualitySummary, setQualitySummary] = useState<Record<string, number>>({});
  const [qualityItems, setQualityItems] = useState<any[]>([]);
  const [qualityLoading, setQualityLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [batchActionLoading, setBatchActionLoading] = useState(false);

  // 成分自動補全狀態
  const [enrichmentSummary, setEnrichmentSummary] = useState<{
    total: number;
    notSearched: number;
    searching: number;
    foundUnconfirmed: number;
    confirmed: number;
    notFound: number;
    ambiguous: number;
    identityUnconfirmed: number;
    missingIngredients: number;
  } | null>(null);
  const [enrichingSingleId, setEnrichingSingleId] = useState<string | null>(null);
  const [lookupProgressIndex, setLookupProgressIndex] = useState<number | null>(null);
  const [showBatchEnrichModal, setShowBatchEnrichModal] = useState(false);
  const [batchEnriching, setBatchEnriching] = useState(false);
  const [batchEnrichProgress, setBatchEnrichProgress] = useState('');
  const [batchCategory, setBatchCategory] = useState('');
  const [batchEnrichResults, setBatchEnrichResults] = useState<any[]>([]);

  // 候選成分抽屜/Modal
  const [selectedCandidateProduct, setSelectedCandidateProduct] =
    useState<FormalProductMaster | null>(null);
  const [candidateDrawerData, setCandidateDrawerData] = useState<CandidateDrawerData | null>(null);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [confirmingSourceId, setConfirmingSourceId] = useState<string | null>(null);
  const [rejectingSourceId, setRejectingSourceId] = useState<string | null>(null);
  const [supplementalSearchKeyword, setSupplementalSearchKeyword] = useState('');
  const [manualSourceUrl, setManualSourceUrl] = useState('');
  const [uploadingIngredientLabel, setUploadingIngredientLabel] = useState(false);
  const [selectedMasterIds, setSelectedMasterIds] = useState<string[]>([]);
  const [batchConfirming, setBatchConfirming] = useState(false);
  const [selectedDiffIndices, setSelectedDiffIndices] = useState<number[]>([]);

  useEffect(() => {
    let active = true;
    void api.auth
      .me()
      .then((result) => {
        if (active) setCurrentUserRole(result.user.role);
      })
      .catch(() => undefined);
    void loadDrugKpi();
    void loadTfdaStatus();
    return () => {
      active = false;
    };
  }, []);

  // 西藥成分補全與 TFDA 狀態
  const [drugKpi, setDrugKpi] = useState<DrugEnrichmentKpi | null>(null);
  const [tfdaStatus, setTfdaStatus] = useState<TfdaSyncStatus | null>(null);
  const [syncingTfda, setSyncingTfda] = useState(false);
  const [enrichingSingleDrugId, setEnrichingSingleDrugId] = useState<string | null>(null);

  // 西藥批次補全 Job 狀態
  const [showDrugBatchModal, setShowDrugBatchModal] = useState(false);
  const [drugBatchStage, setDrugBatchStage] = useState<'PREVIEW' | 'RUNNING' | 'COMPLETED'>(
    'PREVIEW',
  );
  const [drugBatchPreview, setDrugBatchPreview] = useState<{
    totalEligible: number;
    estimatedAutoConfirm: number;
    estimatedNeedsReview: number;
    estimatedNotFound: number;
    alreadyConfirmedCount: number;
  } | null>(null);
  const [drugBatchPreviewLoading, setDrugBatchPreviewLoading] = useState(false);
  const [currentDrugJob, setCurrentDrugJob] = useState<DrugEnrichmentJob | null>(null);
  const [currentJobItems, setCurrentJobItems] = useState<DrugEnrichmentJobItem[]>([]);
  const [drugBatchExecuting, setDrugBatchExecuting] = useState(false);

  // 全品項多層級智慧處方與成分自動補全 (auto-enrich-all) 狀態
  const [showAutoEnrichAllModal, setShowAutoEnrichAllModal] = useState(false);
  const [autoEnrichPreviewLoading, setAutoEnrichPreviewLoading] = useState(false);
  const [autoEnrichStage, setAutoEnrichStage] = useState<'PREVIEW' | 'RUNNING' | 'COMPLETED'>(
    'PREVIEW',
  );
  const [autoEnrichPreview, setAutoEnrichPreview] = useState<{
    totalProducts: number;
    alreadyConfirmedCount: number;
    lockedCount: number;
    pendingCount: number;
    eligibleDrugCount: number;
    eligibleHealthFoodCount: number;
    estimatedRuleMatches: number;
    drugReferenceCount: number;
    healthFoodReferenceCount: number;
  } | null>(null);
  const [autoEnrichRunning, setAutoEnrichRunning] = useState(false);
  const [currentAutoEnrichJob, setCurrentAutoEnrichJob] = useState<DrugEnrichmentJob | null>(null);
  const [autoEnrichJobItems, setAutoEnrichJobItems] = useState<DrugEnrichmentJobItem[]>([]);
  const [autoEnrichWarning, setAutoEnrichWarning] = useState('');

  // 藥品成分審查狀態
  const [drugReviews, setDrugReviews] = useState<DrugCandidateReviewItem[]>([]);
  const [selectedReviewProductIds, setSelectedReviewProductIds] = useState<string[]>([]);
  const [reviewBatchLoading, setReviewBatchLoading] = useState(false);

  // 載入成分補全統計總覽與西藥 KPI
  const loadEnrichmentSummary = async () => {
    try {
      const res = await api.productEnrichment.getSummary();
      setEnrichmentSummary(res.summary);
    } catch {
      // ignore
    }
  };

  const loadDrugKpi = async () => {
    try {
      const res = await api.productIngredients.getDrugKpi();
      setDrugKpi(res.kpi);
    } catch {
      // ignore
    }
  };

  const loadTfdaStatus = async () => {
    try {
      const res = await api.productIngredients.getTfdaStatus();
      setTfdaStatus(res.status);
    } catch {
      // ignore
    }
  };

  const handleSyncTfda = async () => {
    setSyncingTfda(true);
    try {
      const res = await api.productIngredients.syncTfdaData();
      setTfdaStatus(res.status);
      notify('✓ TFDA 官方藥品資料已完成同步與索引更新！');
      void loadDrugKpi();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '同步 TFDA 資料失敗。'), 'error');
    } finally {
      setSyncingTfda(false);
    }
  };

  // 單品西藥成分補全
  const handleSingleDrugEnrich = async (productId: string) => {
    if (!productId) return;
    setEnrichingSingleDrugId(productId);
    try {
      const res = await api.productIngredients.enrichDrug(productId, { forceRefresh: true });
      const result = res?.result;
      if (result?.status === 'AUTO_CONFIRMED') {
        notify(
          `✓ 已取得 TFDA 官方成分：${result.matchedLicenseNumber}（${result.officialData?.chineseName || ''}）`,
        );
      } else if (result?.status === 'NEEDS_REVIEW' || result?.status === 'AMBIGUOUS') {
        notify(`已找到相符之官方候選（${result.matchedLicenseNumber}），已進入待確認清單。`);
      } else if (result?.status === 'ALREADY_CONFIRMED' || result?.status === 'SKIPPED_LOCKED') {
        notify(result.matchReasons?.[0] || '商品已具備確認成分，略過更新');
      } else {
        notify('TFDA 本地資料庫未找到高可信度之許可證處方。', 'error');
      }
      void loadItems();
      void loadDrugKpi();
      if (selectedCandidateProduct?.id === productId) {
        void openCandidateDrawer({ ...selectedCandidateProduct, id: productId });
      }
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '西藥成分查找失敗。'), 'error');
    } finally {
      setEnrichingSingleDrugId(null);
    }
  };

  // 批次分析預覽
  const handlePreviewDrugBatch = async () => {
    setDrugBatchPreviewLoading(true);
    try {
      const res = await api.productIngredients.previewDrugBatch();
      setDrugBatchPreview(res.preview);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次預估失敗。'), 'error');
    } finally {
      setDrugBatchPreviewLoading(false);
    }
  };

  // 啟動批次補全 Job
  const handleStartDrugBatch = async () => {
    setDrugBatchExecuting(true);
    setDrugBatchStage('RUNNING');
    try {
      const startRes = await api.productIngredients.startDrugBatch();
      const jobId = startRes.jobId;
      let finished = false;

      while (!finished) {
        const batchRes = await api.productIngredients.processDrugJobBatch(jobId, 150);
        setCurrentDrugJob(batchRes.job);
        finished = batchRes.isFinished;
        if (!finished) {
          await new Promise((r) => setTimeout(r, 400));
        }
      }

      const finalJob = await api.productIngredients.getDrugJob(jobId);
      setCurrentDrugJob(finalJob.job);
      const itemsRes = await api.productIngredients.getDrugJobItems(jobId);
      setCurrentJobItems(itemsRes.items);
      setDrugBatchStage('COMPLETED');
      notify(
        `🎉 批次補全完成！自動帶入 ${finalJob.job.auto_confirmed_count} 筆，待審查 ${finalJob.job.review_count} 筆。`,
      );
      void loadItems();
      void loadDrugKpi();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次補全執行中斷。'), 'error');
      setDrugBatchStage('PREVIEW');
    } finally {
      setDrugBatchExecuting(false);
    }
  };

  // 載入待確認藥品成分
  const loadDrugReviews = async () => {
    try {
      const res = await api.productIngredients.getDrugReviews({ limit: 100 });
      setDrugReviews(res.items || []);
    } catch {
      // ignore
    }
  };

  // 採購確認藥品匹配
  const handleConfirmDrugReview = async (productId: string, licenseNumber: string) => {
    try {
      await api.productIngredients.confirmDrugMatch({ productId, licenseNumber });
      notify('✓ 已成功確認藥品官方處方成分！');
      void loadDrugReviews();
      void loadItems();
      void loadDrugKpi();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '確認藥品失敗。'), 'error');
    }
  };

  // 採購排除藥品匹配
  const handleRejectDrugReview = async (productId: string, licenseNumber: string) => {
    try {
      await api.productIngredients.rejectDrugMatch({ productId, licenseNumber });
      notify('已排除該藥品匹配候選。');
      void loadDrugReviews();
      void loadItems();
      void loadDrugKpi();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '排除藥品失敗。'), 'error');
    }
  };

  // 批次勾選確認
  const handleBatchConfirmDrugReviews = async () => {
    if (selectedReviewProductIds.length === 0) return;
    setReviewBatchLoading(true);
    try {
      const matchesToConfirm = drugReviews
        .filter((r) => selectedReviewProductIds.includes(r.productId))
        .map((r) => ({ productId: r.productId, licenseNumber: r.officialLicenseNumber }));

      await api.productIngredients.confirmDrugMatch({ matches: matchesToConfirm });
      notify(`✓ 已成功批量確認 ${matchesToConfirm.length} 筆藥品官方成分！`);
      setSelectedReviewProductIds([]);
      void loadDrugReviews();
      void loadItems();
      void loadDrugKpi();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批量確認失敗。'), 'error');
    } finally {
      setReviewBatchLoading(false);
    }
  };

  // 載入商品主檔
  const loadItems = async (qFilter = qualityFilter, mFilter = missingFilter, targetPage = page) => {
    const requestId = ++listRequestSequence.current;
    setLoading(true);
    try {
      const res = await api.productMasters.list({
        keyword,
        category_code: categoryCode,
        subcategory_code: subcategoryCode,
        record_type: recordType,
        family_id: familyFilter,
        quality_filter: qFilter,
        missing_filter: mFilter,
        limit: String(pageSize),
        offset: String((targetPage - 1) * pageSize),
      });
      if (requestId !== listRequestSequence.current) return;
      setItems(res.items || []);
      setTotal(res.total || 0);
      setSelectedMasterIds([]);
      void loadEnrichmentSummary();
    } catch (err: unknown) {
      if (requestId !== listRequestSequence.current) return;
      notify(getApiErrorMessage(err, '載入商品主檔失敗，請稍後再試。'), 'error');
    } finally {
      if (requestId === listRequestSequence.current) setLoading(false);
    }
  };

  // 單品成分查找
  const handleSingleEnrich = async (
    productId: string,
    supplementalKeyword = '',
    sourceUrl = '',
  ) => {
    if (!productId) {
      notify('商品資料不完整，無法執行成分查找。', 'error');
      return;
    }
    setEnrichingSingleId(productId);
    setLookupProgressIndex(0);
    const progressTimer = window.setInterval(() => {
      setLookupProgressIndex((current) =>
        current === null ? null : Math.min(current + 1, LOOKUP_STAGES.length - 1),
      );
    }, 1200);
    try {
      const res = await api.productEnrichment.enrichSingle(productId, {
        supplementalKeyword: supplementalKeyword.trim() || undefined,
        forceRefresh: true,
        sourceUrl: sourceUrl.trim() || undefined,
      });
      notify(res?.message || '已完成 AI 智慧查找成分');
      const targetProduct =
        selectedCandidateProduct?.id === productId
          ? selectedCandidateProduct
          : items.find((item) => item.id === productId) || null;
      if (targetProduct) setSelectedCandidateProduct(targetProduct);
      const updated = await api.productEnrichment.getCandidates(productId);
      setCandidateDrawerData(updated);
      setSupplementalSearchKeyword(updated.searchRun?.supplementalKeyword || supplementalKeyword);
      setManualSourceUrl(updated.product?.official_product_url || sourceUrl);
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, 'AI 智慧查找成分失敗，請稍後再試。'), 'error');
    } finally {
      window.clearInterval(progressTimer);
      setLookupProgressIndex(null);
      setEnrichingSingleId(null);
    }
  };

  // 批次補全商品成分
  const handleBatchEnrich = async () => {
    setBatchEnriching(true);
    setBatchEnrichProgress('正在依序比對內部資料、TFDA、康健藥品頁與可信來源…');
    try {
      const res = await api.productEnrichment.enrichBatch({
        limit: 20,
        categoryCode: batchCategory || undefined,
      });
      setBatchEnrichResults(res?.items || []);
      notify(
        res?.message ||
          `批次補全完成！處理 ${res?.totalProcessed || 0} 筆，成功查得 ${res?.enrichedCount || 0} 筆，查無資料 ${res?.notFoundCount || 0} 筆。`,
      );
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次補全成分失敗，請稍後再試。'), 'error');
    } finally {
      setBatchEnriching(false);
      setBatchEnrichProgress('');
    }
  };

  const handleIngredientLabelUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const file = input.files?.[0];
    if (!file || !selectedCandidateProduct?.id) return;
    setUploadingIngredientLabel(true);
    try {
      await api.productEnrichment.uploadLabelAttachment(selectedCandidateProduct.id, file);
      const updated = await api.productEnrichment.getCandidates(selectedCandidateProduct.id);
      setCandidateDrawerData(updated);
      notify('商品成分／標示附件已上傳。');
    } catch (error) {
      notify(getApiErrorMessage(error, '商品標示上傳失敗，請稍後再試。'), 'error');
    } finally {
      input.value = '';
      setUploadingIngredientLabel(false);
    }
  };

  // 開啟候選成分抽屜
  const openCandidateDrawer = async (item: FormalProductMaster) => {
    if (!item?.id) {
      notify('商品資料不完整。', 'error');
      return;
    }
    setSelectedCandidateProduct(item);
    setLoadingCandidates(true);
    try {
      const res = await api.productEnrichment.getCandidates(item.id);
      setCandidateDrawerData(res);
      if (res.staging?.diffs) {
        const indices = res.staging.diffs
          .map((d, i) => (d.diffType !== 'REMOVED' ? i : -1))
          .filter((i) => i >= 0);
        setSelectedDiffIndices(indices);
      } else {
        setSelectedDiffIndices([]);
      }
      setSupplementalSearchKeyword(res.searchRun?.supplementalKeyword || '');
      setManualSourceUrl(res.product?.official_product_url || '');
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '讀取候選成分失敗，請稍後再試。'), 'error');
    } finally {
      setLoadingCandidates(false);
    }
  };

  // 採購確認成分資料
  const handleConfirmSource = async (sourceId?: string) => {
    if (!selectedCandidateProduct?.id) return;
    setConfirmingSourceId(sourceId || 'DIRECT');
    try {
      const res = await api.productEnrichment.confirm(selectedCandidateProduct.id, { sourceId });
      notify(res?.message || '已成功確認成分資料！');
      const updated = await api.productEnrichment.getCandidates(selectedCandidateProduct.id);
      setCandidateDrawerData(updated);
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '確認成分資料失敗，請稍後再試。'), 'error');
    } finally {
      setConfirmingSourceId(null);
    }
  };

  // 採用暫存差異並確認儲存為正式成分
  const handleConfirmStaging = async () => {
    if (!selectedCandidateProduct?.id) return;
    const staging = candidateDrawerData?.staging;

    // 若有暫存比對
    if (staging && staging.diffs && staging.diffs.length > 0) {
      const diffsToAdopt = staging.diffs.filter((_, idx) => selectedDiffIndices.includes(idx));
      if (diffsToAdopt.length === 0) {
        notify('請至少勾選一項要採用的成分欄位。', 'error');
        return;
      }

      setConfirmingSourceId('STAGING');
      try {
        const ingredientsToSave = diffsToAdopt.map((item, idx) => ({
          nameRaw: item.ingredientName,
          nameNormalized: item.nameNormalized,
          amount: item.incomingAmount !== undefined ? item.incomingAmount : item.currentAmount,
          unit: item.incomingUnit || item.currentUnit || '',
          sequence: idx + 1,
          role: item.incomingRole || item.currentRole || 'PRIMARY',
          formOrStrain: item.incomingFormOrStrain || '',
          servingSizeDescription: item.incomingServingBasis || '',
          completeness: item.incomingCompleteness || 'FULL',
          isUndisclosed:
            (item.incomingAmount !== undefined ? item.incomingAmount : item.currentAmount) === null,
        }));

        const res = await api.productEnrichment.confirm(selectedCandidateProduct.id, {
          sourceId: staging.sourceId,
          rawText: staging.rawText,
          expectedRevision: selectedCandidateProduct.ingredient_revision ?? undefined,
          ingredients: ingredientsToSave,
        });

        notify(res?.message || '已成功採用並儲存正式商品成分！');
        const updated = await api.productEnrichment.getCandidates(selectedCandidateProduct.id);
        setCandidateDrawerData(updated);
        void loadItems();
      } catch (err: unknown) {
        notify(getApiErrorMessage(err, '確認成分資料失敗，請稍後再試。'), 'error');
      } finally {
        setConfirmingSourceId(null);
      }
      return;
    }

    // 若無暫存但有首選來源
    if (candidateDrawerData?.sources && candidateDrawerData.sources.length > 0) {
      void handleConfirmSource(candidateDrawerData.sources[0]?.id);
      return;
    }

    notify('目前尚無已查得之成分可供確認儲存。', 'error');
  };

  // 主檔列表多選商品批次查詢成分
  const handleBatchEnrichSelected = async () => {
    if (selectedMasterIds.length === 0) {
      notify('請先勾選要查詢成分的商品。', 'error');
      return;
    }
    setBatchEnriching(true);
    try {
      const res = await api.productEnrichment.enrichBatch({
        productIds: selectedMasterIds,
        forceAll: true,
      });
      notify(
        `批次查詢完成！共處理 ${res.totalProcessed} 筆，已找到並帶入待確認：${res.enrichedCount} 筆。`,
      );
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次查詢成分失敗，請稍後再試。'), 'error');
    } finally {
      setBatchEnriching(false);
    }
  };

  // 主檔列表多選商品批次確認採用
  const handleBatchConfirmSelected = async () => {
    if (selectedMasterIds.length === 0) {
      notify('請先勾選要確認成分的商品。', 'error');
      return;
    }
    setBatchConfirming(true);
    try {
      const res = await api.productEnrichment.batchConfirm(
        selectedMasterIds.map((id) => ({ productId: id })),
      );
      notify(`批次確認完成！已成功確認 ${res.confirmedCount} 筆商品主檔成分。`);
      setSelectedMasterIds([]);
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次確認失敗，請稍後再試。'), 'error');
    } finally {
      setBatchConfirming(false);
    }
  };

  // 排除錯誤來源
  const handleRejectSource = async (sourceId: string) => {
    if (!selectedCandidateProduct?.id || !sourceId) return;
    setRejectingSourceId(sourceId);
    try {
      const res = await api.productEnrichment.rejectSource(selectedCandidateProduct.id, sourceId);
      notify(res?.message || '已排除該來源');
      const updated = await api.productEnrichment.getCandidates(selectedCandidateProduct.id);
      setCandidateDrawerData(updated);
      void loadItems();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '排除來源失敗，請稍後再試。'), 'error');
    } finally {
      setRejectingSourceId(null);
    }
  };

  useEffect(() => {
    void Promise.all([
      api.productFamilies.list(),
      api.productCategories.list(),
      api.productSubcategories.list(),
      api.vendors.list({ status: 'ACTIVE' }),
    ])
      .then(([familyResult, categoryResult, subcategoryResult, vendorResult]) => {
        setFamilies(familyResult.items || []);
        setCategories(categoryResult.categories || []);
        setSubcategories(subcategoryResult.subcategories || []);
        setVendors(vendorResult.vendors || []);
      })
      .catch((error: unknown) => {
        notify(getApiErrorMessage(error, '載入商品主檔共用選項失敗。'), 'error');
      });
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      void loadBatches();
    } else if (activeTab === 'quality') {
      setSelectedIds([]);
      void loadQualityCenter(qualityActiveTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'masters') return;
    const timer = window.setTimeout(() => {
      void loadItems(qualityFilter, missingFilter, page);
    }, 180);
    return () => window.clearTimeout(timer);
    // 每個篩選條件皆為查詢快取鍵；request sequence 會阻止較慢舊請求覆蓋新結果。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    keyword,
    categoryCode,
    subcategoryCode,
    recordType,
    familyFilter,
    qualityFilter,
    missingFilter,
    page,
  ]);

  // 載入品質中心資料
  const loadQualityCenter = async (tab = qualityActiveTab) => {
    setQualityLoading(true);
    setSelectedIds([]);
    try {
      const res = await api.productMasters.getDataQualityTabs(tab);
      setQualitySummary(res.summary);
      setQualityItems(res.items);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '載入資料待確認中心失敗'), 'error');
    } finally {
      setQualityLoading(false);
    }
  };

  // 載入匯入紀錄
  const loadBatches = async () => {
    setBatchesLoading(true);
    try {
      const res = await api.productMasters.importBatches();
      setBatches(res.items);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '載入匯入紀錄失敗'), 'error');
    } finally {
      setBatchesLoading(false);
    }
  };

  // 批次處理品質中心項目
  const handleBatchResolve = async (action: string) => {
    if (selectedIds.length === 0) {
      notify('請先勾選要批次處理的項目。', 'error');
      return;
    }
    setBatchActionLoading(true);
    try {
      const res = await api.productMasters.batchResolveQuality(
        qualityActiveTab,
        action,
        selectedIds,
      );
      notify(`已成功批次處理 ${res.resolved_count} 筆項目！`);
      setSelectedIds([]);
      void loadQualityCenter(qualityActiveTab);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '批次處理失敗'), 'error');
    } finally {
      setBatchActionLoading(false);
    }
  };

  // 全選 / 取消全選
  const handleToggleSelectAll = () => {
    if (selectedIds.length === qualityItems.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(qualityItems.map((item) => item.id));
    }
  };

  // 單選
  const handleToggleSelect = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((x) => x !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  // 回復批次 (Rollback)
  const handleRollbackBatch = async (batchId: string, batchNo: string) => {
    if (!confirm(`確定要回復批次 ${batchNo} 嗎？這將還原該批次所修改的來源價格與暫存紀錄。`))
      return;
    try {
      await api.productMasters.rollbackBatch(batchId);
      notify(`批次 ${batchNo} 已成功回復！`);
      void loadBatches();
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '回復批次失敗'), 'error');
    }
  };

  // 重複商品審核操作
  const handleResolveDuplicate = async (candidateId: string, action: string, notes?: string) => {
    try {
      await api.productMasters.resolveDuplicate(candidateId, action, notes);
      notify('重複商品決策已成功儲存！');
      void loadQualityCenter(qualityActiveTab);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '儲存重複商品決策失敗'), 'error');
    }
  };

  // 價格異動審核操作
  const handleResolvePriceReview = async (
    productId: string,
    decision: 'ACCEPT_NEW_PRICE' | 'RETAIN_OLD_PRICE',
    newPrice?: number,
  ) => {
    try {
      await api.productMasters.resolvePriceReview(productId, decision, newPrice);
      notify(decision === 'ACCEPT_NEW_PRICE' ? '已接受新進價！' : '已保留原進價！');
      void loadQualityCenter(qualityActiveTab);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '審核價格異動失敗'), 'error');
    }
  };

  // 開啟編輯商品主檔 Modal (名稱、代號、條碼、規格、單位、成分及自訂屬性)
  const openEditModal = (item: FormalProductMaster) => {
    setEditingItem(item);
    setEditProductName(item.product_name || '');
    setEditProductCode(item.product_code || '');
    setEditBarcode(item.primary_barcode_candidate || '');
    setEditEnglishName(item.english_name || '');
    setEditSpecification(item.specification || '');
    setEditSalesUnit(item.sales_unit || '');
    setGiveawayTerms(item.current_vendor_giveaway_terms || '');
    setFamilyId(item.product_family_id || '');
    setFamilySearch('');
    setEditCategoryCode(item.product_category_code || '');
    setEditSubcategoryCode(item.product_subcategory_code || '');
    setEditRecordType(item.record_type || 'PRODUCT');
    setEditSupplierTerms(
      (item.supplier_terms || []).map((term) => ({
        id: term.id,
        vendor_id: term.vendor_id || '',
        purchase_price: term.purchase_price ?? null,
        minimum_order_quantity: term.minimum_order_quantity ?? null,
        giveaway_terms: term.giveaway_terms || '',
        effective_from: term.effective_from || null,
        effective_to: term.effective_to || null,
        is_primary_supplier: Number(term.is_primary_supplier) === 1 ? 1 : 0,
      })),
    );
    setSupplierSearch('');
    setSupplierVendorToAdd('');
    setOfficialProductUrl(item.official_product_url || '');

    // 解析現有成分為結構化編輯清單
    let parsedList: EditableIngredientItem[] = [];
    if (item.ingredients_json) {
      try {
        const rawArr = JSON.parse(item.ingredients_json);
        if (Array.isArray(rawArr)) {
          parsedList = rawArr.map((i: any, idx: number) => ({
            id: `ing-${idx}-${Date.now()}`,
            name: i.nameRaw || i.name || i.nameNormalized || '',
            amount: i.amount !== null && i.amount !== undefined ? String(i.amount) : '',
            unit: i.unit || '',
          }));
        }
      } catch {
        // ignore parse error
      }
    }
    if (parsedList.length === 0 && item.ingredient_raw_text) {
      parsedList = parseIngredientsFromRawText(item.ingredient_raw_text).map((i, idx) => ({
        id: `ing-${idx}-${Date.now()}`,
        name: i.name,
        amount: i.amount,
        unit: i.unit,
      }));
    }
    setEditableIngredients(parsedList);
    setInitialEditableIngredientsJson(
      JSON.stringify(
        parsedList.map((item) => ({ name: item.name, amount: item.amount, unit: item.unit })),
      ),
    );
    setIngredientsJson(item.ingredients_json || JSON.stringify(parsedList));
    setIngredientInputMode('FORM');
    setIngredientQuickText('');
  };

  // 儲存商品主檔資料 (包含自行填寫成分)
  const saveCustomFields = async () => {
    if (!editingItem) return;
    if (!editProductName.trim()) {
      notify('商品中文名稱不可為空白。', 'error');
      return;
    }
    if (!editProductCode.trim()) {
      notify('商品代號不可為空白。', 'error');
      return;
    }
    if (editSubcategoryCode) {
      const selectedSubcategory = subcategories.find((item) => item.code === editSubcategoryCode);
      if (!selectedSubcategory || selectedSubcategory.category_code !== editCategoryCode) {
        notify('商品子分類不屬於所選大分類，請重新選擇。', 'error');
        return;
      }
    }
    if (editSupplierTerms.length > 0) {
      const primaryCount = editSupplierTerms.filter((term) =>
        Boolean(term.is_primary_supplier),
      ).length;
      if (primaryCount !== 1) {
        notify('請指定且只能指定一個主要進貨廠商。', 'error');
        return;
      }
      if (editSupplierTerms.some((term) => !term.vendor_id)) {
        notify('請完成進貨廠商選擇。', 'error');
        return;
      }
    }

    // 將結構化成分清單轉換為標準 JSON
    const validIngredients = editableIngredients
      .filter((i) => i.name.trim().length > 0)
      .map((i, idx) => ({
        nameRaw: i.name.trim(),
        nameNormalized: i.name.trim(),
        amount: i.amount ? parseFloat(i.amount) || null : null,
        unit: i.unit.trim(),
        sequence: idx + 1,
      }));
    const finalIngredientsJson = JSON.stringify(validIngredients);
    const normalizedSupplierTerms = editSupplierTerms.map((term) => ({
      id: term.id,
      vendor_id: term.vendor_id,
      purchase_price: term.purchase_price ?? null,
      minimum_order_quantity: term.minimum_order_quantity ?? null,
      giveaway_terms: term.giveaway_terms || null,
      effective_from: term.effective_from || null,
      effective_to: term.effective_to || null,
      is_primary_supplier: Boolean(term.is_primary_supplier),
    }));
    const originalSupplierTerms = (editingItem.supplier_terms || []).map((term) => ({
      id: term.id,
      vendor_id: term.vendor_id || '',
      purchase_price: term.purchase_price ?? null,
      minimum_order_quantity: term.minimum_order_quantity ?? null,
      giveaway_terms: term.giveaway_terms || null,
      effective_from: term.effective_from || null,
      effective_to: term.effective_to || null,
      is_primary_supplier: Number(term.is_primary_supplier) === 1,
    }));
    const supplierTermsChanged =
      JSON.stringify(normalizedSupplierTerms) !== JSON.stringify(originalSupplierTerms);

    setSavingCustom(true);
    try {
      const payload: Parameters<typeof api.productMasters.updateCustomFields>[1] = {
        expected_updated_at: editingItem.updated_at,
      };
      if (editProductName.trim() !== (editingItem.product_name || '')) {
        payload.product_name = editProductName.trim();
      }
      if (editProductCode.trim() !== (editingItem.product_code || '')) {
        payload.product_code = editProductCode.trim();
      }
      if (editBarcode.trim() !== (editingItem.primary_barcode_candidate || '')) {
        payload.primary_barcode_candidate = editBarcode.trim() || null;
      }
      if (editEnglishName.trim() !== (editingItem.english_name || '')) {
        payload.english_name = editEnglishName.trim() || null;
      }
      if (editSpecification.trim() !== (editingItem.specification || '')) {
        payload.specification = editSpecification.trim();
      }
      if (editSalesUnit.trim() !== (editingItem.sales_unit || '')) {
        payload.sales_unit = editSalesUnit.trim();
      }
      if (giveawayTerms.trim() !== (editingItem.current_vendor_giveaway_terms || '')) {
        payload.current_vendor_giveaway_terms = giveawayTerms.trim() || null;
      }
      const currentEditableIngredientsJson = JSON.stringify(
        editableIngredients.map((item) => ({
          name: item.name,
          amount: item.amount,
          unit: item.unit,
        })),
      );
      if (currentEditableIngredientsJson !== initialEditableIngredientsJson) {
        payload.ingredients_json = finalIngredientsJson;
      }
      if ((familyId || null) !== (editingItem.product_family_id || null)) {
        payload.product_family_id = familyId || null;
      }
      if ((editCategoryCode || null) !== (editingItem.product_category_code || null)) {
        payload.product_category_code = editCategoryCode || null;
      }
      if ((editSubcategoryCode || null) !== (editingItem.product_subcategory_code || null)) {
        payload.product_subcategory_code = editSubcategoryCode || null;
      }
      if (editRecordType !== editingItem.record_type) payload.record_type = editRecordType;
      if (officialProductUrl.trim() !== (editingItem.official_product_url || '')) {
        payload.official_product_url = officialProductUrl.trim() || null;
      }
      if (supplierTermsChanged) payload.supplier_terms = normalizedSupplierTerms;
      if (Object.keys(payload).length === 1) {
        notify('目前沒有需要儲存的變更。');
        return;
      }

      const result = await api.productMasters.updateCustomFields(editingItem.id, payload);
      if (!result.product) throw new Error('API 未回傳已儲存商品資料');
      setItems((current) =>
        current.map((item) => (item.id === result.product.id ? result.product : item)),
      );
      notify(result.message || '商品主檔資料已成功儲存！');
      setEditingItem(null);
      await Promise.all([loadItems(), refreshFamilies()]);
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '儲存商品主檔失敗，請稍後再試。'), 'error');
    } finally {
      setSavingCustom(false);
    }
  };

  const refreshFamilies = async () => {
    const result = await api.productFamilies.list();
    setFamilies(result.items || []);
  };

  const resetFamilyForm = () => {
    setFamilyFormId('');
    setFamilyFormCode('');
    setFamilyFormName('');
    setFamilyFormBrand('');
    setFamilyFormDescription('');
    setFamilyFormActive(true);
  };

  const editFamily = (family: ProductFamilyItem) => {
    setFamilyFormId(family.id);
    setFamilyFormCode(family.family_code);
    setFamilyFormName(family.family_name);
    setFamilyFormBrand(family.brand_name || '');
    setFamilyFormDescription(family.description || '');
    setFamilyFormActive(Number(family.active ?? 1) === 1);
  };

  const saveFamily = async () => {
    if (!familyFormName.trim()) {
      notify('請輸入商品家族名稱。', 'error');
      return;
    }
    setSavingFamily(true);
    try {
      if (familyFormId) {
        await api.productFamilies.update(familyFormId, {
          family_name: familyFormName.trim(),
          brand_name: familyFormBrand.trim(),
          description: familyFormDescription.trim(),
          active: familyFormActive,
        });
        notify('商品家族已更新。');
      } else {
        await api.productFamilies.create({
          family_name: familyFormName.trim(),
          family_code: familyFormCode.trim() || undefined,
          brand_name: familyFormBrand.trim(),
          description: familyFormDescription.trim(),
        });
        notify('商品家族已建立。');
      }
      resetFamilyForm();
      await refreshFamilies();
      void loadItems();
    } catch (error: unknown) {
      notify(getApiErrorMessage(error, '商品家族儲存失敗。'), 'error');
    } finally {
      setSavingFamily(false);
    }
  };

  const removeFamilyMember = async (familyIdValue: string, productId: string) => {
    try {
      await api.productFamilies.removeMember(familyIdValue, productId);
      notify('商品已移出家族。');
      await refreshFamilies();
      void loadItems();
    } catch (error: unknown) {
      notify(getApiErrorMessage(error, '移出商品家族失敗。'), 'error');
    }
  };

  const deleteSelectedProducts = async (productIds: string[]) => {
    if (productIds.length === 0) return;
    setDeletingProducts(true);
    try {
      const preview = await api.productMasters.previewDelete(productIds);
      const candidates = preview.results.filter((item) => item.status !== 'FAILED');
      if (candidates.length === 0) {
        notify('所選商品皆已刪除或不存在。', 'error');
        return;
      }
      const linked = candidates.filter((item) => item.status === 'REQUIRES_INACTIVATION');
      const sample = candidates
        .slice(0, 8)
        .map((item) => `${item.product_code || '—'}｜${item.product_name || '未命名商品'}`)
        .join('\n');
      const linkedNote =
        linked.length > 0
          ? `\n\n其中 ${linked.length} 筆已有歷史業務關聯，將保留歷史並改為「停用」，不會實體刪除。`
          : '';
      if (
        !window.confirm(
          `確定處理以下 ${candidates.length} 筆商品嗎？\n\n${sample}${candidates.length > 8 ? '\n…' : ''}${linkedNote}`,
        )
      ) {
        return;
      }
      const reason = window.prompt('請輸入本次刪除／停用原因：', '商品主檔資料維護');
      if (reason === null) return;
      const result = await api.productMasters.delete(
        candidates.map((item) => item.product_id),
        linked.length > 0 ? 'INACTIVATE' : 'BLOCK',
        reason.trim() || '商品主檔資料維護',
      );
      notify(
        `處理完成：刪除 ${result.summary.deleted} 筆、停用 ${result.summary.inactivated} 筆、略過 ${result.summary.skipped} 筆、失敗 ${result.summary.failed} 筆。`,
        result.summary.failed > 0 ? 'error' : 'success',
      );
      setSelectedMasterIds([]);
      const remainingTotal = Math.max(0, total - result.summary.deleted);
      const lastPage = Math.max(1, Math.ceil(remainingTotal / pageSize));
      if (page > lastPage) setPage(lastPage);
      else void loadItems();
    } catch (error: unknown) {
      notify(getApiErrorMessage(error, '商品刪除／停用失敗。'), 'error');
    } finally {
      setDeletingProducts(false);
    }
  };

  // 執行 TXT 解析預覽 (檢查檔案) - 本地極速無延遲解析
  const executePreview = (content: string, fName: string, enc: string) => {
    if (!content.trim()) {
      notify('請先選擇或貼上 TXT 檔案內容。', 'error');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = parseProductMasterTxt(content, { encoding: enc });
      if (res.missingRequiredHeaders && res.missingRequiredHeaders.length > 0) {
        notify(
          `缺少必要 Header 欄位：${res.missingRequiredHeaders.join('、')}，請檢查檔案。`,
          'error',
        );
        setPreviewData(null);
        setParsedValidRows([]);
        return;
      }
      setParsedValidRows(res.validRows);
      setPreviewData({
        file_name: fName,
        total_rows: res.totalRows,
        importable_rows: res.importableRows,
        warning_rows: res.warningRows,
        invalid_rows_count: res.invalidRowsCount,
        invalid_rows: res.invalidRows,
        breakdown: {
          inserted: res.importableRows,
          updated: 0,
          unchanged: 0,
          price_changed: 0,
          high_risk_price: 0,
          identity_conflicts: 0,
        },
        summary: res.summary,
        header_columns: res.headerColumns,
        preview_rows: res.validRows.slice(0, 50),
      });
      notify(
        `檔案檢查完成！共讀取 ${res.totalRows.toLocaleString()} 筆商品，${res.importableRows.toLocaleString()} 筆可匯入。`,
      );
    } catch (err: unknown) {
      notify(`解析 TXT 失敗：${getApiErrorMessage(err, '檔案格式無法解析')}`, 'error');
      setPreviewData(null);
      setParsedValidRows([]);
    } finally {
      setPreviewLoading(false);
    }
  };

  // 處理檔案選取與讀取 (CP950 / UTF-8 解碼)
  const processFile = async (file: File) => {
    setFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      let decoded = '';
      try {
        const decoder = new TextDecoder(encoding === 'CP950' ? 'big5' : 'utf-8');
        decoded = decoder.decode(buffer);
      } catch {
        const decoder = new TextDecoder('utf-8');
        decoded = decoder.decode(buffer);
      }
      setTxtContent(decoded);
      executePreview(decoded, file.name, encoding);
    } catch (err: unknown) {
      notify(`檔案讀取失敗：${getApiErrorMessage(err, '無法讀取檔案內容')}`, 'error');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    void processFile(file);
  };

  // 拖曳上傳
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void processFile(file);
    }
  };

  // 載入範例資料
  const handleLoadSample = () => {
    setFileName('sample_huisheng_master.txt');
    setTxtContent(SAMPLE_TXT_DATA);
    executePreview(SAMPLE_TXT_DATA, 'sample_huisheng_master.txt', encoding);
  };

  // 提交 TXT 匯入 (採用分塊上傳 Chunk Ingestion，支援超大檔案且不逾時)
  const handleCommitTxt = async () => {
    if (!parsedValidRows || parsedValidRows.length === 0) {
      notify('無可匯入之商品資料，請先選擇或檢查檔案。', 'error');
      return;
    }
    setCommitting(true);
    setImportProgress({ percent: 5, text: '正在初始化匯入批次...' });

    try {
      // 1. 初始化批次
      const startRes = await api.productMasters.startImport({
        file_name: fileName || 'import.txt',
        encoding,
        tax_type: taxType,
        total_rows: previewData?.total_rows || parsedValidRows.length,
        warning_rows: previewData?.warning_rows || 0,
        invalid_rows_count: previewData?.invalid_rows_count || 0,
        summary: previewData?.summary,
      });

      const batchId = startRes.batch_id;
      const batchNo = startRes.batch_no;

      let totalInserted = 0;
      let totalUpdated = 0;
      let totalUnchanged = 0;
      let totalPriceChanges = 0;

      // 2. 分塊上傳 (每批 50 筆，平穩傳輸且不超載)
      const chunkSize = 50;
      const totalChunks = Math.ceil(parsedValidRows.length / chunkSize);

      for (let cIdx = 0; cIdx < totalChunks; cIdx++) {
        const chunk = parsedValidRows.slice(cIdx * chunkSize, (cIdx + 1) * chunkSize);
        const chunkRes = await api.productMasters.importChunk({
          batch_id: batchId,
          tax_type: taxType,
          chunk,
        });

        totalInserted += chunkRes.inserted_count;
        totalUpdated += chunkRes.updated_count;
        totalUnchanged += chunkRes.unchanged_count;
        totalPriceChanges += chunkRes.price_change_count;

        const currentProcessed = Math.min((cIdx + 1) * chunkSize, parsedValidRows.length);
        const percent = 5 + Math.round((currentProcessed / parsedValidRows.length) * 85);
        setImportProgress({
          percent,
          text: `正在匯入資料：${currentProcessed.toLocaleString()} / ${parsedValidRows.length.toLocaleString()} 筆 (${percent}%)...`,
        });
      }

      // 3. 完成批次總結
      setImportProgress({ percent: 95, text: '正在計算重複候選與更新主檔最新狀態...' });
      const allImportedCodes = parsedValidRows.map((r) => r.item_id).filter(Boolean);

      const finishRes = await api.productMasters.finishImport({
        batch_id: batchId,
        inserted_count: totalInserted,
        updated_count: totalUpdated,
        unchanged_count: totalUnchanged,
        price_change_count: totalPriceChanges,
        all_imported_codes: allImportedCodes,
        sample_for_duplicates: parsedValidRows.slice(0, 150),
      });

      const finalResult = {
        success: true,
        batch_id: batchId,
        batch_no: batchNo,
        tax_type: taxType,
        total_rows: previewData?.total_rows || parsedValidRows.length,
        importable_rows: parsedValidRows.length,
        inserted_rows: totalInserted,
        updated_rows: totalUpdated,
        unchanged_rows: totalUnchanged,
        warning_rows: previewData?.warning_rows || 0,
        invalid_rows_count: previewData?.invalid_rows_count || 0,
        invalid_rows: previewData?.invalid_rows || [],
        price_change_count: totalPriceChanges,
        duplicate_candidate_count: finishRes.duplicate_candidate_count,
      };

      setLastImportResult(finalResult);
      notify(
        `🎉 匯入完成！成功處理 ${parsedValidRows.length.toLocaleString()} 筆商品（新增 ${totalInserted.toLocaleString()}、更新 ${totalUpdated.toLocaleString()}、未變 ${totalUnchanged.toLocaleString()}）。`,
      );
      setPreviewData(null);
      setParsedValidRows([]);
      setTxtContent('');
      void loadItems();
    } catch (err: unknown) {
      notify(`匯入失敗：${getApiErrorMessage(err, '伺服器處理失敗，請稍後再試。')}`, 'error');
    } finally {
      setCommitting(false);
      setImportProgress(null);
    }
  };

  // 開啟全品項智慧補全 Modal 並載入預估分析
  const handleOpenAutoEnrichModal = async () => {
    setShowAutoEnrichAllModal(true);
    setAutoEnrichStage('PREVIEW');
    setCurrentAutoEnrichJob(null);
    setAutoEnrichJobItems([]);
    setAutoEnrichWarning('');
    setAutoEnrichPreviewLoading(true);
    try {
      const res = await api.productEnrichment.previewAutoEnrichAll();
      if (res?.preview) {
        setAutoEnrichPreview(res.preview);
      }
    } catch (err: unknown) {
      notify(getApiErrorMessage(err, '預估分析全品項補全失敗。'), 'error');
    } finally {
      setAutoEnrichPreviewLoading(false);
    }
  };

  // 執行全品項多層級自動補全；工作進度保存於 D1，可中斷後接續。
  const handleStartAutoEnrichAll = async () => {
    setAutoEnrichRunning(true);
    setAutoEnrichStage('RUNNING');
    setAutoEnrichWarning('');

    try {
      if ((autoEnrichPreview?.drugReferenceCount || 0) === 0) {
        const syncResult = await api.productIngredients.syncTfdaData();
        setTfdaStatus(syncResult.status);
      }

      const started = await api.productEnrichment.startAutoEnrichAllJob();
      setAutoEnrichWarning(started.healthFoodSyncWarning || '');
      let isFinished = started.totalCount === 0;
      let latestJob: DrugEnrichmentJob | null = null;

      if (isFinished) {
        const emptyJob = await api.productEnrichment.getAutoEnrichAllJob(started.jobId);
        latestJob = emptyJob.job;
        setCurrentAutoEnrichJob(emptyJob.job);
      }

      while (!isFinished) {
        const result = await api.productEnrichment.processAutoEnrichAllJob(started.jobId, 40);
        latestJob = result.job;
        setCurrentAutoEnrichJob(result.job);
        isFinished = result.isFinished;
      }

      const itemResult = await api.productEnrichment.getAutoEnrichAllJobItems(started.jobId);
      setAutoEnrichJobItems(itemResult.items || []);
      setAutoEnrichStage('COMPLETED');
      notify(
        `全庫智慧補全完成：自動確認 ${Number(latestJob?.auto_confirmed_count || 0).toLocaleString()} 筆，待人工確認 ${Number(latestJob?.review_count || 0).toLocaleString()} 筆。`,
        'success',
      );
      void loadItems();
      void loadEnrichmentSummary();
      void loadDrugKpi();
    } catch (err: unknown) {
      setAutoEnrichStage('PREVIEW');
      notify(
        `${getApiErrorMessage(err, '全庫智慧補全執行中斷。')} 已完成進度會保留，重新執行即可接續。`,
        'error',
      );
    } finally {
      setAutoEnrichRunning(false);
    }
  };

  const displayedSearchQueries = candidateDrawerData?.searchRun?.queries?.length
    ? candidateDrawerData.searchRun.queries
    : (candidateDrawerData?.searchPlan?.queries || []).map((query) => ({
        ...query,
        executed: false,
        resultCount: 0,
        status: 'PLANNED',
      }));
  const selectedGtinResult = normalizeBarcodeCandidate(
    selectedCandidateProduct?.primary_barcode_candidate,
  );
  const selectedGtin =
    selectedGtinResult.status === 'VALID' || selectedGtinResult.status === 'REPAIRED'
      ? selectedGtinResult.value
      : '';
  const lookupTrace = candidateDrawerData?.searchRun?.providerTrace || [];
  const sourceComparison = candidateDrawerData?.searchRun?.sourceComparison;
  const identityDebug =
    candidateDrawerData?.searchRun?.identityDebug ||
    (candidateDrawerData?.searchPlan
      ? {
          normalizedProductName: candidateDrawerData.searchPlan.searchNameNormalized,
          baseProductName: candidateDrawerData.searchPlan.baseProductName,
          specification: candidateDrawerData.searchPlan.resolvedSpecification,
          salesUnit: candidateDrawerData.searchPlan.resolvedSalesUnit,
          barcodeStatus: candidateDrawerData.searchPlan.barcodeStatus,
          barcodeRawValues: candidateDrawerData.searchPlan.barcodeRawValues,
          validGtins: candidateDrawerData.searchPlan.validGtins,
          brandCandidate: candidateDrawerData.searchPlan.brandCandidate,
          brandCandidateMatchedAlias: candidateDrawerData.searchPlan.brandCandidateMatchedAlias,
        }
      : null);
  const canViewIngredientDebug = currentUserRole === 'ADMIN' || import.meta.env.DEV;

  return (
    <div className="space-y-6 pb-12">
      <PageHeader
        eyebrow="HUI-SHENG MASTER DATA"
        title="惠生正式商品主檔"
        description="管理惠生既有商品主檔、TXT 匯入、分類品質與 AI 智慧成分查找；所有候選都保留可信來源供採購確認。"
        actions={
          <div className="flex flex-wrap gap-2">
            {(currentUserRole === 'ADMIN' || currentUserRole === 'BUYER') && (
              <Button
                variant="primary"
                onClick={() => void handleOpenAutoEnrichModal()}
                className="bg-teal-800 hover:bg-teal-900 text-white font-bold shadow-sm"
              >
                ⚡ 一鍵全庫智慧補全
              </Button>
            )}
            <Button
              variant={activeTab === 'import' ? 'primary' : 'secondary'}
              onClick={() => setActiveTab('import')}
              className={activeTab === 'import' ? 'bg-teal-700' : ''}
            >
              📥 上傳商品主檔 TXT
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setRosterParams({ categoryCode: categoryCode || undefined });
                setRosterScopeTitle(
                  categoryCode ? `分類代碼「${categoryCode}」全部商品` : '全品項正式商品主檔',
                );
                setRosterModalOpen(true);
              }}
              className="border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100 font-bold"
            >
              📊 依廠商匯出清冊
            </Button>
            {(currentUserRole === 'ADMIN' || currentUserRole === 'BUYER') && (
              <Button
                variant="secondary"
                onClick={() => {
                  resetFamilyForm();
                  setShowFamilyManager(true);
                }}
                className="border-slate-300 bg-white text-slate-800 hover:bg-slate-50 font-bold"
              >
                🧩 商品家族管理
              </Button>
            )}
            <Button
              variant={activeTab === 'quality' ? 'primary' : 'secondary'}
              onClick={() => {
                setActiveTab('quality');
                setQualityActiveTab('drug_ingredients');
                void loadDrugReviews();
              }}
              className={activeTab === 'quality' ? 'bg-teal-700' : ''}
            >
              🩺 資料待確認中心
              {drugKpi && drugKpi.needsReviewCount > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px]">
                  {drugKpi.needsReviewCount} 筆待確認
                </span>
              )}
            </Button>
          </div>
        }
      />

      {/* 4 個主頁籤 */}
      <div className="flex border-b border-slate-200">
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'masters'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => {
            setActiveTab('masters');
            void loadItems();
            void loadDrugKpi();
            void loadTfdaStatus();
          }}
        >
          🗂 正式商品主檔 {total > 0 && `(${total})`}
        </button>
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'pos_export'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => {
            setActiveTab('pos_export');
            void loadPosBatches();
            void loadItems();
          }}
        >
          📤 POS 匯出與建檔確認
        </button>
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'import'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('import')}
        >
          📥 上傳惠生商品主檔 TXT
        </button>
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'quality'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => {
            setActiveTab('quality');
            if (qualityActiveTab === 'drug_ingredients') {
              void loadDrugReviews();
            } else {
              void loadQualityCenter(qualityActiveTab);
            }
          }}
        >
          🩺 資料待確認中心
        </button>
        <button
          className={`px-5 py-3 text-sm font-bold border-b-2 transition ${
            activeTab === 'history'
              ? 'border-teal-600 text-teal-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
          onClick={() => setActiveTab('history')}
        >
          📜 匯入紀錄與 Rollback
        </button>
      </div>

      {/* TAB 1: 正式商品主檔列表 */}
      {activeTab === 'masters' && (
        <div className="space-y-6">
          {/* 西藥成分完整率 KPI 儀表板與 TFDA 官方資料來源狀態 */}
          {drugKpi && (
            <Card className="p-5 bg-gradient-to-r from-emerald-50/70 via-teal-50/50 to-sky-50/50 border-teal-200 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-base font-bold text-teal-950 flex items-center gap-1.5">
                      💊 西藥成分完整率儀表
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-extrabold border border-emerald-300">
                      完整率 {drugKpi.completionRatePercentage}%
                    </span>
                    {tfdaStatus && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                          (tfdaStatus.licenseCount || 0) === 0
                            ? 'bg-rose-100 text-rose-800 border-rose-300 animate-pulse'
                            : 'bg-teal-100 text-teal-800 border-teal-300'
                        }`}
                      >
                        {(tfdaStatus.licenseCount || 0) === 0
                          ? '❌ TFDA 官方資料尚未同步 (0筆)'
                          : `✓ TFDA 官方資料就緒 (${tfdaStatus.licenseCount.toLocaleString()} 筆許可證 / ${tfdaStatus.ingredientCount.toLocaleString()} 筆處方)`}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600">
                    依據衛福部 TFDA 官方全部藥品許可證 (Dataset 9122) 與詳細處方成分 (Dataset 9121)
                    結構化資料，自動比對並補全 A~D 類西藥商品主檔成分。
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 text-xs bg-white/80 px-3 py-1.5 rounded-lg border border-teal-100 shadow-2xs">
                    <span className="text-slate-500">西藥總數:</span>
                    <span className="font-bold text-slate-800">
                      {drugKpi.totalWesternDrugs.toLocaleString()}
                    </span>
                    <span className="text-slate-300">|</span>
                    <span className="text-emerald-700 font-bold">
                      ✓ 已確認: {drugKpi.confirmedOfficialCount.toLocaleString()}
                    </span>
                    <span className="text-slate-300">|</span>
                    <span className="text-amber-700 font-bold">
                      △ 待確認: {drugKpi.needsReviewCount.toLocaleString()}
                    </span>
                    <span className="text-slate-300">|</span>
                    <span className="text-rose-700 font-bold">
                      ○ 缺處方: {drugKpi.missingCount.toLocaleString()}
                    </span>
                  </div>

                  <Button
                    size="sm"
                    onClick={() => {
                      if ((tfdaStatus?.licenseCount || 0) === 0) {
                        notify(
                          'TFDA 官方藥品資料尚未同步（目前 0 筆），請先點擊「立即同步TFDA」按鈕。',
                          'error',
                        );
                        return;
                      }
                      setShowDrugBatchModal(true);
                      void handlePreviewDrugBatch();
                    }}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                  >
                    ⚡ 批次補全藥品
                  </Button>

                  {(currentUserRole === 'ADMIN' || currentUserRole === 'BUYER') && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void handleSyncTfda()}
                      loading={syncingTfda}
                      className="text-xs border-teal-300 text-teal-900 bg-white font-bold hover:bg-teal-50"
                      title={
                        tfdaStatus?.lastSyncedAt
                          ? `上次同步時間：${tfdaStatus.lastSyncedAt}`
                          : '一鍵下載並同步 TFDA 官方資料庫'
                      }
                    >
                      🔄 立即同步TFDA官方資料 ({tfdaStatus?.licenseCount || 0}筆)
                    </Button>
                  )}
                </div>
              </div>

              {/* 進度條 */}
              <div className="w-full bg-slate-200 rounded-full h-2 mt-3 overflow-hidden">
                <div
                  className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min(100, Math.max(0, drugKpi.completionRatePercentage))}%`,
                  }}
                />
              </div>
            </Card>
          )}
          <Card className="p-4 bg-slate-50 border-slate-200">
            <div className="grid gap-3 md:grid-cols-7">
              <div className="md:col-span-2">
                <Input
                  label="關鍵字搜尋"
                  placeholder="搜尋商品代號、品名、條碼、廠商…"
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <Select
                label="大分類 (A~P)"
                value={categoryCode}
                onChange={(e) => {
                  const nextCategory = e.target.value;
                  setCategoryCode(nextCategory);
                  if (
                    subcategoryCode &&
                    subcategories.find((item) => item.code === subcategoryCode)?.category_code !==
                      nextCategory
                  ) {
                    setSubcategoryCode('');
                  }
                  setPage(1);
                }}
              >
                <option value="">全部大分類</option>
                {Object.entries(HUI_SHENG_CATEGORY_MAP).map(([code, name]) => (
                  <option key={code} value={code}>
                    {code} - {name}
                  </option>
                ))}
              </Select>
              <Select
                label="子分類代碼 (mcs_no)"
                value={subcategoryCode}
                onChange={(e) => {
                  setSubcategoryCode(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">全部子分類</option>
                {subcategories
                  .filter((item) => !categoryCode || item.category_code === categoryCode)
                  .map((item) => (
                    <option key={item.id} value={item.code}>
                      {item.code}｜{item.name}
                    </option>
                  ))}
              </Select>
              <Select
                label="類型過濾"
                value={recordType}
                onChange={(e) => {
                  setRecordType(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">全部品項</option>
                {PRODUCT_RECORD_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} ({option.value})
                  </option>
                ))}
              </Select>
              <Select
                label="商品家族"
                value={familyFilter}
                onChange={(event) => {
                  setFamilyFilter(event.target.value);
                  setPage(1);
                }}
              >
                <option value="">全部家族</option>
                {families.map((family) => (
                  <option key={family.id} value={family.id}>
                    {family.family_code}｜{family.family_name}
                  </option>
                ))}
              </Select>
              <div className="flex items-end gap-2">
                <Button onClick={() => void loadItems()} className="flex-1 bg-teal-700">
                  🔍 查詢
                </Button>
                {(qualityFilter || missingFilter) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setQualityFilter('');
                      setMissingFilter('');
                      void loadItems('', '');
                    }}
                  >
                    清除篩選
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {loading ? (
            <Loading label="載入商品主檔中…" />
          ) : items.length === 0 ? (
            <Card className="p-8 text-center bg-slate-50 border-dashed border-2 border-slate-200">
              <div className="max-w-md mx-auto space-y-4">
                <div className="text-4xl">📦</div>
                <h3 className="text-lg font-bold text-ink">目前尚無符合之商品主檔資料</h3>
                <p className="text-xs text-slate-500">
                  可使用 TXT 檔案直接匯入，或點擊下方按鈕前往匯入。
                </p>
                <div className="flex justify-center gap-3 pt-2">
                  <Button onClick={() => setActiveTab('import')} className="bg-teal-700 font-bold">
                    前往上傳 TXT
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="p-4 border-b border-slate-100 flex flex-wrap justify-between items-center gap-2 text-xs text-slate-500">
                <div className="flex items-center gap-3">
                  <span className="font-bold text-slate-700">共找到 {total} 筆商品主檔</span>
                  {selectedMasterIds.length > 0 && (
                    <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-800 font-bold">
                      已勾選 {selectedMasterIds.length} 筆
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedMasterIds.length > 0 && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => void handleBatchEnrichSelected()}
                        loading={batchEnriching}
                        className="bg-teal-700 hover:bg-teal-800 text-white font-bold text-xs"
                      >
                        ⚡ 批次查詢成分（已選 {selectedMasterIds.length} 筆）
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => void handleBatchConfirmSelected()}
                        loading={batchConfirming}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                      >
                        ✓ 批次確認採用（已選 {selectedMasterIds.length} 筆）
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setRosterParams({ productIds: selectedMasterIds });
                          setRosterScopeTitle(`已勾選之 ${selectedMasterIds.length} 筆商品`);
                          setRosterModalOpen(true);
                        }}
                        className="bg-teal-50 hover:bg-teal-100 text-teal-800 border-teal-300 font-bold text-xs"
                      >
                        📊 匯出所選清冊（已選 {selectedMasterIds.length} 筆）
                      </Button>
                      {(currentUserRole === 'ADMIN' || currentUserRole === 'BUYER') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void deleteSelectedProducts(selectedMasterIds)}
                          loading={deletingProducts}
                          className="bg-rose-50 hover:bg-rose-100 text-rose-800 border-rose-300 font-bold text-xs"
                        >
                          🗑️ 刪除／停用所選（本頁 {selectedMasterIds.length} 筆）
                        </Button>
                      )}
                    </>
                  )}
                  {selectedMasterIds.length === 0 && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setRosterParams({ categoryCode: categoryCode || undefined });
                        setRosterScopeTitle(
                          categoryCode ? `分類代碼「${categoryCode}」全部商品` : '全部正式商品主檔',
                        );
                        setRosterModalOpen(true);
                      }}
                      className="bg-teal-50 hover:bg-teal-100 text-teal-800 border-teal-300 font-bold text-xs"
                    >
                      📊 依廠商匯出清冊
                    </Button>
                  )}
                  <span className="text-teal-700 font-medium">
                    💡 支援「自動補全商品成分」及「官方藥品許可證與健食功效成分比對」
                  </span>
                </div>
              </div>
              <Table className="[&_table]:min-w-[1700px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="p-3 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={items.length > 0 && selectedMasterIds.length === items.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMasterIds(items.map((i) => i.id));
                          } else {
                            setSelectedMasterIds([]);
                          }
                        }}
                        className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        title="全選 / 取消全選當前頁商品"
                      />
                    </th>
                    <th className="p-3">商品代號</th>
                    <th className="p-3">品名與規格</th>
                    <th className="p-3">大分類 / 子分類</th>
                    <th className="p-3">商品家族</th>
                    <th className="p-3">進貨廠商</th>
                    <th className="p-3">商品成分與來源</th>
                    <th className="p-3">目前進價 (稅別)</th>
                    <th className="p-3">VIP價 / 售價</th>
                    <th className="p-3">庫存 / 在途</th>
                    <th className="p-3">廠商搭贈</th>
                    <th className="p-3">狀態標籤</th>
                    <th className="p-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50/50">
                      <td className="p-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedMasterIds.includes(item.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedMasterIds([...selectedMasterIds, item.id]);
                            } else {
                              setSelectedMasterIds(
                                selectedMasterIds.filter((id) => id !== item.id),
                              );
                            }
                          }}
                          className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                      </td>
                      <td className="p-3 font-mono font-bold text-teal-800">{item.product_code}</td>
                      <td className="p-3">
                        <div className="font-bold text-ink">{item.product_name}</div>
                        {item.english_name && (
                          <div className="text-[11px] text-slate-400">{item.english_name}</div>
                        )}
                        <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap items-center gap-1">
                          <span>單位：{item.sales_unit || '未設定'} ｜ 條碼：</span>
                          {normalizeBarcodeCandidate(item.primary_barcode_candidate).status ===
                          'BARCODE_REPAIR_REQUIRED' ? (
                            <span
                              className="px-1.5 py-0.2 rounded bg-amber-50 text-amber-800 font-bold border border-amber-200 text-[10px]"
                              title="原始條碼為科學記號且已遺失末尾數字，待來源修復"
                            >
                              條碼待修復
                            </span>
                          ) : (
                            <span>{displayBarcodeCandidate(item.primary_barcode_candidate)}</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-bold rounded text-[11px]">
                          {item.product_category_code
                            ? `${item.product_category_code}｜${item.product_category_name || HUI_SHENG_CATEGORY_MAP[item.product_category_code] || ''}`
                            : '未設定'}
                        </span>
                        <div className="text-[11px] text-slate-500 mt-1">
                          子分類：
                          {item.product_subcategory_code ||
                            item.imported_subcategory_code ||
                            '—'}{' '}
                          {item.product_subcategory_name || item.imported_subcategory_name || ''}
                        </div>
                      </td>
                      <td className="p-3 max-w-[150px] whitespace-normal break-words">
                        {item.product_family_id ? (
                          <div>
                            <div className="font-bold text-slate-800">
                              {item.family_code || '—'}｜{item.family_name || '未命名家族'}
                            </div>
                            <div className="text-[10px] text-slate-400 mt-0.5">穩定 ID 關聯</div>
                          </div>
                        ) : (
                          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px]">
                            待編制
                          </span>
                        )}
                      </td>
                      <td className="p-3 max-w-[180px] whitespace-normal break-words">
                        {item.primary_supplier?.vendor_id ? (
                          <div>
                            <div className="font-bold text-teal-800">
                              {item.primary_supplier.vendor_code || '—'}｜
                              {item.primary_supplier.vendor_name || '未命名廠商'}
                            </div>
                            {(item.supplier_terms?.length || 0) > 1 && (
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                另有 {(item.supplier_terms?.length || 1) - 1} 家供應商
                              </div>
                            )}
                          </div>
                        ) : item.imported_supplier_name || item.legacy_vendor_code ? (
                          <div>
                            <div className="text-slate-700">
                              {item.legacy_vendor_code || '—'}｜
                              {item.imported_supplier_name || '匯入廠商未命名'}
                            </div>
                            <div className="text-[10px] text-amber-700 font-bold mt-0.5">
                              匯入來源（待設定正式廠商）
                            </div>
                          </div>
                        ) : (
                          <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-bold">
                            待設定
                          </span>
                        )}
                      </td>
                      <td className="p-3 max-w-[200px]">
                        {item.ingredient_status === 'CONFIRMED' ? (
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-bold border border-emerald-200 text-[10px]">
                                ✓ 已確認
                              </span>
                              {item.ingredient_source_name && (
                                <span
                                  className="text-[10px] text-teal-700 truncate max-w-[110px]"
                                  title={item.ingredient_source_name}
                                >
                                  [{item.ingredient_source_name}]
                                </span>
                              )}
                            </div>
                            <div
                              className="text-[11px] text-slate-600 truncate mt-0.5"
                              title={item.ingredient_raw_text || ''}
                            >
                              {item.ingredient_raw_text || '已正式建立'}
                            </div>
                          </div>
                        ) : [
                            'FOUND_UNCONFIRMED',
                            'FOUND_COMPLETE',
                            'FOUND_PARTIAL',
                            'FOUND_MULTIPLE',
                          ].includes(item.ingredient_status || '') ? (
                          <div>
                            <div className="flex items-center gap-1">
                              <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 font-bold border border-amber-200 text-[10px]">
                                △{' '}
                                {item.ingredient_status === 'FOUND_PARTIAL'
                                  ? '部分成分待確認'
                                  : item.ingredient_status === 'FOUND_MULTIPLE'
                                    ? '多來源待比較'
                                    : '成分待確認'}{' '}
                                ({Math.round(item.ingredient_confidence_score || 0)}%)
                              </span>
                            </div>
                            <div
                              className="text-[11px] text-slate-600 truncate mt-0.5"
                              title={item.ingredient_raw_text || ''}
                            >
                              {item.ingredient_raw_text}
                            </div>
                          </div>
                        ) : ['AMBIGUOUS', 'SOURCE_CONFLICT'].includes(
                            item.ingredient_status || '',
                          ) ? (
                          <span className="px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 font-bold border border-rose-200 text-[10px]">
                            ! 來源衝突
                          </span>
                        ) : item.ingredient_status === 'IDENTITY_UNCONFIRMED' ? (
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-bold text-[10px]">
                            ? 身分不明
                          </span>
                        ) : [
                            'NOT_FOUND',
                            'NO_SOURCE',
                            'NO_MATCH',
                            'SOURCE_FETCH_FAILED',
                            'FOUND_PRODUCT_NO_FULL_INGREDIENTS',
                          ].includes(item.ingredient_status || '') ? (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[10px]">
                            —{' '}
                            {SEARCH_PROVIDER_STATUS_LABELS[item.ingredient_status || ''] ||
                              '目前無候選'}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 text-[10px]">
                            ○ 未查找
                          </span>
                        )}
                      </td>
                      <td className="p-3 font-bold text-ink">
                        <div>
                          {item.current_purchase_price === 0 ||
                          item.current_purchase_price === null ? (
                            <span className="text-amber-700 font-bold">未設定 (0元)</span>
                          ) : (
                            <Money value={item.current_purchase_price} />
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 font-normal">
                          {item.purchase_price_tax_type === 'TAX_EXCLUSIVE' ? '未稅' : '含稅'}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="font-bold text-teal-700">
                          VIP: <Money value={item.vip_price} />
                        </div>
                        <div className="text-slate-400 text-[11px]">
                          售: <Money value={item.regular_sale_price} />
                        </div>
                      </td>
                      <td className="p-3">
                        <div
                          className={`font-bold ${item.warehouse_stock < 0 ? 'text-rose-600' : 'text-slate-700'}`}
                        >
                          庫存: {item.warehouse_stock}
                        </div>
                        <div className="text-slate-400 text-[11px]">
                          在途: {item.on_order_quantity}
                        </div>
                      </td>
                      <td className="p-3">
                        {item.current_vendor_giveaway_terms ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded text-[11px]">
                            🎁 {item.current_vendor_giveaway_terms}
                          </span>
                        ) : item.detected_giveaway_candidate ? (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-[11px]">
                            候選: {item.detected_giveaway_candidate}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {item.status_hint === 'INACTIVE' && (
                            <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded text-[10px] font-bold">
                              已停用
                            </span>
                          )}
                          {item.missing_from_latest_import === 1 && (
                            <span className="px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded text-[10px]">
                              未出現 ({item.missing_import_count}次)
                            </span>
                          )}
                          {item.sku_identity_conflict_flag === 1 && (
                            <span className="px-1.5 py-0.5 bg-rose-200 text-rose-900 rounded text-[10px] font-bold">
                              身分衝突
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1">
                          {['A', 'B', 'C', 'D'].includes(
                            (
                              item.product_category_code ||
                              item.product_code?.charAt(0) ||
                              ''
                            ).toUpperCase(),
                          ) && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => void handleSingleDrugEnrich(item.id)}
                              loading={enrichingSingleDrugId === item.id}
                              disabled={Boolean(enrichingSingleDrugId)}
                              className="text-xs px-2 py-1 min-h-7 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300 font-bold"
                              title="直接查詢 TFDA 本地資料庫精準匹配處方成分"
                            >
                              💊 官方處方補全
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void handleSingleEnrich(item.id)}
                            loading={enrichingSingleId === item.id}
                            disabled={Boolean(enrichingSingleId)}
                            className="text-xs px-2 py-1 min-h-7"
                            title="依國際條碼/許可證自動查找官方與可信成分"
                          >
                            ✨ AI智慧查找成分
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => void openCandidateDrawer(item)}
                            className="text-xs px-2 py-1 min-h-7 bg-teal-50 hover:bg-teal-100 text-teal-800"
                            title="查看候選來源與確認"
                          >
                            📋 候選
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEditModal(item)}
                            className="text-xs px-2.5 py-1 min-h-7 text-teal-800 bg-teal-50 hover:bg-teal-100 border border-teal-200 font-bold shadow-2xs"
                            title="編輯修正商品名稱、代號、條碼與規格"
                          >
                            ✏️ 編輯
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setRosterParams({ productIds: [item.id] });
                              setRosterScopeTitle(`商品「${item.product_name}」所屬廠商清冊`);
                              setRosterModalOpen(true);
                            }}
                            className="text-xs px-2 py-1 min-h-7 bg-teal-50 hover:bg-teal-100 text-teal-800 border-teal-200"
                            title="匯出此商品及其所屬廠商之 Excel 清冊"
                          >
                            📊 清冊
                          </Button>
                          {(currentUserRole === 'ADMIN' || currentUserRole === 'BUYER') && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => void deleteSelectedProducts([item.id])}
                              loading={deletingProducts}
                              className="text-xs px-2 py-1 min-h-7 bg-rose-50 hover:bg-rose-100 text-rose-800 border-rose-200"
                              title="刪除無歷史關聯商品；有歷史關聯時改為停用"
                            >
                              🗑️ 刪除
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="p-4 border-t border-slate-100 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between text-xs text-slate-600">
                <div>
                  第 {page} / {Math.max(1, Math.ceil(total / pageSize))} 頁，每頁 {pageSize}{' '}
                  筆；全選僅選取目前頁面。
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={page <= 1 || loading}
                    onClick={() => setPage((current) => Math.max(1, current - 1))}
                  >
                    上一頁
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={page >= Math.ceil(total / pageSize) || loading}
                    onClick={() => setPage((current) => current + 1)}
                  >
                    下一頁
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* TAB: POS 匯出與建檔確認 */}
      {activeTab === 'pos_export' && (
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-4 mb-5">
              <div>
                <h3 className="text-base font-bold text-ink flex items-center gap-2">
                  <span>📤 產生惠生 POS / ERP 標準匯出檔 (.txt / TSV)</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 font-bold border border-teal-200">
                    規格標準版 v1.0
                  </span>
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  依照惠生 POS 系統匯入規範產生 Tab-delimited (TSV)
                  格式檔案，包含商品代號、中英文品名、分類編號、廠商、國際條碼、售價、成本與進價。
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  onClick={() => void handleExportPos()}
                  loading={exportingPos}
                  className="bg-teal-700 hover:bg-teal-800 text-white font-bold text-xs shadow"
                >
                  📥 匯出並下載 POS 標準檔
                  {selectedPosProductIds.length > 0 && ` (已選 ${selectedPosProductIds.length} 項)`}
                </Button>
              </div>
            </div>

            {/* 提示治理要點 */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <span>⚠️ 治理規範說明：</span>
                <span>「已匯出」不等於「POS 建檔完成」</span>
              </div>
              <p className="text-amber-800 leading-relaxed">
                匯出檔案後，門市或資訊人員需將檔案匯入實體 POS 系統。在 POS
                系統完成建檔前，商品不能逕行正式發布門市上市公告。請於 POS
                建檔成功後，至下方歷史批次點擊「✓ 回報 POS 建檔完成」，形成正式治理閉環。
              </p>
            </div>
          </Card>

          {/* POS 匯出歷史批次與回報紀錄 */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h4 className="text-sm font-bold text-ink">📋 歷史匯出批次與建檔確認紀錄</h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  追蹤所有產出之 POS 匯出批次、匯出人及建檔確認狀態
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void loadPosBatches()}
                loading={loadingPosBatches}
                className="text-xs"
              >
                🔄 重新整理
              </Button>
            </div>

            {loadingPosBatches ? (
              <Loading label="載入歷史批次中…" />
            ) : posBatches.length === 0 ? (
              <EmptyState
                title="尚無 POS 匯出紀錄"
                description="點擊上方「匯出並下載 POS 標準檔」以產出第一份匯出檔。"
              />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <Table>
                  <thead>
                    <tr className="bg-slate-50 text-slate-700 text-xs">
                      <th className="p-3 text-left">批次號碼</th>
                      <th className="p-3 text-left">規格版本</th>
                      <th className="p-3 text-right">商品筆數</th>
                      <th className="p-3 text-left">匯出時間 / 人員</th>
                      <th className="p-3 text-center">狀態</th>
                      <th className="p-3 text-left">建檔確認時間 / 人員</th>
                      <th className="p-3 text-left">確認說明</th>
                      <th className="p-3 text-center">操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {posBatches.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-50/60">
                        <td className="p-3 font-mono font-bold text-ink">{b.batch_no}</td>
                        <td className="p-3 text-slate-600 font-mono text-[11px]">
                          {b.format_version}
                        </td>
                        <td className="p-3 text-right font-bold text-ink">{b.item_count} 筆</td>
                        <td className="p-3 text-slate-600">
                          <div>
                            {b.exported_at ? new Date(b.exported_at).toLocaleString('zh-TW') : '—'}
                          </div>
                          <div className="text-[11px] text-slate-400">
                            {b.exported_by_name || b.exported_by}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                              b.status === 'CONFIRMED'
                                ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                                : b.status === 'FAILED'
                                  ? 'bg-rose-100 text-rose-800 border border-rose-300'
                                  : 'bg-amber-100 text-amber-800 border border-amber-300'
                            }`}
                          >
                            {b.status === 'CONFIRMED'
                              ? '✓ 建檔完成'
                              : b.status === 'FAILED'
                                ? '✕ 失敗'
                                : '⏳ 已匯出待確認'}
                          </span>
                        </td>
                        <td className="p-3 text-slate-600">
                          {b.confirmed_at ? (
                            <>
                              <div>{new Date(b.confirmed_at).toLocaleString('zh-TW')}</div>
                              <div className="text-[11px] text-emerald-700 font-medium">
                                {b.confirmed_by_name || b.confirmed_by}
                              </div>
                            </>
                          ) : (
                            <span className="text-slate-400">尚未確認</span>
                          )}
                        </td>
                        <td className="p-3 text-slate-600 max-w-xs truncate">
                          {b.confirmation_note || '—'}
                        </td>
                        <td className="p-3 text-center">
                          {b.status === 'EXPORTED' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setConfirmingBatch(b);
                                setConfirmedByName('');
                                setConfirmationNote('');
                              }}
                              className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                            >
                              ✓ 回報確認
                            </Button>
                          )}
                          {b.status === 'CONFIRMED' && (
                            <span className="text-xs text-emerald-600 font-bold">已結案</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card>

          {/* 回報 POS 建檔完成 Modal */}
          {confirmingBatch && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
              <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 space-y-4">
                <div className="flex justify-between items-center border-b border-slate-100 pb-3">
                  <h3 className="text-base font-bold text-ink">✓ 回報 POS 建檔完成</h3>
                  <button
                    type="button"
                    onClick={() => setConfirmingBatch(null)}
                    className="text-slate-400 hover:text-slate-600 text-lg"
                  >
                    ✕
                  </button>
                </div>
                <div className="text-xs text-slate-600 space-y-1">
                  <div>
                    匯出批次：<strong className="text-ink">{confirmingBatch.batch_no}</strong>
                  </div>
                  <div>
                    包含商品：<strong className="text-ink">{confirmingBatch.item_count} 項</strong>
                  </div>
                  <div>
                    匯出時間：{new Date(confirmingBatch.exported_at).toLocaleString('zh-TW')}
                  </div>
                </div>
                <div className="space-y-3 text-xs">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">
                      確認人員姓名 <span className="text-rose-500">*</span>
                    </label>
                    <Input
                      placeholder="例：採購專案經理 陳小明"
                      value={confirmedByName}
                      onChange={(e) => setConfirmedByName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">
                      確認說明 / POS 系統單號 (選填)
                    </label>
                    <textarea
                      rows={3}
                      className="w-full p-2 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 text-xs"
                      placeholder="例：已於惠生 POS 主機匯入成功，商品代號已生效且條碼可刷讀。"
                      value={confirmationNote}
                      onChange={(e) => setConfirmationNote(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button variant="secondary" onClick={() => setConfirmingBatch(null)}>
                    取消
                  </Button>
                  <Button
                    onClick={() => void handleConfirmPosBatch()}
                    loading={savingConfirmation}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
                  >
                    確認建檔完成
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: 上傳惠生商品主檔 TXT (極簡化 3 步) */}
      {activeTab === 'import' && (
        <div className="space-y-6">
          {/* 匯入完成摘要 (Section 二十七) */}
          {lastImportResult && (
            <Card className="p-6 bg-emerald-50/70 border-emerald-200">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-base font-bold text-emerald-900">
                    🎉 匯入完成（批號：{lastImportResult.batch_no}）
                  </h3>
                  <p className="text-xs text-emerald-700 mt-1">
                    進價稅別：{lastImportResult.tax_type === 'TAX_EXCLUSIVE' ? '未稅' : '含稅'}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setLastImportResult(null)}>
                  ✕ 關閉
                </Button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center mb-4">
                <div className="p-3 bg-white rounded-xl border border-emerald-200">
                  <span className="text-[11px] text-slate-500 block">成功處理</span>
                  <span className="text-xl font-extrabold text-ink">
                    {lastImportResult.importable_rows} 筆
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-emerald-200">
                  <span className="text-[11px] text-emerald-600 block">新增</span>
                  <span className="text-xl font-extrabold text-emerald-700">
                    +{lastImportResult.inserted_rows}
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-sky-200">
                  <span className="text-[11px] text-sky-600 block">更新</span>
                  <span className="text-xl font-extrabold text-sky-700">
                    ~{lastImportResult.updated_rows}
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-slate-200">
                  <span className="text-[11px] text-slate-500 block">無變更</span>
                  <span className="text-xl font-extrabold text-slate-600">
                    {lastImportResult.unchanged_rows || 0}
                  </span>
                </div>
                <div className="p-3 bg-white rounded-xl border border-amber-200">
                  <span className="text-[11px] text-amber-600 block">需注意 / 待確認</span>
                  <span className="text-xl font-extrabold text-amber-700">
                    {lastImportResult.warning_rows || 0}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button onClick={() => setActiveTab('masters')} className="bg-teal-700 text-xs">
                  🗂 查看商品主檔
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setActiveTab('quality');
                    void loadQualityCenter('price_changes');
                  }}
                  className="text-xs"
                >
                  🩺 查看需注意資料 ({lastImportResult.warning_rows || 0})
                </Button>
              </div>
            </Card>
          )}

          {/* 上傳主區塊 (Section 二、三、三十三) */}
          <Card className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-base font-bold text-ink">上傳惠生商品主檔 TXT</h3>
                <p className="text-xs text-slate-500 mt-1">
                  請選取惠生 POS / ERP 匯出之商品主檔 TXT
                  檔案，系統將自動辨識編碼並執行安全比對與更新。
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={handleLoadSample} className="text-xs">
                ⚡ 載入示範 TXT 測試資料
              </Button>
            </div>

            {/* 【1】選擇檔案 */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${
                isDragging
                  ? 'border-teal-500 bg-teal-50/50 scale-[1.01]'
                  : 'border-slate-300 hover:border-teal-400 bg-slate-50/50'
              }`}
              onClick={() => document.getElementById('file-upload-input')?.click()}
            >
              <input
                id="file-upload-input"
                type="file"
                accept=".txt,.tsv,.csv"
                className="hidden"
                onChange={handleFileUpload}
              />
              <div className="max-w-md mx-auto space-y-2">
                <div className="text-4xl">📄</div>
                <div className="text-sm font-bold text-ink">
                  {fileName ? `已選取檔案：${fileName}` : '點擊或將惠生商品主檔 TXT 檔案拖曳至此'}
                </div>
                <p className="text-xs text-slate-400">支援副檔名：.txt, .tsv (最大 20MB)</p>
              </div>
            </div>

            {/* 【2】選擇目前進價稅別 (Section 二) */}
            <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <label className="block text-xs font-bold text-slate-700 mb-2">
                目前進價稅別（必填）：
              </label>
              <div className="flex gap-6 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="taxType"
                    value="TAX_INCLUSIVE"
                    checked={taxType === 'TAX_INCLUSIVE'}
                    onChange={() => setTaxType('TAX_INCLUSIVE')}
                  />
                  <span className="font-bold text-ink">目前進價為含稅 (預設)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="taxType"
                    value="TAX_EXCLUSIVE"
                    checked={taxType === 'TAX_EXCLUSIVE'}
                    onChange={() => setTaxType('TAX_EXCLUSIVE')}
                  />
                  <span className="font-bold text-ink">目前進價為未稅</span>
                </label>
              </div>
            </div>

            {/* 【3】檢查檔案按鈕 */}
            <div className="flex justify-end gap-3 mt-6">
              {txtContent && (
                <Button
                  onClick={() => void executePreview(txtContent, fileName, encoding)}
                  loading={previewLoading}
                  className="bg-teal-700 hover:bg-teal-800 text-white font-bold text-sm px-6 py-2.5 shadow"
                >
                  🔍 檢查檔案
                </Button>
              )}
            </div>
          </Card>

          {/* 檔案檢查完成後之簡單摘要 (Section 四、五、三十三) */}
          {previewLoading ? (
            <Loading label="系統自動檢查檔案與比對商品中…" />
          ) : previewData ? (
            <Card className="p-6 space-y-6 bg-slate-50/50 border-slate-200">
              {previewData.previous_import_warning && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                  ℹ️ {previewData.previous_import_warning}
                </div>
              )}

              <div>
                <h4 className="text-base font-bold text-ink">檢查完成摘要</h4>
                <p className="text-xs text-slate-500 mt-0.5">檔案名稱：{previewData.file_name}</p>
              </div>

              {/* 核心大數字摘要 (Section 四 & 三十三) */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-white rounded-2xl border border-slate-200">
                  <span className="text-xs text-slate-500 block font-bold">讀取商品</span>
                  <span className="text-2xl font-extrabold text-ink mt-1 block">
                    {previewData.total_rows.toLocaleString()} 筆
                  </span>
                </div>

                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200">
                  <span className="text-xs text-emerald-800 block font-bold">✓ 可匯入</span>
                  <span className="text-2xl font-extrabold text-emerald-700 mt-1 block">
                    {previewData.importable_rows.toLocaleString()} 筆
                  </span>
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200">
                  <span className="text-xs text-amber-800 block font-bold">△ 需注意 (有警示)</span>
                  <span className="text-2xl font-extrabold text-amber-700 mt-1 block">
                    {previewData.warning_rows.toLocaleString()} 筆
                  </span>
                </div>

                <div className="p-4 bg-rose-50 rounded-2xl border border-rose-200">
                  <span className="text-xs text-rose-800 block font-bold">✕ 無法匯入</span>
                  <span className="text-2xl font-extrabold text-rose-700 mt-1 block">
                    {previewData.invalid_rows_count.toLocaleString()} 筆
                  </span>
                </div>
              </div>

              {/* 細項拆解 */}
              <div className="p-4 bg-white rounded-xl border border-slate-200 flex flex-wrap gap-4 text-xs">
                <span className="text-emerald-700 font-bold">
                  新增：{previewData.breakdown.inserted.toLocaleString()}
                </span>
                <span className="text-sky-700 font-bold">
                  更新：{previewData.breakdown.updated.toLocaleString()}
                </span>
                <span className="text-slate-500">
                  資料未變：{previewData.breakdown.unchanged.toLocaleString()}
                </span>
                <span className="text-amber-700 font-bold">
                  價格異動：{previewData.breakdown.price_changed.toLocaleString()}
                </span>
                <span className="text-rose-700 font-bold">
                  高風險價格：{previewData.breakdown.high_risk_price.toLocaleString()}
                </span>
              </div>

              {/* 匯入即時進度條 */}
              {importProgress && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl space-y-2">
                  <div className="flex justify-between text-xs font-bold text-teal-900">
                    <span>{importProgress.text}</span>
                    <span>{importProgress.percent}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="bg-teal-600 h-2.5 rounded-full transition-all duration-300"
                      style={{ width: `${importProgress.percent}%` }}
                    />
                  </div>
                </div>
              )}

              {/* 按鈕組 (Section 五) */}
              <div className="flex flex-wrap justify-between items-center gap-3 pt-2">
                <div>
                  {previewData.invalid_rows_count > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowInvalidModal(true)}
                      className="text-rose-600 hover:bg-rose-50 text-xs font-bold"
                    >
                      👁 查看無法匯入資料 ({previewData.invalid_rows_count} 筆)
                    </Button>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="ghost"
                    onClick={() => setPreviewData(null)}
                    disabled={committing}
                    className="text-xs"
                  >
                    取消
                  </Button>
                  <Button
                    onClick={handleCommitTxt}
                    loading={committing}
                    disabled={committing}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-6 py-2.5 shadow-md"
                  >
                    ✓ 確認匯入
                  </Button>
                </div>
              </div>
            </Card>
          ) : null}
        </div>
      )}

      {/* TAB 3: 資料待確認中心 6 大分類 */}
      {activeTab === 'quality' && (
        <div className="space-y-6">
          {/* 6 個清晰分類頁籤 */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {[
              {
                id: 'drug_ingredients',
                label: '💊 藥品成分',
                count: drugKpi?.needsReviewCount || 0,
              },
              {
                id: 'price_changes',
                label: '📈 價格異動',
                count: qualitySummary.price_changes || 0,
              },
              { id: 'duplicates', label: '🔀 疑似重複', count: qualitySummary.duplicates || 0 },
              {
                id: 'category_issues',
                label: '📁 商品分類',
                count: qualitySummary.category_issues || 0,
              },
              {
                id: 'barcode_issues',
                label: '🏷 條碼問題',
                count: qualitySummary.barcode_issues || 0,
              },
              { id: 'other_issues', label: '⚠️ 其他異常', count: qualitySummary.other_issues || 0 },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setQualityActiveTab(tab.id);
                  if (tab.id === 'drug_ingredients') {
                    void loadDrugReviews();
                  } else {
                    void loadQualityCenter(tab.id);
                  }
                }}
                className={`p-3 rounded-xl border text-center transition ${
                  qualityActiveTab === tab.id
                    ? 'bg-teal-700 text-white font-bold border-teal-700 shadow'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className="text-xs block">{tab.label}</span>
                <span
                  className={`text-lg font-extrabold mt-1 block ${
                    qualityActiveTab === tab.id ? 'text-teal-200' : 'text-teal-800'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {qualityActiveTab === 'drug_ingredients' ? (
            <Card className="p-6">
              <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                <div>
                  <h3 className="text-base font-bold text-ink">💊 藥品成分待確認審查清單</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    以下為系統由 TFDA 資料庫自動匹配、信心度為 80~94%
                    或存在多筆候選之西藥商品，請採購核對後一鍵確認或排除。
                  </p>
                </div>
                {selectedReviewProductIds.length > 0 && (
                  <Button
                    size="sm"
                    loading={reviewBatchLoading}
                    onClick={() => void handleBatchConfirmDrugReviews()}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                  >
                    ✓ 批次確認勾選項目 ({selectedReviewProductIds.length})
                  </Button>
                )}
              </div>

              {drugReviews.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  目前無待審查的藥品成分，所有西藥候選皆已確認或已自動帶入。
                </div>
              ) : (
                <Table>
                  <thead>
                    <tr className="bg-slate-50 text-slate-600 text-xs">
                      <th className="p-3 w-10">
                        <input
                          type="checkbox"
                          checked={
                            selectedReviewProductIds.length > 0 &&
                            selectedReviewProductIds.length === drugReviews.length
                          }
                          onChange={() => {
                            if (selectedReviewProductIds.length === drugReviews.length) {
                              setSelectedReviewProductIds([]);
                            } else {
                              setSelectedReviewProductIds(drugReviews.map((r) => r.productId));
                            }
                          }}
                        />
                      </th>
                      <th className="p-3">商品代號 / 品名</th>
                      <th className="p-3">官方比對藥品 / 許可證</th>
                      <th className="p-3">製造商 / 劑型</th>
                      <th className="p-3">匹配分數</th>
                      <th className="p-3">主要成分摘要</th>
                      <th className="p-3 text-right">採購操作</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {drugReviews.map((rev) => (
                      <tr key={rev.productId}>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedReviewProductIds.includes(rev.productId)}
                            onChange={() => {
                              setSelectedReviewProductIds((prev) =>
                                prev.includes(rev.productId)
                                  ? prev.filter((id) => id !== rev.productId)
                                  : [...prev, rev.productId],
                              );
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <div className="font-mono font-bold text-teal-800">{rev.productCode}</div>
                          <div className="font-bold text-ink">{rev.productName}</div>
                          {rev.specification && (
                            <div className="text-[11px] text-slate-500">
                              規格: {rev.specification}
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="font-bold text-emerald-900">
                            {rev.officialProductName}
                          </div>
                          <div className="font-mono text-emerald-700 font-bold text-[11px]">
                            {rev.officialLicenseNumber}
                          </div>
                          {rev.officialEnglishName && (
                            <div className="text-[10px] text-slate-400">
                              {rev.officialEnglishName}
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          <div>{rev.officialManufacturer || '—'}</div>
                          {rev.officialDosageForm && (
                            <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
                              {rev.officialDosageForm}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                              rev.matchScore >= 90
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {rev.matchScore} 分
                          </span>
                          <div
                            className="text-[10px] text-slate-400 mt-1"
                            title={rev.matchReasons.join('；')}
                          >
                            {rev.matchReasons[0]}
                          </div>
                        </td>
                        <td className="p-3 max-w-xs">
                          <div
                            className="text-[11px] text-slate-700 line-clamp-2"
                            title={rev.keyIngredients}
                          >
                            {rev.keyIngredients ||
                              rev.ingredients
                                .map((i) => `${i.nameRaw} ${i.amount ?? ''}${i.unit}`)
                                .join('；') ||
                              '—'}
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1.5">
                            <Button
                              size="sm"
                              onClick={() =>
                                void handleConfirmDrugReview(
                                  rev.productId,
                                  rev.officialLicenseNumber,
                                )
                              }
                              className="text-xs bg-emerald-700 hover:bg-emerald-800 font-bold text-white px-2.5 py-1"
                            >
                              ✓ 確認為此藥品
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                void handleRejectDrugReview(
                                  rev.productId,
                                  rev.officialLicenseNumber,
                                )
                              }
                              className="text-xs text-rose-700 hover:bg-rose-50 border-rose-200 px-2 py-1"
                            >
                              ✕ 不是此商品
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card>
          ) : (
            <>
              {qualityItems.length > 0 && (
                <div className="flex flex-wrap justify-between items-center bg-slate-50 p-3 rounded-xl border border-slate-200 text-xs">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-700">
                      <input
                        type="checkbox"
                        checked={
                          selectedIds.length > 0 && selectedIds.length === qualityItems.length
                        }
                        onChange={handleToggleSelectAll}
                      />
                      全選本頁 ({selectedIds.length} / {qualityItems.length})
                    </label>
                  </div>

                  <div className="flex gap-2">
                    {qualityActiveTab === 'price_changes' && (
                      <Button
                        size="sm"
                        disabled={selectedIds.length === 0}
                        loading={batchActionLoading}
                        onClick={() => handleBatchResolve('ACCEPT_NEW_PRICE')}
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                      >
                        ✓ 批次接受新價格 ({selectedIds.length})
                      </Button>
                    )}
                    {qualityActiveTab === 'duplicates' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={selectedIds.length === 0}
                        loading={batchActionLoading}
                        onClick={() => handleBatchResolve('CONFIRM_DIFFERENT')}
                        className="text-xs"
                      >
                        ✓ 批次確認為不同商品 ({selectedIds.length})
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={selectedIds.length === 0}
                      loading={batchActionLoading}
                      onClick={() => handleBatchResolve('DISMISS')}
                      className="text-xs text-slate-500"
                    >
                      標示稍後處理
                    </Button>
                  </div>
                </div>
              )}

              {qualityLoading ? (
                <Loading label="載入待確認清單中…" />
              ) : qualityItems.length === 0 ? (
                <Card className="p-8 text-center bg-slate-50">
                  <EmptyState
                    title="目前此分類無待確認項目"
                    description="所有相關品項皆已確認完畢。"
                  />
                </Card>
              ) : qualityActiveTab === 'price_changes' ? (
                /* 1. 價格異動 */
                <Card className="p-6">
                  <h3 className="text-base font-bold text-ink mb-4">📈 價格異動審核</h3>
                  <Table>
                    <thead>
                      <tr className="bg-slate-50 text-slate-600 text-xs">
                        <th className="p-3 w-10">選取</th>
                        <th className="p-3">商品代號</th>
                        <th className="p-3">商品名稱</th>
                        <th className="p-3">目前進價</th>
                        <th className="p-3">VIP售價</th>
                        <th className="p-3">異動狀態</th>
                        <th className="p-3 text-right">採購操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {qualityItems.map((p) => (
                        <tr key={p.id}>
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(p.id)}
                              onChange={() => handleToggleSelect(p.id)}
                            />
                          </td>
                          <td className="p-3 font-mono font-bold text-teal-800">
                            {p.product_code}
                          </td>
                          <td className="p-3 font-bold text-ink">{p.product_name}</td>
                          <td className="p-3 font-bold">
                            <Money value={p.current_purchase_price} />
                          </td>
                          <td className="p-3 text-teal-700 font-bold">
                            <Money value={p.vip_price} />
                          </td>
                          <td className="p-3">
                            <span
                              className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                                p.price_comparison_status === 'ZERO_PRICE_REVIEW'
                                  ? 'bg-amber-100 text-amber-800'
                                  : p.price_comparison_status === 'PRICE_ABNORMAL'
                                    ? 'bg-rose-100 text-rose-800'
                                    : 'bg-sky-100 text-sky-800'
                              }`}
                            >
                              {p.price_comparison_status === 'ZERO_PRICE_REVIEW'
                                ? '新來源進價為0(已保護原價)'
                                : p.price_comparison_status === 'PRICE_ABNORMAL'
                                  ? '價格異常(進價高於售價)'
                                  : '價格異動'}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-1.5">
                              <Button
                                size="sm"
                                onClick={() =>
                                  handleResolvePriceReview(
                                    p.id,
                                    'ACCEPT_NEW_PRICE',
                                    p.current_purchase_price,
                                  )
                                }
                                className="text-xs bg-emerald-600 hover:bg-emerald-700"
                              >
                                ✓ 接受價格
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleResolvePriceReview(p.id, 'RETAIN_OLD_PRICE')}
                                className="text-xs"
                              >
                                保留原價
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              ) : qualityActiveTab === 'duplicates' ? (
                /* 2. 疑似重複 */
                <Card className="p-6">
                  <h3 className="text-base font-bold text-ink mb-4">🔀 疑似重複商品審核</h3>
                  <Table>
                    <thead>
                      <tr className="bg-slate-50 text-slate-600 text-xs">
                        <th className="p-3 w-10">選取</th>
                        <th className="p-3">相似度</th>
                        <th className="p-3">商品 A</th>
                        <th className="p-3">商品 B</th>
                        <th className="p-3">判定依據</th>
                        <th className="p-3 text-right">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {qualityItems.map((c) => (
                        <tr key={c.id}>
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(c.id)}
                              onChange={() => handleToggleSelect(c.id)}
                            />
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-1 bg-rose-100 text-rose-800 font-extrabold rounded text-xs">
                              {c.similarity_score}%
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="font-mono font-bold text-teal-800">{c.pa_code}</div>
                            <div className="font-bold text-ink">{c.pa_name}</div>
                            <div className="text-[11px] text-slate-500">
                              進價: {c.pa_in_price} ｜ 單位: {c.pa_unit}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="font-mono font-bold text-purple-800">{c.pb_code}</div>
                            <div className="font-bold text-ink">{c.pb_name}</div>
                            <div className="text-[11px] text-slate-500">
                              進價: {c.pb_in_price} ｜ 單位: {c.pb_unit}
                            </div>
                          </td>
                          <td className="p-3 text-slate-500">{c.match_reasons_json}</td>
                          <td className="p-3 text-right">
                            <div className="flex justify-end gap-1.5">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleResolveDuplicate(c.id, 'SET_SAME_FAMILY')}
                                className="text-xs bg-teal-50 text-teal-800"
                              >
                                👨‍👩‍👧 設為家族
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleResolveDuplicate(c.id, 'CONFIRM_DIFFERENT')}
                                className="text-xs"
                              >
                                ✓ 不同商品
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              ) : (
                /* 3. 分類 / 條碼 / 其他異常清單 */
                <Card className="p-6">
                  <h3 className="text-base font-bold text-ink mb-4">待確認項目清單</h3>
                  <Table>
                    <thead>
                      <tr className="bg-slate-50 text-slate-600 text-xs">
                        <th className="p-3 w-10">選取</th>
                        <th className="p-3">商品代號</th>
                        <th className="p-3">商品名稱</th>
                        <th className="p-3">大分類 / 子分類</th>
                        <th className="p-3">目前進價</th>
                        <th className="p-3">問題註記</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {qualityItems.map((p) => (
                        <tr key={p.id}>
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(p.id)}
                              onChange={() => handleToggleSelect(p.id)}
                            />
                          </td>
                          <td className="p-3 font-mono font-bold text-teal-800">
                            {p.product_code}
                          </td>
                          <td className="p-3 font-bold text-ink">{p.product_name}</td>
                          <td className="p-3">
                            {p.product_category_code
                              ? `${p.product_category_code} ${HUI_SHENG_CATEGORY_MAP[p.product_category_code] || ''}`
                              : '未設定'}
                          </td>
                          <td className="p-3 font-bold">
                            <Money value={p.current_purchase_price} />
                          </td>
                          <td className="p-3 text-amber-700">
                            {p.data_quality_issues_json || '待確認'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* TAB 4: 匯入紀錄與 Rollback */}
      {activeTab === 'history' && (
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-base font-bold text-ink">歷史 TXT 匯入批次紀錄與 Rollback</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                管理員可於此檢視每次匯入之檔案
                Hash、進價稅別與筆數統計，並支援一鍵回復特定批次（Rollback）。
              </p>
            </div>
          </div>
          {batchesLoading ? (
            <Loading label="載入匯入紀錄中…" />
          ) : batches.length === 0 ? (
            <EmptyState title="尚無匯入紀錄" description="匯入 TXT 檔案後，歷史批次將記錄於此。" />
          ) : (
            <Table>
              <thead>
                <tr className="bg-slate-50 text-slate-600 text-xs">
                  <th className="p-3">批次編號</th>
                  <th className="p-3">檔案名稱</th>
                  <th className="p-3">進價稅別</th>
                  <th className="p-3">總筆數</th>
                  <th className="p-3">新增 / 更新 / 無變更</th>
                  <th className="p-3">狀態</th>
                  <th className="p-3">匯入時間</th>
                  <th className="p-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {batches.map((b) => (
                  <tr key={b.id}>
                    <td className="p-3 font-mono font-bold text-teal-800">{b.batch_no}</td>
                    <td className="p-3 font-medium text-ink">{b.file_name}</td>
                    <td className="p-3">
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[11px]">
                        {b.purchase_price_tax_type === 'TAX_EXCLUSIVE' ? '未稅' : '含稅'}
                      </span>
                    </td>
                    <td className="p-3 font-bold">{b.total_rows}</td>
                    <td className="p-3">
                      <span className="text-emerald-700 font-bold">+{b.inserted_rows}</span> /{' '}
                      <span className="text-sky-700 font-bold">~{b.updated_rows}</span> /{' '}
                      <span className="text-slate-400">{b.skipped_rows}</span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          b.status === 'ROLLED_BACK'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {b.status === 'ROLLED_BACK' ? '已回復 (Rolled Back)' : '正常完成'}
                      </span>
                    </td>
                    <td className="p-3 text-slate-400 text-[11px]">{b.created_at}</td>
                    <td className="p-3 text-right">
                      {b.status !== 'ROLLED_BACK' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRollbackBatch(b.id, b.batch_no)}
                          className="text-xs text-rose-600 hover:bg-rose-50"
                        >
                          ↩ 回復本次匯入
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {/* 編輯商品主檔基本資料與屬性 Modal */}
      {editingItem && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-ink flex items-center gap-2">
                  ✏️ 編輯商品主檔資料
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  可直接修正商品名稱、代號、國際條碼、規格與採購人工補充屬性。
                </p>
              </div>
              <button
                onClick={() => setEditingItem(null)}
                className="text-slate-400 hover:text-slate-600 text-lg p-1"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* 基本識別區塊 */}
              <div className="bg-slate-50/80 p-3.5 rounded-xl border border-slate-200/80 space-y-3">
                <div className="text-[11px] font-extrabold text-teal-800 uppercase tracking-wider">
                  🏷️ 商品核心識別資訊
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    商品中文名稱 <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 font-bold text-ink"
                    placeholder="請輸入商品中文品名"
                    value={editProductName}
                    onChange={(e) => setEditProductName(e.target.value)}
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    ＊修改名稱後，系統將自動同步更新去重與搜尋的正規化品名。
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">
                      商品代號 (Item ID) <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full p-2.5 bg-white font-mono font-bold text-teal-900 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                      placeholder="例：001001"
                      value={editProductCode}
                      onChange={(e) => setEditProductCode(e.target.value)}
                    />
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      ＊商品唯一代號，不可與其他商品主檔重複。
                    </p>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">
                      國際條碼 (GTIN / Barcode)
                    </label>
                    <input
                      type="text"
                      className="w-full p-2.5 bg-white font-mono border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                      placeholder="例：4710001001018"
                      value={editBarcode}
                      onChange={(e) => setEditBarcode(e.target.value)}
                    />
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      ＊支援 8/12/13/14 碼標準 GTIN 國際條碼。
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">商品英文品名</label>
                  <input
                    type="text"
                    className="w-full p-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                    placeholder="例：Hui-Sheng Vitamin C 20s"
                    value={editEnglishName}
                    onChange={(e) => setEditEnglishName(e.target.value)}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">規格包裝</label>
                    <input
                      type="text"
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                      placeholder="例：20錠/盒"
                      value={editSpecification}
                      onChange={(e) => setEditSpecification(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">銷售單位</label>
                    <input
                      type="text"
                      className="w-full p-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                      placeholder="例：盒 / 瓶 / 條"
                      value={editSalesUnit}
                      onChange={(e) => setEditSalesUnit(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* 人工維護與補充區塊 */}
              <div className="bg-white p-3.5 rounded-xl border border-slate-200 space-y-3">
                <div className="text-[11px] font-extrabold text-slate-600 uppercase tracking-wider">
                  ⚙️ 採購維護與搭贈條件
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    目前廠商搭贈條件（如：買12送1、買10送2）
                  </label>
                  <input
                    type="text"
                    className="w-full p-2.5 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                    placeholder="例：買12送1"
                    value={giveawayTerms}
                    onChange={(e) => setGiveawayTerms(e.target.value)}
                  />
                  <p className="text-[11px] text-slate-400 mt-1">
                    ＊此欄位為採購專屬維護，後續 TXT 重新匯入絕不會被覆蓋清空。
                  </p>
                </div>

                <Input
                  label="商品官方／公開來源網址"
                  type="url"
                  value={officialProductUrl}
                  placeholder="https://品牌官網/商品頁"
                  onChange={(event) => setOfficialProductUrl(event.target.value)}
                  hint="由採購確認並保存；成分查找時會優先讀取此公開頁面。"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Select
                    label="商品大分類"
                    value={editCategoryCode}
                    onChange={(event) => {
                      const nextCategory = event.target.value;
                      setEditCategoryCode(nextCategory);
                      if (
                        editSubcategoryCode &&
                        subcategories.find((item) => item.code === editSubcategoryCode)
                          ?.category_code !== nextCategory
                      ) {
                        setEditSubcategoryCode('');
                      }
                    }}
                  >
                    <option value="">未設定</option>
                    {categories
                      .filter((category) => Number(category.is_active) === 1)
                      .map((category) => (
                        <option key={category.id} value={category.code}>
                          {category.code}｜{category.name}
                        </option>
                      ))}
                  </Select>
                  <Select
                    label="商品子分類（mcs_no）"
                    value={editSubcategoryCode}
                    onChange={(event) => setEditSubcategoryCode(event.target.value)}
                  >
                    <option value="">請選擇子分類</option>
                    {subcategories
                      .filter(
                        (subcategory) =>
                          Number(subcategory.is_active) === 1 &&
                          (!editCategoryCode || subcategory.category_code === editCategoryCode),
                      )
                      .map((subcategory) => (
                        <option key={subcategory.id} value={subcategory.code}>
                          {subcategory.code}｜{subcategory.name}
                        </option>
                      ))}
                  </Select>
                </div>
                {editCategoryCode && !editSubcategoryCode && (
                  <p className="text-[11px] text-amber-700 font-bold">
                    已變更大分類時，請重新選擇對應的商品子分類；系統不會自動套用第一項。
                  </p>
                )}

                <Select
                  label="品項類型"
                  value={editRecordType}
                  onChange={(event) => setEditRecordType(event.target.value)}
                >
                  {PRODUCT_RECORD_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}（{option.value}）
                    </option>
                  ))}
                </Select>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">
                    所屬商品家族（同商品不同包裝規格）
                  </label>
                  <input
                    type="search"
                    className="w-full p-2 mb-2 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                    value={familySearch}
                    onChange={(event) => setFamilySearch(event.target.value)}
                    placeholder="搜尋家族代碼或名稱"
                  />
                  <Select value={familyId} onChange={(event) => setFamilyId(event.target.value)}>
                    <option value="">未歸入家族</option>
                    {families
                      .filter(
                        (family) =>
                          (Number(family.active ?? 1) === 1 || family.id === familyId) &&
                          (!familySearch.trim() ||
                            `${family.family_code} ${family.family_name} ${family.brand_name || ''}`
                              .toLowerCase()
                              .includes(familySearch.trim().toLowerCase())),
                      )
                      .map((family) => (
                        <option key={family.id} value={family.id}>
                          {family.family_code}｜{family.family_name}
                          {Number(family.active ?? 1) === 0 ? '（已停用）' : ''}
                        </option>
                      ))}
                  </Select>
                  <p className="text-[10px] text-slate-400 mt-1">
                    商品只會歸屬一個家族；改編家族不會修改商品代碼、條碼或交易資料。
                  </p>
                </div>

                <div className="space-y-2 border-t border-slate-100 pt-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <label className="block font-bold text-slate-700">進貨廠商</label>
                      <p className="text-[10px] text-slate-400">
                        可依正式廠商代碼、名稱或統一編號搜尋；價格與最低進貨量依廠商分開維護。
                      </p>
                    </div>
                    <span className="px-2 py-0.5 rounded bg-teal-50 text-teal-800 font-bold">
                      {editSupplierTerms.length} 家
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                    <Input
                      label="搜尋正式廠商"
                      value={supplierSearch}
                      onChange={(event) => {
                        setSupplierSearch(event.target.value);
                        setSupplierVendorToAdd('');
                      }}
                      placeholder="廠商代碼、名稱或統編"
                    />
                    <Select
                      label="選擇搜尋結果"
                      value={supplierVendorToAdd}
                      onChange={(event) => setSupplierVendorToAdd(event.target.value)}
                    >
                      <option value="">請選擇廠商</option>
                      {vendors
                        .filter(
                          (vendor) =>
                            !editSupplierTerms.some((term) => term.vendor_id === vendor.id) &&
                            (!supplierSearch.trim() ||
                              `${vendor.code} ${vendor.name} ${vendor.tax_id || ''}`
                                .toLowerCase()
                                .includes(supplierSearch.trim().toLowerCase())),
                        )
                        .slice(0, 100)
                        .map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.code}｜{vendor.name}｜{vendor.tax_id || '無統編'}
                          </option>
                        ))}
                    </Select>
                    <Button
                      type="button"
                      size="sm"
                      disabled={!supplierVendorToAdd}
                      onClick={() => {
                        if (!supplierVendorToAdd) return;
                        setEditSupplierTerms((current) => [
                          ...current,
                          {
                            id: '',
                            vendor_id: supplierVendorToAdd,
                            purchase_price: null,
                            minimum_order_quantity: null,
                            giveaway_terms: '',
                            effective_from: null,
                            effective_to: null,
                            is_primary_supplier: current.length === 0 ? 1 : 0,
                          },
                        ]);
                        setSupplierVendorToAdd('');
                        setSupplierSearch('');
                      }}
                      className="bg-teal-700 text-white font-bold"
                    >
                      ＋ 加入
                    </Button>
                  </div>

                  {editSupplierTerms.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50 p-3 text-amber-800">
                      {editingItem.imported_supplier_name || editingItem.legacy_vendor_code ? (
                        <>
                          匯入來源：{editingItem.legacy_vendor_code || '—'}｜
                          {editingItem.imported_supplier_name || '未命名廠商'}
                          。目前無法與正式廠商唯一對應，請人工設定。
                        </>
                      ) : (
                        '尚未設定進貨廠商。'
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {editSupplierTerms.map((term, index) => {
                        const vendor = vendors.find((item) => item.id === term.vendor_id);
                        return (
                          <div
                            key={`${term.id || 'new'}-${term.vendor_id}-${index}`}
                            className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2"
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div className="font-bold text-slate-800">
                                {vendor ? `${vendor.code}｜${vendor.name}` : '找不到正式廠商資料'}
                              </div>
                              <div className="flex items-center gap-3">
                                <label className="inline-flex items-center gap-1 font-bold text-teal-800">
                                  <input
                                    type="radio"
                                    name="primary-supplier"
                                    checked={Boolean(term.is_primary_supplier)}
                                    onChange={() =>
                                      setEditSupplierTerms((current) =>
                                        current.map((item, itemIndex) => ({
                                          ...item,
                                          is_primary_supplier: itemIndex === index ? 1 : 0,
                                        })),
                                      )
                                    }
                                  />
                                  主要進貨廠商
                                </label>
                                <button
                                  type="button"
                                  className="text-rose-700 font-bold hover:underline"
                                  onClick={() => {
                                    setEditSupplierTerms((current) => {
                                      const next = current.filter(
                                        (_, itemIndex) => itemIndex !== index,
                                      );
                                      if (
                                        next.length > 0 &&
                                        !next.some((item) => item.is_primary_supplier)
                                      ) {
                                        next[0] = { ...next[0], is_primary_supplier: 1 };
                                      }
                                      return next;
                                    });
                                  }}
                                >
                                  移除
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <Input
                                label="進價"
                                type="number"
                                min="0"
                                step="0.01"
                                value={term.purchase_price ?? ''}
                                onChange={(event) =>
                                  setEditSupplierTerms((current) =>
                                    current.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            purchase_price:
                                              event.target.value === ''
                                                ? null
                                                : Number(event.target.value),
                                          }
                                        : item,
                                    ),
                                  )
                                }
                                hint="0 與未填寫分開保存"
                              />
                              <Input
                                label="最低進貨數量"
                                type="number"
                                min="0"
                                step="1"
                                value={term.minimum_order_quantity ?? ''}
                                onChange={(event) =>
                                  setEditSupplierTerms((current) =>
                                    current.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? {
                                            ...item,
                                            minimum_order_quantity:
                                              event.target.value === ''
                                                ? null
                                                : Number(event.target.value),
                                          }
                                        : item,
                                    ),
                                  )
                                }
                              />
                              <Input
                                label="搭贈條件"
                                value={term.giveaway_terms || ''}
                                onChange={(event) =>
                                  setEditSupplierTerms((current) =>
                                    current.map((item, itemIndex) =>
                                      itemIndex === index
                                        ? { ...item, giveaway_terms: event.target.value }
                                        : item,
                                    ),
                                  )
                                }
                                placeholder="例：買12送1"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* 🧪 商品處方與主要成分自行填寫維護區塊 */}
                <div className="bg-emerald-50/40 p-4 rounded-xl border border-emerald-200/80 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-100 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] font-extrabold text-emerald-950 uppercase tracking-wider flex items-center gap-1.5">
                        🧪 商品處方與主要成分維護
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold border border-emerald-200">
                        {editableIngredients.length > 0
                          ? `已填寫 ${editableIngredients.length} 項成分`
                          : '尚未填寫成分'}
                      </span>
                    </div>

                    {/* 切換填寫模式 */}
                    <div className="flex bg-white rounded-lg p-0.5 border border-emerald-200 text-[11px]">
                      <button
                        type="button"
                        onClick={() => setIngredientInputMode('FORM')}
                        className={`px-2.5 py-1 rounded-md font-bold transition ${
                          ingredientInputMode === 'FORM'
                            ? 'bg-emerald-700 text-white shadow-xs'
                            : 'text-slate-600 hover:text-emerald-800'
                        }`}
                      >
                        📋 逐項填寫清單
                      </button>
                      <button
                        type="button"
                        onClick={() => setIngredientInputMode('TEXT')}
                        className={`px-2.5 py-1 rounded-md font-bold transition ${
                          ingredientInputMode === 'TEXT'
                            ? 'bg-emerald-700 text-white shadow-xs'
                            : 'text-slate-600 hover:text-emerald-800'
                        }`}
                      >
                        ⚡ 純文字智慧貼上
                      </button>
                      <button
                        type="button"
                        onClick={() => setIngredientInputMode('JSON')}
                        className={`px-2 py-1 rounded-md font-bold transition ${
                          ingredientInputMode === 'JSON'
                            ? 'bg-emerald-700 text-white shadow-xs'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        進階 JSON
                      </button>
                    </div>
                  </div>

                  {/* 模式 1: 結構化表單逐項填寫 */}
                  {ingredientInputMode === 'FORM' && (
                    <div className="space-y-2.5">
                      {editableIngredients.length === 0 ? (
                        <div className="p-4 bg-white/80 rounded-xl border border-dashed border-emerald-200 text-center space-y-2">
                          <div className="text-slate-400 text-xs">目前尚未填寫任何成分資料。</div>
                          <div className="flex flex-wrap justify-center gap-2">
                            <button
                              type="button"
                              onClick={() => {
                                setEditableIngredients([
                                  ...editableIngredients,
                                  { id: `ing-${Date.now()}`, name: '', amount: '', unit: 'mg' },
                                ]);
                              }}
                              className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-bold text-xs shadow-xs"
                            >
                              ➕ 新增第一筆成分
                            </button>
                            <button
                              type="button"
                              onClick={() => setIngredientInputMode('TEXT')}
                              className="px-3 py-1.5 bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-lg font-bold text-xs"
                            >
                              ⚡ 貼上整段成分文字
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="grid grid-cols-12 gap-2 text-[11px] font-bold text-slate-600 px-1">
                            <div className="col-span-6">成分名稱（中英文品名／通用名）</div>
                            <div className="col-span-3">含量 / 劑量</div>
                            <div className="col-span-2">單位</div>
                            <div className="col-span-1 text-center">操作</div>
                          </div>

                          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                            {editableIngredients.map((ing, idx) => (
                              <div
                                key={ing.id || idx}
                                className="grid grid-cols-12 gap-2 items-center bg-white p-2 rounded-xl border border-emerald-100 shadow-2xs"
                              >
                                <div className="col-span-6">
                                  <input
                                    type="text"
                                    className="w-full p-1.5 bg-slate-50/50 border border-slate-200 rounded-lg text-xs font-bold text-ink focus:bg-white focus:outline-none focus:border-teal-500"
                                    placeholder="例：Acetaminophen 乙醯胺酚"
                                    value={ing.name}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setEditableIngredients(
                                        editableIngredients.map((item, i) =>
                                          i === idx ? { ...item, name: val } : item,
                                        ),
                                      );
                                    }}
                                  />
                                </div>
                                <div className="col-span-3">
                                  <input
                                    type="text"
                                    className="w-full p-1.5 bg-slate-50/50 border border-slate-200 rounded-lg text-xs font-mono font-bold text-emerald-900 focus:bg-white focus:outline-none focus:border-teal-500"
                                    placeholder="例：500"
                                    value={ing.amount}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setEditableIngredients(
                                        editableIngredients.map((item, i) =>
                                          i === idx ? { ...item, amount: val } : item,
                                        ),
                                      );
                                    }}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <input
                                    type="text"
                                    list={`unit-list-${idx}`}
                                    className="w-full p-1.5 bg-slate-50/50 border border-slate-200 rounded-lg text-xs font-mono focus:bg-white focus:outline-none focus:border-teal-500"
                                    placeholder="mg"
                                    value={ing.unit}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setEditableIngredients(
                                        editableIngredients.map((item, i) =>
                                          i === idx ? { ...item, unit: val } : item,
                                        ),
                                      );
                                    }}
                                  />
                                  <datalist id={`unit-list-${idx}`}>
                                    {COMMON_UNITS.map((u) => (
                                      <option key={u} value={u} />
                                    ))}
                                  </datalist>
                                </div>
                                <div className="col-span-1 text-center">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setEditableIngredients(
                                        editableIngredients.filter((_, i) => i !== idx),
                                      );
                                    }}
                                    className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg transition"
                                    title="刪除此成分"
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="flex justify-between items-center pt-1">
                            <button
                              type="button"
                              onClick={() => {
                                setEditableIngredients([
                                  ...editableIngredients,
                                  { id: `ing-${Date.now()}`, name: '', amount: '', unit: 'mg' },
                                ]);
                              }}
                              className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-bold text-xs inline-flex items-center gap-1 shadow-xs"
                            >
                              ➕ 新增成分項目
                            </button>
                            <span className="text-[11px] text-slate-500">
                              共 {editableIngredients.length} 項
                            </span>
                          </div>
                        </div>
                      )}

                      {/* 常用成分快捷範本 */}
                      <div className="pt-2 border-t border-emerald-100">
                        <div className="text-[10px] font-bold text-slate-500 mb-1">
                          常用成分快速加入：
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {COMMON_INGREDIENT_TEMPLATES.map((tmpl, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => {
                                setEditableIngredients([
                                  ...editableIngredients,
                                  {
                                    id: `ing-${Date.now()}-${idx}`,
                                    name: tmpl.name,
                                    amount: tmpl.amount,
                                    unit: tmpl.unit,
                                  },
                                ]);
                              }}
                              className="px-2 py-0.5 rounded-full bg-white hover:bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-medium transition"
                            >
                              {tmpl.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 模式 2: 純文字智慧貼上 */}
                  {ingredientInputMode === 'TEXT' && (
                    <div className="space-y-2">
                      <p className="text-[11px] text-slate-600">
                        直接貼上商品說明書、外包裝或官方頁面的成分文字，點擊「⚡ 智慧解析為清單」：
                      </p>
                      <textarea
                        className="w-full h-24 p-2.5 bg-white font-sans text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                        placeholder="例：Acetaminophen 500mg, Caffeine 30mg, Phenylephrine 10mg&#10;或：黃耆 5g, 當歸 3g, 枸杞 2g&#10;或：維生素B1 50mg / 維生素B6 50mg / 維生素B12 500mcg"
                        value={ingredientQuickText}
                        onChange={(e) => setIngredientQuickText(e.target.value)}
                      />
                      <div className="flex justify-between items-center">
                        <button
                          type="button"
                          onClick={() => {
                            if (!ingredientQuickText.trim()) return;
                            const parsed = parseIngredientsFromRawText(ingredientQuickText);
                            if (parsed.length === 0) {
                              notify('未偵測到有效成分文字', 'error');
                              return;
                            }
                            const newItems = parsed.map((p, idx) => ({
                              id: `ing-${Date.now()}-${idx}`,
                              name: p.name,
                              amount: p.amount,
                              unit: p.unit,
                            }));
                            setEditableIngredients([...editableIngredients, ...newItems]);
                            setIngredientInputMode('FORM');
                            notify(`成功解析並新增 ${newItems.length} 項成分！`);
                          }}
                          disabled={!ingredientQuickText.trim()}
                          className="px-3.5 py-1.5 bg-teal-800 hover:bg-teal-900 disabled:opacity-50 text-white rounded-lg font-bold text-xs shadow-xs inline-flex items-center gap-1.5"
                        >
                          ⚡ 智慧解析為清單並加入
                        </button>
                        <button
                          type="button"
                          onClick={() => setIngredientInputMode('FORM')}
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          返回清單
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 模式 3: 進階 JSON 編輯 */}
                  {ingredientInputMode === 'JSON' && (
                    <div className="space-y-2">
                      <textarea
                        className="w-full h-24 p-2.5 font-mono text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                        placeholder='[{"nameRaw": "維生素C", "amount": 1000, "unit": "mg"}]'
                        value={ingredientsJson}
                        onChange={(e) => {
                          setIngredientsJson(e.target.value);
                          try {
                            const parsed = JSON.parse(e.target.value);
                            if (Array.isArray(parsed)) {
                              setEditableIngredients(
                                parsed.map((item: any, idx: number) => ({
                                  id: `ing-${idx}-${Date.now()}`,
                                  name:
                                    item.ingredientName || item.name || item.nameNormalized || '',
                                  amount:
                                    item.amount !== null && item.amount !== undefined
                                      ? String(item.amount)
                                      : '',
                                  unit: item.unit || '',
                                })),
                              );
                            }
                          } catch {
                            // ignore parse error
                          }
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setEditingItem(null)}>
                取消
              </Button>
              <Button
                onClick={saveCustomFields}
                loading={savingCustom}
                className="bg-teal-700 hover:bg-teal-800 text-white font-bold"
              >
                💾 儲存修改
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 商品家族建立、維護與成員檢視 */}
      {showFamilyManager && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-lg font-bold text-ink">🧩 商品家族管理</h3>
                <p className="text-xs text-slate-500 mt-1">
                  建立與維護家族名稱；商品加入、移出或改編使用穩定 ID
                  關聯，不會變更商品代碼、條碼或交易資料。
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowFamilyManager(false);
                  resetFamilyForm();
                }}
                className="text-slate-400 hover:text-slate-700 text-lg"
              >
                ✕
              </button>
            </div>

            <Card className="p-4 bg-slate-50 border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="家族代碼"
                  value={familyFormCode}
                  disabled={Boolean(familyFormId)}
                  onChange={(event) => setFamilyFormCode(event.target.value)}
                  placeholder="留空時依既有系統規則產生"
                  hint={familyFormId ? '家族建立後不直接修改代碼。' : '不另創新編碼規則。'}
                />
                <Input
                  label="家族名稱 *"
                  value={familyFormName}
                  onChange={(event) => setFamilyFormName(event.target.value)}
                  placeholder="例：樂敦勁極酷眼藥水系列"
                />
                <Input
                  label="品牌"
                  value={familyFormBrand}
                  onChange={(event) => setFamilyFormBrand(event.target.value)}
                />
                <Input
                  label="說明"
                  value={familyFormDescription}
                  onChange={(event) => setFamilyFormDescription(event.target.value)}
                />
              </div>
              {familyFormId && (
                <label className="inline-flex items-center gap-2 mt-3 text-xs font-bold text-slate-700">
                  <input
                    type="checkbox"
                    checked={familyFormActive}
                    onChange={(event) => setFamilyFormActive(event.target.checked)}
                  />
                  啟用此商品家族
                </label>
              )}
              <div className="flex justify-end gap-2 mt-4">
                {familyFormId && (
                  <Button variant="ghost" onClick={resetFamilyForm}>
                    取消編輯
                  </Button>
                )}
                <Button
                  onClick={() => void saveFamily()}
                  loading={savingFamily}
                  className="bg-teal-700 text-white font-bold"
                >
                  {familyFormId ? '儲存家族修改' : '建立商品家族'}
                </Button>
              </div>
            </Card>

            <div className="space-y-3">
              {families.length === 0 ? (
                <EmptyState title="尚未建立商品家族" description="可使用上方表單建立第一個家族。" />
              ) : (
                families.map((family) => (
                  <Card key={family.id} className="p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-xs font-bold text-teal-800">
                            {family.family_code}
                          </span>
                          <span className="font-bold text-ink">{family.family_name}</span>
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              Number(family.active ?? 1) === 1
                                ? 'bg-emerald-50 text-emerald-800'
                                : 'bg-slate-100 text-slate-500'
                            }`}
                          >
                            {Number(family.active ?? 1) === 1 ? '啟用' : '已停用'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {(family.members || []).length} 項商品
                          </span>
                        </div>
                        {(family.brand_name || family.description) && (
                          <p className="text-xs text-slate-500 mt-1 whitespace-normal break-words">
                            {[family.brand_name, family.description].filter(Boolean).join('｜')}
                          </p>
                        )}
                      </div>
                      <Button size="sm" variant="secondary" onClick={() => editFamily(family)}>
                        編輯家族
                      </Button>
                    </div>

                    {(family.members || []).length > 0 ? (
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-left text-slate-500 border-b border-slate-100">
                              <th className="py-2 pr-2">商品代碼</th>
                              <th className="py-2 pr-2">商品名稱</th>
                              <th className="py-2 pr-2">規格</th>
                              <th className="py-2 text-right">操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(family.members || []).map((member) => (
                              <tr key={member.product_id} className="border-b border-slate-50">
                                <td className="py-2 pr-2 font-mono text-teal-800">
                                  {member.product_code}
                                </td>
                                <td className="py-2 pr-2 font-bold text-slate-800">
                                  {member.product_name}
                                </td>
                                <td className="py-2 pr-2 text-slate-500">
                                  {member.package_spec || member.specification || '—'}
                                </td>
                                <td className="py-2 text-right">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      void removeFamilyMember(family.id, member.product_id)
                                    }
                                    className="text-rose-700"
                                  >
                                    移出家族
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-500">
                        尚無成員。請在商品編輯頁搜尋並選擇此家族，即可加入。
                      </div>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* 查看無法匯入資料 Modal (Section 二十五) */}
      {showInvalidModal && previewData && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-rose-900">
                  無法匯入商品清單（共 {previewData.invalid_rows_count} 筆）
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  以下列因缺少必要之商品代號或商品名稱，無法建立識別主檔。
                </p>
              </div>
              <button
                onClick={() => setShowInvalidModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="overflow-x-auto max-h-80 border border-slate-200 rounded-xl">
              <Table>
                <thead>
                  <tr className="bg-slate-50 text-slate-600 text-xs">
                    <th className="p-3">來源資料列</th>
                    <th className="p-3">商品代號</th>
                    <th className="p-3">商品名稱</th>
                    <th className="p-3">錯誤原因</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {previewData.invalid_rows.map((r, idx) => (
                    <tr key={idx}>
                      <td className="p-3 font-mono font-bold text-slate-600">
                        第 {r.rowNumber} 列
                      </td>
                      <td className="p-3 font-mono text-rose-700">{r.itemId || '（空白）'}</td>
                      <td className="p-3 text-slate-700">{r.cname || '（空白）'}</td>
                      <td className="p-3 text-rose-700 font-bold">{r.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <Button onClick={() => setShowInvalidModal(false)}>關閉</Button>
            </div>
          </div>
        </div>
      )}

      {/* 一鍵全庫智慧補全 Modal */}
      {showAutoEnrichAllModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-teal-950">⚡ 一鍵全庫智慧補全</h3>
                <p className="text-xs text-slate-500 mt-1">
                  依序套用 TFDA 官方藥品、TFDA 健康食品與通用候選規則，補全正式商品主檔成分。
                </p>
              </div>
              <button
                onClick={() => setShowAutoEnrichAllModal(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="關閉"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              {/* 五大核心功能操作列與狀態指示 */}
              {selectedCandidateProduct && (
                <div className="rounded-xl border border-teal-200 bg-white p-4 shadow-sm space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
                    <div>
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <span>🧪 商品成分與來源維護流程</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200">
                          主檔版本 Revision: V{selectedCandidateProduct.ingredient_revision || 1}
                        </span>
                      </h4>
                      <p className="text-xs text-slate-500 mt-0.5">
                        依品名查詢官方及可信資料、自動帶入待確認欄位、顯示來源出處與並列差異、人工確認儲存。
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() =>
                          void handleSingleEnrich(
                            selectedCandidateProduct.id,
                            supplementalSearchKeyword,
                          )
                        }
                        loading={enrichingSingleId === selectedCandidateProduct.id}
                        className="bg-teal-700 hover:bg-teal-800 text-white text-xs font-bold"
                        title="以目前主檔品名啟動官方資料查詢"
                      >
                        🔍 依品名查詢並帶入
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          const input = document.getElementById('manual-source-url-input');
                          if (input) input.focus();
                        }}
                        className="text-xs font-bold"
                        title="貼上品牌官網、TFDA 或公開商品網址"
                      >
                        🔗 貼上官方商品連結
                      </Button>
                      <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 shadow-sm">
                        📑 補充商品標示／說明書
                        <input
                          type="file"
                          className="hidden"
                          accept="application/pdf,image/jpeg,image/png,image/webp"
                          disabled={uploadingIngredientLabel}
                          onChange={(e) => void handleIngredientLabelUpload(e)}
                        />
                      </label>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() =>
                          void handleSingleEnrich(
                            selectedCandidateProduct.id,
                            supplementalSearchKeyword,
                            manualSourceUrl,
                          )
                        }
                        loading={enrichingSingleId === selectedCandidateProduct.id}
                        className="text-xs font-bold"
                        title="以目前關鍵字或網址重新執行查找"
                      >
                        🔄 重新查詢
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => void handleConfirmStaging()}
                        loading={confirmingSourceId === 'STAGING'}
                        disabled={
                          !candidateDrawerData?.staging &&
                          (!candidateDrawerData?.sources ||
                            candidateDrawerData.sources.length === 0)
                        }
                        className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold shadow-sm"
                        title="一次確認採用所選項目並寫入主檔"
                      >
                        💾 確認並儲存
                      </Button>
                    </div>
                  </div>

                  {/* 待確認採用差異對比區塊 (Diff View: 目前資料 vs 查詢結果) */}
                  {candidateDrawerData?.staging?.diffs &&
                    candidateDrawerData.staging.diffs.length > 0 && (
                      <div className="rounded-xl border border-teal-300 bg-teal-50/50 p-4 space-y-3 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-teal-950">
                                📊 待確認採用差異對比（目前主檔資料 vs 最新查詢結果）
                              </span>
                              <span className="rounded bg-teal-200/80 px-2 py-0.5 text-[11px] font-bold text-teal-900">
                                暫存版本已保留（重新整理後仍可繼續確認）
                              </span>
                            </div>
                            <div className="text-xs text-teal-800 mt-1 flex flex-wrap gap-x-3">
                              <span>
                                來源出處：
                                <strong className="text-ink">
                                  {candidateDrawerData.staging.sourceName}
                                </strong>
                              </span>
                              {candidateDrawerData.staging.sourceUrl && (
                                <a
                                  href={candidateDrawerData.staging.sourceUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-teal-700 font-bold hover:underline"
                                >
                                  開啟官方商品頁核對 ↗
                                </a>
                              )}
                              <span>
                                擷取時間：
                                {candidateDrawerData.staging.fetchedAt
                                  ? new Date(candidateDrawerData.staging.fetchedAt).toLocaleString(
                                      'zh-TW',
                                    )
                                  : '剛剛'}
                              </span>
                            </div>
                            {candidateDrawerData.staging.evidenceSnippet && (
                              <div className="mt-1.5 text-[11px] text-slate-600 bg-white/80 p-2 rounded border border-teal-100">
                                <span className="font-bold text-slate-500">來源佐證摘錄：</span>
                                {candidateDrawerData.staging.evidenceSnippet}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-xs"
                              onClick={() => {
                                if (
                                  selectedDiffIndices.length ===
                                  candidateDrawerData.staging!.diffs.length
                                ) {
                                  setSelectedDiffIndices([]);
                                } else {
                                  setSelectedDiffIndices(
                                    candidateDrawerData.staging!.diffs.map((_, i) => i),
                                  );
                                }
                              }}
                            >
                              {selectedDiffIndices.length ===
                              candidateDrawerData.staging.diffs.length
                                ? '取消全選'
                                : '全選採用'}
                            </Button>
                            <Button
                              size="sm"
                              className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                              onClick={() => void handleConfirmStaging()}
                              loading={confirmingSourceId === 'STAGING'}
                            >
                              ✓ 確認採用所選欄位 ({selectedDiffIndices.length})
                            </Button>
                          </div>
                        </div>

                        <div className="overflow-x-auto rounded-lg border border-teal-200 bg-white shadow-sm">
                          <Table>
                            <thead>
                              <tr className="bg-teal-100/60 text-xs text-teal-900">
                                <th className="p-2 w-10 text-center">採用</th>
                                <th className="p-2">成分名稱</th>
                                <th className="p-2">比對狀態</th>
                                <th className="p-2">目前主檔資料</th>
                                <th className="p-2">查詢結果 (候選)</th>
                                <th className="p-2">角色 / 規格形態</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-teal-100 text-xs">
                              {candidateDrawerData.staging.diffs.map((diff, index) => {
                                const isChecked = selectedDiffIndices.includes(index);
                                const statusBadge =
                                  diff.diffType === 'ADDED' ? (
                                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">
                                      + 新增成分
                                    </span>
                                  ) : diff.diffType === 'MODIFIED' ? (
                                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                                      ✎ 含量異動
                                    </span>
                                  ) : diff.diffType === 'REMOVED' ? (
                                    <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">
                                      - 查詢未列
                                    </span>
                                  ) : (
                                    <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                                      = 內容相符
                                    </span>
                                  );

                                const currentDisplay =
                                  diff.currentAmount !== undefined && diff.currentAmount !== null
                                    ? `${diff.currentAmount} ${diff.currentUnit || ''}`
                                    : diff.currentAmount === null
                                      ? '未揭露'
                                      : '— (主檔空白)';

                                const incomingDisplay =
                                  diff.incomingAmount !== undefined && diff.incomingAmount !== null
                                    ? `${diff.incomingAmount} ${diff.incomingUnit || ''}`
                                    : diff.incomingAmount === null
                                      ? '未揭露'
                                      : '— (查無)';

                                return (
                                  <tr
                                    key={`${diff.nameNormalized}-${index}`}
                                    className={`hover:bg-teal-50/40 ${isChecked ? 'bg-teal-50/20' : ''}`}
                                  >
                                    <td className="p-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setSelectedDiffIndices([...selectedDiffIndices, index]);
                                          } else {
                                            setSelectedDiffIndices(
                                              selectedDiffIndices.filter((i) => i !== index),
                                            );
                                          }
                                        }}
                                        className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                      />
                                    </td>
                                    <td className="p-2 font-bold text-ink">
                                      <div>{diff.ingredientName}</div>
                                      <div className="font-mono text-[10px] text-slate-400">
                                        {diff.nameNormalized}
                                      </div>
                                    </td>
                                    <td className="p-2">{statusBadge}</td>
                                    <td className="p-2 font-mono text-slate-600">
                                      {currentDisplay}
                                    </td>
                                    <td className="p-2 font-mono font-bold text-teal-800">
                                      {incomingDisplay}
                                    </td>
                                    <td className="p-2 text-[11px] text-slate-500">
                                      <div>
                                        {diff.incomingRole || diff.currentRole || '主要成分'}
                                      </div>
                                      {diff.incomingFormOrStrain && (
                                        <div className="text-[10px] text-teal-700">
                                          {diff.incomingFormOrStrain}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    )}
                </div>
              )}
              {autoEnrichStage === 'PREVIEW' && (
                <div className="space-y-4">
                  <div className="p-4 bg-teal-50 border border-teal-200 rounded-xl text-xs text-teal-950 space-y-2">
                    <p className="font-bold">安全寫入原則</p>
                    <ul className="list-disc list-inside space-y-1 text-teal-900">
                      <li>已由人工確認或鎖定的商品成分一律保留，不會被覆蓋。</li>
                      <li>只有官方資料精準命中才會自動確認。</li>
                      <li>品名近似與通用規則只建立候選，統一送至「資料待確認中心」。</li>
                      <li>工作進度會存入 D1；若中途離開頁面，下次可從既有進度接續。</li>
                    </ul>
                  </div>

                  {autoEnrichPreviewLoading ? (
                    <Loading label="正在統計商品與官方參考資料…" />
                  ) : autoEnrichPreview ? (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div className="p-3 border border-slate-200 rounded-xl text-center">
                          <span className="block text-slate-500">正式商品總數</span>
                          <strong className="block mt-1 text-xl text-slate-800">
                            {autoEnrichPreview.totalProducts.toLocaleString()}
                          </strong>
                        </div>
                        <div className="p-3 border border-emerald-200 bg-emerald-50 rounded-xl text-center">
                          <span className="block text-emerald-800">已確認／保留</span>
                          <strong className="block mt-1 text-xl text-emerald-700">
                            {autoEnrichPreview.alreadyConfirmedCount.toLocaleString()}
                          </strong>
                        </div>
                        <div className="p-3 border border-amber-200 bg-amber-50 rounded-xl text-center">
                          <span className="block text-amber-800">本次待處理</span>
                          <strong className="block mt-1 text-xl text-amber-700">
                            {autoEnrichPreview.pendingCount.toLocaleString()}
                          </strong>
                        </div>
                        <div className="p-3 border border-slate-200 bg-slate-50 rounded-xl text-center">
                          <span className="block text-slate-500">人工鎖定</span>
                          <strong className="block mt-1 text-xl text-slate-700">
                            {autoEnrichPreview.lockedCount.toLocaleString()}
                          </strong>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                        <div className="p-3 border border-slate-200 rounded-xl">
                          <span className="font-bold text-slate-700">A～D 類西藥</span>
                          <span className="float-right font-mono">
                            {autoEnrichPreview.eligibleDrugCount.toLocaleString()} 筆
                          </span>
                          <p className="mt-2 text-slate-500">
                            TFDA 許可證參考：{autoEnrichPreview.drugReferenceCount.toLocaleString()}{' '}
                            筆
                          </p>
                        </div>
                        <div className="p-3 border border-slate-200 rounded-xl">
                          <span className="font-bold text-slate-700">M 類健康食品</span>
                          <span className="float-right font-mono">
                            {autoEnrichPreview.eligibleHealthFoodCount.toLocaleString()} 筆
                          </span>
                          <p className="mt-2 text-slate-500">
                            小綠人參考：
                            {autoEnrichPreview.healthFoodReferenceCount.toLocaleString()} 筆
                          </p>
                        </div>
                        <div className="p-3 border border-slate-200 rounded-xl">
                          <span className="font-bold text-slate-700">通用規則候選</span>
                          <span className="float-right font-mono">
                            約 {autoEnrichPreview.estimatedRuleMatches.toLocaleString()} 筆
                          </span>
                          <p className="mt-2 text-slate-500">只建立待確認候選，不會直接確認。</p>
                        </div>
                      </div>

                      {autoEnrichPreview.drugReferenceCount === 0 && (
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
                          TFDA 藥品參考資料尚未初始化；開始執行時系統會先同步官方資料。
                        </div>
                      )}
                    </>
                  ) : (
                    <EmptyState title="尚未取得預覽資料" description="請重新開啟視窗或稍後再試。" />
                  )}
                </div>
              )}

              {autoEnrichStage === 'RUNNING' && (
                <div className="py-8 space-y-5 text-center">
                  <div className="animate-pulse text-4xl">⚡</div>
                  <div>
                    <h4 className="font-bold text-ink">全庫智慧補全執行中</h4>
                    <p className="text-xs text-slate-500 mt-1">
                      系統正分批處理並逐批保存進度，請保持此頁開啟直到完成。
                    </p>
                  </div>
                  {currentAutoEnrichJob ? (
                    <div className="max-w-xl mx-auto space-y-3">
                      <div className="flex justify-between text-xs font-mono text-slate-600">
                        <span>
                          {currentAutoEnrichJob.processed_count.toLocaleString()} /{' '}
                          {currentAutoEnrichJob.total_count.toLocaleString()} 筆
                        </span>
                        <span>
                          {currentAutoEnrichJob.total_count > 0
                            ? Math.min(
                                100,
                                Math.round(
                                  (currentAutoEnrichJob.processed_count /
                                    currentAutoEnrichJob.total_count) *
                                    100,
                                ),
                              )
                            : 100}
                          %
                        </span>
                      </div>
                      <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-teal-700 rounded-full transition-all duration-300"
                          style={{
                            width: `${
                              currentAutoEnrichJob.total_count > 0
                                ? Math.min(
                                    100,
                                    (currentAutoEnrichJob.processed_count /
                                      currentAutoEnrichJob.total_count) *
                                      100,
                                  )
                                : 100
                            }%`,
                          }}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <span className="p-2 bg-emerald-50 text-emerald-800 rounded-lg">
                          自動確認 {currentAutoEnrichJob.auto_confirmed_count.toLocaleString()}
                        </span>
                        <span className="p-2 bg-amber-50 text-amber-800 rounded-lg">
                          待確認 {currentAutoEnrichJob.review_count.toLocaleString()}
                        </span>
                        <span className="p-2 bg-slate-50 text-slate-600 rounded-lg">
                          查無 {currentAutoEnrichJob.not_found_count.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <Loading label="正在同步官方資料並建立工作…" />
                  )}
                </div>
              )}

              {autoEnrichStage === 'COMPLETED' && currentAutoEnrichJob && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                    <div className="text-3xl">✓</div>
                    <h4 className="font-bold text-emerald-900 mt-1">全庫智慧補全已完成</h4>
                    <p className="text-xs text-emerald-800 mt-1">
                      官方精準命中已寫入；近似與規則結果已送往待確認清單。
                    </p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center text-xs">
                    <div className="p-3 border border-slate-200 rounded-xl">
                      <span className="block text-slate-500">已處理</span>
                      <strong className="text-xl text-slate-800">
                        {currentAutoEnrichJob.processed_count.toLocaleString()}
                      </strong>
                    </div>
                    <div className="p-3 border border-emerald-200 rounded-xl">
                      <span className="block text-emerald-800">自動確認</span>
                      <strong className="text-xl text-emerald-700">
                        {currentAutoEnrichJob.auto_confirmed_count.toLocaleString()}
                      </strong>
                    </div>
                    <div className="p-3 border border-amber-200 rounded-xl">
                      <span className="block text-amber-800">待人工確認</span>
                      <strong className="text-xl text-amber-700">
                        {currentAutoEnrichJob.review_count.toLocaleString()}
                      </strong>
                    </div>
                    <div className="p-3 border border-rose-200 rounded-xl">
                      <span className="block text-rose-700">處理錯誤</span>
                      <strong className="text-xl text-rose-700">
                        {currentAutoEnrichJob.error_count.toLocaleString()}
                      </strong>
                    </div>
                  </div>

                  {autoEnrichWarning && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
                      {autoEnrichWarning}
                    </div>
                  )}

                  {autoEnrichJobItems.length > 0 && (
                    <div className="border border-slate-200 rounded-xl overflow-auto max-h-56">
                      <Table>
                        <thead>
                          <tr className="bg-slate-50 text-xs text-slate-600">
                            <th className="p-2">商品</th>
                            <th className="p-2">結果</th>
                            <th className="p-2">比對方式</th>
                            <th className="p-2">成分數</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-xs">
                          {autoEnrichJobItems.map((item) => (
                            <tr key={item.id}>
                              <td className="p-2">
                                <span className="font-bold text-ink">{item.product_name}</span>
                                <span className="block text-[10px] text-slate-400 font-mono">
                                  {item.product_code}
                                </span>
                              </td>
                              <td className="p-2">
                                {item.status === 'AUTO_CONFIRMED'
                                  ? '自動確認'
                                  : item.status === 'NEEDS_REVIEW' || item.status === 'AMBIGUOUS'
                                    ? '待人工確認'
                                    : item.status === 'ERROR'
                                      ? '處理失敗'
                                      : '未找到可靠資料'}
                              </td>
                              <td className="p-2 font-mono text-[10px]">
                                {item.match_method || '—'}
                              </td>
                              <td className="p-2">{item.ingredient_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setShowAutoEnrichAllModal(false)}>
                關閉
              </Button>
              {autoEnrichStage === 'PREVIEW' && (
                <Button
                  onClick={() => void handleStartAutoEnrichAll()}
                  disabled={autoEnrichPreviewLoading || autoEnrichRunning || !autoEnrichPreview}
                  loading={autoEnrichRunning}
                  className="bg-teal-800 hover:bg-teal-900 text-white font-bold"
                >
                  開始／接續全庫補全
                </Button>
              )}
              {autoEnrichStage === 'COMPLETED' &&
                currentAutoEnrichJob &&
                currentAutoEnrichJob.review_count > 0 && (
                  <Button
                    onClick={() => {
                      setShowAutoEnrichAllModal(false);
                      setActiveTab('quality');
                      setQualityActiveTab('drug_ingredients');
                      void loadDrugReviews();
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
                  >
                    前往資料待確認中心
                  </Button>
                )}
            </div>
          </div>
        </div>
      )}

      {/* 西藥專屬批次補全 Modal（極簡兩階段流程） */}
      {showDrugBatchModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-emerald-950 flex items-center gap-2">
                  <span>⚡</span> 批次補全藥品成分（TFDA 官方處方資料庫）
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  依據 TFDA 官方結構化資料，自動比對並補全 A~D 類西藥商品主檔成分。
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDrugBatchModal(false);
                  if (drugBatchStage === 'COMPLETED') setDrugBatchStage('PREVIEW');
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              {drugBatchStage === 'PREVIEW' && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-100 text-xs space-y-2">
                    <div className="font-bold text-emerald-900">📋 本次批次補全範圍：</div>
                    <ul className="list-disc list-inside text-emerald-800 space-y-1">
                      <li>範圍：西藥類商品（商品代號第一碼為 A, B, C, D）</li>
                      <li>
                        資料來源：TFDA 官方藥品許可證與處方成分本地資料庫（免費官方結構化資料）
                      </li>
                      <li>保護機制：已人工確認或鎖定之成分絕不被批次覆蓋</li>
                    </ul>
                  </div>

                  {drugBatchPreviewLoading ? (
                    <Loading label="正在分析西藥資料庫與評估匹配率中…" />
                  ) : drugBatchPreview ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div className="p-3 bg-white border border-slate-200 rounded-xl text-center">
                          <span className="text-slate-500 block">待分析西藥</span>
                          <span className="text-xl font-extrabold text-slate-800 block mt-1">
                            {drugBatchPreview.totalEligible.toLocaleString()} 筆
                          </span>
                        </div>
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-center">
                          <span className="text-emerald-800 font-bold block">✓ 預計自動帶入</span>
                          <span className="text-xl font-extrabold text-emerald-700 block mt-1">
                            {drugBatchPreview.estimatedAutoConfirm.toLocaleString()} 筆
                          </span>
                        </div>
                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-center">
                          <span className="text-amber-800 font-bold block">△ 需人工審查</span>
                          <span className="text-xl font-extrabold text-amber-700 block mt-1">
                            {drugBatchPreview.estimatedNeedsReview.toLocaleString()} 筆
                          </span>
                        </div>
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-center">
                          <span className="text-slate-500 block">○ 查無相符處方</span>
                          <span className="text-xl font-extrabold text-slate-600 block mt-1">
                            {drugBatchPreview.estimatedNotFound.toLocaleString()} 筆
                          </span>
                        </div>
                      </div>

                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
                        已有 {drugBatchPreview.alreadyConfirmedCount.toLocaleString()}{' '}
                        筆西藥商品具備確認處方或鎖定，本次將自動跳過以保護現有資料。
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <Button
                        onClick={() => void handlePreviewDrugBatch()}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
                      >
                        🔍 開始分析西藥商品資料
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {drugBatchStage === 'RUNNING' && (
                <div className="py-8 space-y-4 text-center">
                  <div className="animate-spin text-3xl">💊</div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-bold text-ink">批次補全藥品成分執行中…</h4>
                    <p className="text-xs text-slate-500">
                      系統正分批檢索本地 TFDA 官方資料庫，您可以離開此畫面，背景將持續平穩處理。
                    </p>
                  </div>

                  {currentDrugJob && (
                    <div className="max-w-md mx-auto space-y-2">
                      <div className="flex justify-between text-xs text-slate-600 font-mono">
                        <span>
                          處理進度：{currentDrugJob.processed_count.toLocaleString()} /{' '}
                          {currentDrugJob.total_count.toLocaleString()} 筆
                        </span>
                        <span>
                          {currentDrugJob.total_count > 0
                            ? Math.round(
                                (currentDrugJob.processed_count / currentDrugJob.total_count) * 100,
                              )
                            : 0}
                          %
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                        <div
                          className="bg-emerald-600 h-3 rounded-full transition-all duration-300"
                          style={{
                            width: `${
                              currentDrugJob.total_count > 0
                                ? (currentDrugJob.processed_count / currentDrugJob.total_count) *
                                  100
                                : 0
                            }%`,
                          }}
                        />
                      </div>
                      <div className="flex justify-center gap-4 text-xs pt-2">
                        <span className="text-emerald-700 font-bold">
                          ✓ 自動帶入: {currentDrugJob.auto_confirmed_count.toLocaleString()}
                        </span>
                        <span className="text-amber-700 font-bold">
                          △ 待確認: {currentDrugJob.review_count.toLocaleString()}
                        </span>
                        <span className="text-slate-500">
                          ○ 查無: {currentDrugJob.not_found_count.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {drugBatchStage === 'COMPLETED' && currentDrugJob && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-200 text-center space-y-1">
                    <div className="text-3xl">🎉</div>
                    <h4 className="text-base font-bold text-emerald-900">西藥成分批次補全完成！</h4>
                    <p className="text-xs text-emerald-800">
                      所有處理結果已寫入正式商品主檔與稽核版本紀錄。
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-3 text-center text-xs">
                    <div className="p-3 bg-white border border-emerald-200 rounded-xl">
                      <span className="text-emerald-800 font-bold block">✓ 自動帶入官方處方</span>
                      <span className="text-2xl font-extrabold text-emerald-700 block mt-1">
                        {currentDrugJob.auto_confirmed_count.toLocaleString()}
                      </span>
                    </div>
                    <div className="p-3 bg-white border border-amber-200 rounded-xl">
                      <span className="text-amber-800 font-bold block">△ 進入待確認清單</span>
                      <span className="text-2xl font-extrabold text-amber-700 block mt-1">
                        {currentDrugJob.review_count.toLocaleString()}
                      </span>
                    </div>
                    <div className="p-3 bg-white border border-slate-200 rounded-xl">
                      <span className="text-slate-500 block">○ 未找到可靠處方</span>
                      <span className="text-2xl font-extrabold text-slate-600 block mt-1">
                        {currentDrugJob.not_found_count.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {currentJobItems.length > 0 && (
                    <div className="border border-slate-200 rounded-xl overflow-hidden max-h-48 overflow-y-auto text-xs">
                      <Table>
                        <thead>
                          <tr className="bg-slate-50 text-slate-600">
                            <th className="p-2">商品</th>
                            <th className="p-2">狀態</th>
                            <th className="p-2">比對許可證</th>
                            <th className="p-2">處方成分數</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {currentJobItems.slice(0, 30).map((item) => (
                            <tr key={item.id}>
                              <td className="p-2">
                                <span className="font-bold text-ink">{item.product_name}</span>
                                <span className="text-[10px] text-slate-400 block font-mono">
                                  {item.product_code}
                                </span>
                              </td>
                              <td className="p-2">
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                    item.status === 'AUTO_CONFIRMED'
                                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                      : item.status === 'NEEDS_REVIEW' ||
                                          item.status === 'AMBIGUOUS'
                                        ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                        : 'bg-slate-100 text-slate-500'
                                  }`}
                                >
                                  {item.status === 'AUTO_CONFIRMED'
                                    ? '✓ 自動帶入'
                                    : item.status === 'NEEDS_REVIEW'
                                      ? '△ 待審查'
                                      : item.status === 'AMBIGUOUS'
                                        ? '! 多筆候選'
                                        : item.status === 'SKIPPED_LOCKED'
                                          ? '🔒 已鎖定'
                                          : '○ 未找到'}
                                </span>
                              </td>
                              <td className="p-2 font-mono text-teal-800 font-bold">
                                {item.matched_license_number || '—'}
                              </td>
                              <td className="p-2 font-bold text-slate-700">
                                {item.ingredient_count} 項
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowDrugBatchModal(false);
                  if (drugBatchStage === 'COMPLETED') setDrugBatchStage('PREVIEW');
                }}
              >
                關閉
              </Button>
              {drugBatchStage === 'PREVIEW' && (
                <Button
                  onClick={() => void handleStartDrugBatch()}
                  disabled={drugBatchPreviewLoading || drugBatchExecuting}
                  loading={drugBatchExecuting}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
                >
                  ⚡ 開始補全藥品成分
                </Button>
              )}
              {drugBatchStage === 'COMPLETED' &&
                currentDrugJob &&
                currentDrugJob.review_count > 0 && (
                  <Button
                    onClick={() => {
                      setShowDrugBatchModal(false);
                      setDrugBatchStage('PREVIEW');
                      setActiveTab('quality');
                      setQualityActiveTab('drug_ingredients');
                      void loadDrugReviews();
                    }}
                    className="bg-teal-700 hover:bg-teal-800 text-white font-bold"
                  >
                    🩺 查看待確認藥品 ({currentDrugJob.review_count} 筆)
                  </Button>
                )}
            </div>
          </div>
        </div>
      )}

      {/* 批次自動補全成分 Modal */}
      {showBatchEnrichModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-teal-950 flex items-center gap-2">
                  <span>⚡</span> 批次自動補全商品成分資料
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  依商品身分與分類，自動檢索官方許可證、康健藥品頁及公開可信來源，分批平穩處理。
                </p>
              </div>
              <button
                onClick={() => setShowBatchEnrichModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              <div className="grid grid-cols-2 gap-3 p-4 bg-teal-50/50 rounded-xl border border-teal-100 text-xs">
                <div>
                  <span className="text-slate-500">待補全缺成分總數：</span>
                  <span className="font-bold text-teal-800 text-sm block mt-0.5">
                    {enrichmentSummary
                      ? enrichmentSummary.missingIngredients.toLocaleString()
                      : '—'}{' '}
                    筆
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">已確認成分商品：</span>
                  <span className="font-bold text-emerald-700 text-sm block mt-0.5">
                    {enrichmentSummary ? enrichmentSummary.confirmed.toLocaleString() : '—'} 筆
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-xs font-bold text-slate-700">
                  指定大分類（選填，預設全庫缺成分商品）：
                </label>
                <Select value={batchCategory} onChange={(e) => setBatchCategory(e.target.value)}>
                  <option value="">全部大分類 (A~P)</option>
                  {Object.entries(HUI_SHENG_CATEGORY_MAP).map(([code, name]) => (
                    <option key={code} value={code}>
                      {code} - {name}
                    </option>
                  ))}
                </Select>
              </div>

              {batchEnriching && (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-center space-y-2">
                  <div className="animate-spin text-2xl">⚙️</div>
                  <div className="text-xs font-bold text-teal-800">{batchEnrichProgress}</div>
                  <div className="text-[11px] text-slate-400">系統分批檢索中，請勿關閉視窗…</div>
                </div>
              )}

              {batchEnrichResults.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold text-slate-700">本次批次處理結果：</h4>
                  <div className="border border-slate-200 rounded-xl overflow-hidden max-h-56 overflow-y-auto text-xs">
                    <Table>
                      <thead>
                        <tr className="bg-slate-50 text-slate-600">
                          <th className="p-2">商品</th>
                          <th className="p-2">狀態</th>
                          <th className="p-2">信心度</th>
                          <th className="p-2">查得來源</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {batchEnrichResults.map((r, idx) => (
                          <tr key={idx}>
                            <td className="p-2">
                              <span className="font-bold text-ink">{r.productName}</span>
                              <span className="text-[10px] text-slate-400 block">
                                {r.productCode}
                              </span>
                            </td>
                            <td className="p-2">
                              <span
                                className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                  r.status === 'FOUND_UNCONFIRMED'
                                    ? 'bg-amber-50 text-amber-800'
                                    : r.status === 'CONFIRMED'
                                      ? 'bg-emerald-50 text-emerald-700'
                                      : 'bg-slate-100 text-slate-500'
                                }`}
                              >
                                {SEARCH_PROVIDER_STATUS_LABELS[r.status] || r.status}
                              </span>
                            </td>
                            <td className="p-2 font-bold text-teal-700">{r.score}%</td>
                            <td
                              className="p-2 text-slate-600 truncate max-w-[140px]"
                              title={r.sourceName}
                            >
                              {r.sourceName || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setShowBatchEnrichModal(false)}>
                關閉
              </Button>
              <Button
                onClick={() => void handleBatchEnrich()}
                loading={batchEnriching}
                className="bg-teal-700"
              >
                ⚡ 開始自動補全 (每批20筆)
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 候選成分來源與明細抽屜 Modal */}
      {selectedCandidateProduct && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full p-6 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-start border-b border-slate-100 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded font-mono text-xs font-bold">
                    {selectedCandidateProduct.product_code}
                  </span>
                  <h3 className="text-base font-bold text-ink">
                    {selectedCandidateProduct.product_name}
                  </h3>
                </div>
                <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  <span>
                    規格：
                    {selectedCandidateProduct.specification ||
                      selectedCandidateProduct.sales_unit ||
                      '未設定'}
                  </span>
                  <span className="flex items-center gap-1">
                    條碼：
                    {normalizeBarcodeCandidate(selectedCandidateProduct.primary_barcode_candidate)
                      .status === 'BARCODE_REPAIR_REQUIRED' ? (
                      <span
                        className="px-1.5 py-0.2 rounded bg-amber-50 text-amber-800 font-bold border border-amber-200 text-[10px]"
                        title="原始條碼為科學記號且已遺失末尾數字，待來源修復"
                      >
                        條碼待修復
                      </span>
                    ) : (
                      <span>
                        {displayBarcodeCandidate(
                          selectedCandidateProduct.primary_barcode_candidate,
                        )}
                      </span>
                    )}
                  </span>
                  <span>分類：{selectedCandidateProduct.product_category_code || '—'}</span>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedCandidateProduct(null);
                  setCandidateDrawerData(null);
                }}
                className="text-slate-400 hover:text-slate-600 text-lg"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              {/* TFDA 官方藥品資料卡片（若為西藥或已關聯許可證） */}
              {selectedCandidateProduct &&
                (['A', 'B', 'C', 'D'].includes(
                  (
                    selectedCandidateProduct.product_category_code ||
                    selectedCandidateProduct.product_code?.charAt(0) ||
                    ''
                  ).toUpperCase(),
                ) ||
                  selectedCandidateProduct.regulatory_license_number) && (
                  <div className="space-y-3 rounded-xl border border-emerald-300 bg-emerald-50/50 p-4 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="text-base font-bold text-emerald-950 flex items-center gap-1.5">
                          💊 TFDA 官方藥品處方與監管資訊
                        </span>
                        {selectedCandidateProduct.regulatory_license_number && (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold border border-emerald-300">
                            ✓ 官方匹配 ({selectedCandidateProduct.regulatory_match_score || 100}分)
                          </span>
                        )}
                        {selectedCandidateProduct.ingredient_locked ? (
                          <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-800 text-[11px] font-bold">
                            🔒 採購已鎖定
                          </span>
                        ) : null}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => void handleSingleDrugEnrich(selectedCandidateProduct.id)}
                          loading={enrichingSingleDrugId === selectedCandidateProduct.id}
                          className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                        >
                          ⚡ 重新匹配 TFDA 處方
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={async () => {
                            const newLocked = !selectedCandidateProduct.ingredient_locked;
                            await api.productIngredients.lockDrugIngredients(
                              selectedCandidateProduct.id,
                              newLocked,
                            );
                            notify(
                              newLocked ? '已鎖定成分，批次作業將跳過此商品。' : '已解除鎖定。',
                            );
                            setSelectedCandidateProduct({
                              ...selectedCandidateProduct,
                              ingredient_locked: newLocked ? 1 : 0,
                            });
                            void loadItems();
                          }}
                          className="text-xs"
                        >
                          {selectedCandidateProduct.ingredient_locked
                            ? '🔓 解除鎖定'
                            : '🔒 鎖定成分'}
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs bg-white p-3 rounded-lg border border-emerald-200">
                      <div>
                        <span className="text-slate-400 block">許可證字號：</span>
                        <span className="font-mono font-bold text-teal-800 text-sm">
                          {selectedCandidateProduct.regulatory_license_number ||
                            '尚未正式關聯許可證'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block">官方核准品名：</span>
                        <span className="font-bold text-slate-800">
                          {selectedCandidateProduct.official_product_name || '—'}
                        </span>
                        {selectedCandidateProduct.official_english_name && (
                          <span className="text-[10px] text-slate-400 block">
                            {selectedCandidateProduct.official_english_name}
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-slate-400 block">製造廠商：</span>
                        <span className="text-slate-700">
                          {selectedCandidateProduct.official_manufacturer_name || '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-400 block">劑型規格：</span>
                        <span className="text-slate-700">
                          {selectedCandidateProduct.official_dosage_form || '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              {!loadingCandidates && candidateDrawerData && (
                <div className="space-y-3 rounded-xl border border-teal-200 bg-teal-50/40 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-bold text-teal-900">AI智慧查找成分</h4>
                      <p className="mt-0.5 text-xs text-teal-700">
                        AI
                        負責分類、身分匹配、結構化與來源比較；資料只取自惠生已確認資料、免費政府資料、康健藥品頁、官方網站及正式附件。
                      </p>
                    </div>
                    <span className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-teal-800 ring-1 ring-teal-200">
                      {SEARCH_PROVIDER_STATUS_LABELS[
                        candidateDrawerData.searchRun?.providerStatus || 'PLANNED'
                      ] || '搜尋狀態待確認'}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-white px-2.5 py-1 font-bold text-slate-700 ring-1 ring-teal-100">
                      商品類型：
                      {PRODUCT_TYPE_LABELS[
                        candidateDrawerData.searchRun?.productType ||
                          candidateDrawerData.product?.ingredient_product_type ||
                          ''
                      ] || '待分析'}
                    </span>
                    <span className="rounded-full bg-white px-2.5 py-1 text-slate-600 ring-1 ring-teal-100">
                      判斷器：
                      {candidateDrawerData.searchRun?.aiProvider === 'RULE_BASED_V1'
                        ? '免費規則式 AI'
                        : candidateDrawerData.searchRun?.aiProvider || '待執行'}
                    </span>
                    <span className="rounded-full bg-white px-2.5 py-1 font-bold text-emerald-700 ring-1 ring-emerald-100">
                      ZERO_PAID_WEB_SEARCH
                    </span>
                  </div>

                  <div>
                    <div className="mb-1.5 text-xs font-bold text-slate-700">查找進度</div>
                    <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-5">
                      {LOOKUP_STAGES.map((stage, stageIndex) => {
                        const traces = lookupTrace.filter((trace) =>
                          (stage.providers as readonly string[]).includes(trace.provider),
                        );
                        const restricted = traces.some(
                          (trace) => trace.state === 'SOURCE_RESTRICTED',
                        );
                        const completed = traces.length > 0;
                        const active =
                          enrichingSingleId === selectedCandidateProduct.id &&
                          lookupProgressIndex === stageIndex;
                        const passed =
                          enrichingSingleId === selectedCandidateProduct.id &&
                          lookupProgressIndex !== null &&
                          stageIndex < lookupProgressIndex;
                        return (
                          <div
                            key={stage.key}
                            className={`rounded-lg border px-2.5 py-2 text-xs ${
                              active
                                ? 'border-teal-400 bg-white text-teal-800'
                                : restricted
                                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                                  : completed || passed
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                                    : 'border-slate-200 bg-white/70 text-slate-500'
                            }`}
                          >
                            <span className="mr-1 font-bold">
                              {active ? '◌' : restricted ? '△' : completed || passed ? '✓' : '○'}
                            </span>
                            {stage.label}
                            {completed && !active && (
                              <span className="mt-0.5 block text-[10px] opacity-75">
                                {traces.some((trace) => trace.fromCache)
                                  ? '已檢查（使用快取）'
                                  : '已完成檢查'}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <div className="mb-1.5 text-xs font-bold text-slate-700">查找依據</div>
                    <div className="grid gap-1.5 sm:grid-cols-2">
                      {[
                        {
                          label: '商品名稱',
                          value: selectedCandidateProduct.product_name || '待補資料',
                          ready: Boolean(selectedCandidateProduct.product_name),
                        },
                        {
                          label: '規格',
                          value:
                            selectedCandidateProduct.specification ||
                            selectedCandidateProduct.sales_unit ||
                            '待補資料',
                          ready: Boolean(
                            selectedCandidateProduct.specification ||
                            selectedCandidateProduct.sales_unit,
                          ),
                        },
                        {
                          label: 'GTIN',
                          value:
                            selectedGtin ||
                            (normalizeBarcodeCandidate(
                              selectedCandidateProduct.primary_barcode_candidate,
                            ).status === 'BARCODE_REPAIR_REQUIRED'
                              ? '條碼待修復（依品名規格繼續查找）'
                              : '待修復或尚未提供'),
                          ready: Boolean(selectedGtin),
                        },
                        {
                          label: '分類',
                          value:
                            [
                              selectedCandidateProduct.product_subcategory_code ||
                                selectedCandidateProduct.imported_subcategory_code ||
                                selectedCandidateProduct.product_category_code,
                              selectedCandidateProduct.imported_subcategory_name ||
                                selectedCandidateProduct.imported_category_name,
                            ]
                              .filter(Boolean)
                              .join('｜') || '待補資料',
                          ready: Boolean(
                            selectedCandidateProduct.product_subcategory_code ||
                            selectedCandidateProduct.imported_subcategory_code ||
                            selectedCandidateProduct.product_category_code,
                          ),
                        },
                      ].map((basis) => (
                        <div
                          key={basis.label}
                          className="rounded-lg border border-white bg-white/80 px-2.5 py-2 text-xs text-slate-700"
                        >
                          <span
                            className={`mr-1 font-bold ${basis.ready ? 'text-emerald-600' : 'text-amber-600'}`}
                          >
                            {basis.ready ? '✓' : '△'}
                          </span>
                          <span className="font-bold">{basis.label}：</span>
                          <span className="break-all">{basis.value}</span>
                        </div>
                      ))}
                    </div>
                    {candidateDrawerData.searchPlan?.searchNameNormalized && (
                      <p className="mt-2 text-[11px] text-slate-500">
                        搜尋用正規化品名：{candidateDrawerData.searchPlan.searchNameNormalized}
                      </p>
                    )}
                  </div>

                  <details className="rounded-lg border border-teal-100 bg-white">
                    <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-slate-700">
                      人工搜尋關鍵字（選用，共 {displayedSearchQueries.length} 組）
                    </summary>
                    <div className="max-h-56 space-y-1.5 overflow-y-auto border-t border-teal-100 p-2.5">
                      {displayedSearchQueries.map((query) => (
                        <div
                          key={query.id}
                          className="flex items-start justify-between gap-2 rounded-md bg-slate-50 px-2.5 py-2 text-xs"
                        >
                          <div className="min-w-0">
                            <div className="break-words font-mono text-slate-800">
                              {query.query}
                            </div>
                            <div className="mt-0.5 text-[10px] text-slate-500">
                              第 {query.priority} 順位・{query.reason}
                              {query.executed ? `・取得 ${query.resultCount} 筆原始結果` : ''}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-1.5">
                            <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-600">
                              {SEARCH_QUERY_STATUS_LABELS[query.status] || query.status}
                            </span>
                            <a
                              href={manualGoogleSearchUrl(query.query)}
                              target="_blank"
                              rel="noreferrer"
                              className="rounded bg-teal-700 px-2 py-1 text-[10px] font-bold text-white hover:bg-teal-800"
                            >
                              搜尋網路
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>

                  {canViewIngredientDebug && identityDebug && (
                    <details className="rounded-lg border border-slate-700 bg-slate-950 text-slate-100">
                      <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-teal-400 hover:text-teal-300">
                        🔍 查看AI查找紀錄與診斷（Admin / Dev）
                      </summary>
                      <div className="grid gap-2 border-t border-slate-800 p-3 text-[11px] sm:grid-cols-2">
                        <div>
                          <span className="text-slate-400">正規化品名：</span>
                          {identityDebug.normalizedProductName || '—'}
                        </div>
                        <div>
                          <span className="text-slate-400">基礎品名：</span>
                          {identityDebug.baseProductName || '—'}
                        </div>
                        <div>
                          <span className="text-slate-400">解析規格：</span>
                          {identityDebug.specification || '—'}
                        </div>
                        <div>
                          <span className="text-slate-400">銷售單位：</span>
                          {identityDebug.salesUnit || '—'}
                        </div>
                        <div>
                          <span className="text-slate-400">原始條碼狀態：</span>
                          <span
                            className={
                              identityDebug.barcodeStatus === 'BARCODE_REPAIR_REQUIRED'
                                ? 'text-amber-400 font-bold'
                                : 'text-slate-200'
                            }
                          >
                            {identityDebug.barcodeStatus || 'MISSING'}
                          </span>
                        </div>
                        <div className="break-all">
                          <span className="text-slate-400">原始條碼文字：</span>
                          {(identityDebug.barcodeRawValues || []).join('、') || '—'}
                        </div>
                        <div className="break-all">
                          <span className="text-slate-400">可用 GTIN：</span>
                          {(identityDebug.validGtins || []).join('、') ||
                            '無（已啟用品名規格查找）'}
                        </div>
                        <div>
                          <span className="text-slate-400">系統推測品牌：</span>
                          <span className="text-emerald-400 font-bold">
                            {identityDebug.brandCandidate || '—'}
                          </span>
                          {identityDebug.brandCandidateMatchedAlias
                            ? `（命中別名：${identityDebug.brandCandidateMatchedAlias}）`
                            : ''}
                        </div>
                        <div>
                          <span className="text-slate-400">主要成分完整度：</span>
                          {candidateDrawerData.sources[0]?.completeness
                            ? INGREDIENT_COMPLETENESS_LABELS[
                                candidateDrawerData.sources[0].completeness
                              ] || candidateDrawerData.sources[0].completeness
                            : '待確認'}
                        </div>
                        <div>
                          <span className="text-slate-400">主要來源層級：</span>
                          {candidateDrawerData.sources[0]?.source_level
                            ? INGREDIENT_SOURCE_LEVEL_LABELS[
                                candidateDrawerData.sources[0].source_level
                              ] || candidateDrawerData.sources[0].source_level
                            : '待確認'}
                        </div>
                        <div className="sm:col-span-2">
                          <span className="text-slate-400">最終狀態判定：</span>
                          <span className="font-bold text-teal-300">
                            {candidateDrawerData.searchRun?.providerStatus || 'PLANNED'}
                          </span>
                        </div>
                        <div className="sm:col-span-2 space-y-1">
                          <span className="text-slate-400">來源執行追蹤紀錄：</span>
                          {lookupTrace.length === 0 ? (
                            <div>尚無執行紀錄</div>
                          ) : (
                            lookupTrace.map((trace, index) => (
                              <div
                                key={`${trace.provider}-${index}`}
                                className="font-mono text-[10px] text-slate-300"
                              >
                                {trace.provider} → {trace.state}
                                {trace.fromCache ? ' [CACHE]' : ''}
                                {trace.reasonCode ? ` (${trace.reasonCode})` : ''}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </details>
                  )}

                  <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                    <Input
                      id="manual-source-url-input"
                      label="官方商品頁網址（選填）"
                      type="url"
                      value={manualSourceUrl}
                      placeholder="貼上品牌官網、TFDA 或其他公開商品頁網址"
                      onChange={(event) => setManualSourceUrl(event.target.value)}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!manualSourceUrl.trim()}
                      onClick={() =>
                        void handleSingleEnrich(
                          selectedCandidateProduct.id,
                          supplementalSearchKeyword,
                          manualSourceUrl,
                        )
                      }
                      loading={enrichingSingleId === selectedCandidateProduct.id}
                    >
                      讀取官方網址
                    </Button>
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                    <span>系統只讀取公開頁面，不會繞過登入、驗證碼或網站存取限制。</span>
                    {displayedSearchQueries[0]?.query && (
                      <a
                        href={manualGoogleSearchUrl(displayedSearchQueries[0].query)}
                        target="_blank"
                        rel="noreferrer"
                        className="font-bold text-teal-700 hover:underline"
                      >
                        搜尋網路（瀏覽器）↗
                      </a>
                    )}
                  </div>

                  <div className="rounded-lg border border-teal-100 bg-white p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-xs font-bold text-slate-700">商品成分／標示附件</div>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          支援 PDF、JPG、PNG、WEBP，單檔最大 10 MB；附件內容仍須由採購核對。
                        </p>
                      </div>
                      <label
                        className={`inline-flex cursor-pointer items-center rounded-lg border px-3 py-2 text-xs font-bold ${
                          uploadingIngredientLabel
                            ? 'cursor-wait border-slate-200 bg-slate-100 text-slate-400'
                            : 'border-teal-300 bg-teal-50 text-teal-800 hover:bg-teal-100'
                        }`}
                      >
                        {uploadingIngredientLabel ? '上傳中…' : '上傳商品標示'}
                        <input
                          type="file"
                          className="hidden"
                          accept="application/pdf,image/jpeg,image/png,image/webp"
                          disabled={uploadingIngredientLabel}
                          onChange={(event) => void handleIngredientLabelUpload(event)}
                        />
                      </label>
                    </div>
                    {(candidateDrawerData.labelAttachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {candidateDrawerData.labelAttachments.map((attachment) => (
                          <a
                            key={attachment.id}
                            href={attachment.view_url}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md bg-slate-50 px-2.5 py-1.5 text-[11px] font-medium text-teal-700 hover:underline"
                          >
                            {attachment.original_name}（
                            {(attachment.size_bytes / 1024 / 1024).toFixed(2)} MB）↗
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
                    <Input
                      label="補充搜尋關鍵字（選填）"
                      value={supplementalSearchKeyword}
                      maxLength={80}
                      placeholder="例如：新配方、葡萄口味、兒童、2026新版"
                      onChange={(event) => setSupplementalSearchKeyword(event.target.value)}
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        void handleSingleEnrich(
                          selectedCandidateProduct.id,
                          supplementalSearchKeyword,
                        )
                      }
                      loading={enrichingSingleId === selectedCandidateProduct.id}
                    >
                      重新執行AI智慧查找
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    補充詞只協助建立人工搜尋關鍵字，不會呼叫付費搜尋 API。
                  </p>
                </div>
              )}

              {loadingCandidates ? (
                <Loading label="讀取成分候選來源中…" />
              ) : !candidateDrawerData || candidateDrawerData.sources.length === 0 ? (
                <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200 space-y-3">
                  <div className="text-3xl">🔍</div>
                  <h4 className="text-sm font-bold text-slate-700">
                    {SEARCH_PROVIDER_STATUS_LABELS[
                      candidateDrawerData?.searchRun?.providerStatus || 'NO_SOURCE'
                    ] || '目前沒有可用的成分候選。'}
                  </h4>
                  <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    系統已保留商品識別、來源執行狀態與安全錯誤代碼；條碼無法安全還原時仍會依品名、規格、品牌候選與分類繼續查找。
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Button
                      onClick={() => void handleSingleEnrich(selectedCandidateProduct.id)}
                      loading={enrichingSingleId === selectedCandidateProduct.id}
                      className="bg-teal-700"
                    >
                      ✨ 開始AI智慧查找
                    </Button>
                    {displayedSearchQueries[0]?.query && (
                      <a
                        href={manualGoogleSearchUrl(displayedSearchQueries[0].query)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      >
                        搜尋網路 ↗
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {sourceComparison?.hasConflict && (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
                      <div className="font-bold">△ 來源資料不一致</div>
                      <p className="mt-1 text-xs">{sourceComparison.summary}</p>
                      {(sourceComparison.differences || []).length > 0 && (
                        <div className="mt-3 overflow-x-auto rounded-lg border border-amber-200 bg-white">
                          <Table>
                            <thead>
                              <tr className="bg-amber-100/60 text-xs text-amber-900">
                                <th className="p-2">差異成分</th>
                                <th className="p-2">來源一</th>
                                <th className="p-2">來源二</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-amber-100 text-xs">
                              {(sourceComparison.differences || [])
                                .slice(0, 20)
                                .map((difference, index) => (
                                  <tr key={`${difference.ingredient}-${difference.field}-${index}`}>
                                    <td className="p-2 font-bold">{difference.ingredient}</td>
                                    <td className="p-2">
                                      <div className="font-medium">{difference.sourceA}</div>
                                      <div>{difference.valueA}</div>
                                    </td>
                                    <td className="p-2">
                                      <div className="font-medium">{difference.sourceB}</div>
                                      <div>{difference.valueB}</div>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                      <p className="mt-2 text-xs font-bold">
                        系統不會自動選擇，請由採購逐一核對來源。
                      </p>
                    </div>
                  )}
                  {(candidateDrawerData.ingredientVersions || []).length > 0 && (
                    <details className="rounded-xl border border-slate-200 bg-slate-50">
                      <summary className="cursor-pointer px-4 py-3 text-xs font-bold text-slate-700">
                        已確認配方版本（{candidateDrawerData.ingredientVersions.length}）
                      </summary>
                      <div className="space-y-2 border-t border-slate-200 p-3">
                        {candidateDrawerData.ingredientVersions.map((version) => (
                          <div
                            key={version.id}
                            className="rounded-lg border border-slate-200 bg-white p-3 text-xs"
                          >
                            <div className="flex flex-wrap justify-between gap-2 font-bold text-slate-800">
                              <span>{version.version_label}</span>
                              <span>{version.confirmed_at || version.created_at}</span>
                            </div>
                            <div className="mt-1 text-slate-600">
                              {version.ingredient_raw_text || '未載明成分文字'}
                            </div>
                            <div className="mt-1 text-[11px] text-slate-500">
                              來源層級：
                              {INGREDIENT_SOURCE_LEVEL_LABELS[version.source_level] ||
                                version.source_level ||
                                '待確認'}
                              {version.source_date ? `・來源日期：${version.source_date}` : ''}
                            </div>
                            {version.formula_version_warning && (
                              <div className="mt-1 text-[11px] text-amber-700">
                                △ {version.formula_version_warning}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                  {candidateDrawerData.sources.map((src) => {
                    const isConfirmed = src.status === 'CONFIRMED';
                    const isRejected = src.status === 'REJECTED';
                    const sourceIngredients = candidateDrawerData.ingredients.filter(
                      (ingredient) => ingredient.source_id === src.id,
                    );
                    const srcLevelTag =
                      src.source_type === 'INTERNAL_CONFIRMED'
                        ? '惠生已確認資料'
                        : src.source_type === 'GOVERNMENT_OFFICIAL' ||
                            src.source_type === 'A_OFFICIAL_GOV'
                          ? 'TFDA 官方政府資料'
                          : src.source_type === 'BRAND_OFFICIAL' ||
                              src.source_type === 'A_BRAND_OFFICIAL'
                            ? '品牌/製造商官方'
                            : src.source_type === 'MANUFACTURER_OFFICIAL' ||
                                src.source_type === 'A_MANUFACTURER_OFFICIAL'
                              ? '製造商官方'
                              : src.source_type === 'VENDOR_DOCUMENT' ||
                                  src.source_type === 'A_OFFICIAL_DOCUMENT'
                                ? '廠商正式附件／標示'
                                : src.source_type === 'MANUAL_URL'
                                  ? '採購提供可信網址'
                                  : src.source_type === 'C_OTHER_PUBLIC'
                                    ? '康健／其他公開來源'
                                    : src.source_type === 'B_RETAILER'
                                      ? '公開零售通路'
                                      : src.source_type === 'MANUAL'
                                        ? '採購人工維護'
                                        : '其他公開來源';

                    return (
                      <div
                        key={src.id}
                        className={`p-4 rounded-xl border transition ${
                          isConfirmed
                            ? 'border-emerald-500 bg-emerald-50/20 ring-2 ring-emerald-500/20'
                            : isRejected
                              ? 'border-slate-200 bg-slate-50/60 opacity-60'
                              : 'border-slate-200 bg-white hover:border-teal-300'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              <span
                                className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                  src.source_type === 'GOVERNMENT_OFFICIAL' ||
                                  src.source_type === 'A_OFFICIAL_GOV'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : src.source_type === 'BRAND_OFFICIAL' ||
                                        src.source_type === 'A_BRAND_OFFICIAL'
                                      ? 'bg-sky-100 text-sky-800'
                                      : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                [{srcLevelTag}]
                              </span>
                              <span className="font-bold text-sm text-ink">{src.source_name}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-2">
                              <span>
                                比對方式：{MATCHED_BY_LABELS[src.matched_by] || src.matched_by}
                              </span>
                              <span>
                                完整度：
                                {INGREDIENT_COMPLETENESS_LABELS[src.completeness] ||
                                  src.completeness}
                              </span>
                              <span>
                                來源層級：
                                {INGREDIENT_SOURCE_LEVEL_LABELS[src.source_level] ||
                                  src.source_level ||
                                  '待確認'}
                              </span>
                              {src.source_date && <span>來源日期：{src.source_date}</span>}
                              {src.ingredient_type && src.ingredient_type !== 'UNKNOWN' && (
                                <span>資料類型：{src.ingredient_type}</span>
                              )}
                            </div>
                            {src.match_value && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {String(src.match_value)
                                  .split('；')
                                  .filter(Boolean)
                                  .map((reason: string) => (
                                    <span
                                      key={reason}
                                      className="rounded bg-emerald-50 px-2 py-1 text-[10px] font-medium text-emerald-700"
                                    >
                                      ✓ {reason}
                                    </span>
                                  ))}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 text-xs font-bold">
                              信心度 {src.confidence_score}%
                            </span>
                            {isConfirmed && (
                              <span className="block mt-1 text-[10px] text-emerald-700 font-bold">
                                ✓ 目前正式採納來源
                              </span>
                            )}
                            {isRejected && (
                              <span className="block mt-1 text-[10px] text-rose-600 font-bold">
                                ✕ 已排除
                              </span>
                            )}
                          </div>
                        </div>

                        {src.completeness === 'KEY_INGREDIENTS_ONLY' ? (
                          <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 p-2.5 text-xs text-emerald-950">
                            <span className="font-bold">
                              ✓ 已找到官方商品資料，但目前只有主要成分。
                            </span>
                            <div className="mt-0.5 text-[11px] text-emerald-800">
                              此官方來源載明核心/有效成分，未列出完整原料表（水、糖、賦形劑等）；採購可採納此主要成分，或繼續查找完整原料。
                            </div>
                          </div>
                        ) : ['PARTIAL', 'NUTRITION_ONLY'].includes(src.completeness) ? (
                          <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-900">
                            已找到可信的部分資料；此內容不是完整配方，系統會保留完整度標示。
                          </div>
                        ) : null}

                        {src.formula_version_warning && (
                          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                            △ {src.formula_version_warning}
                          </div>
                        )}

                        {/* 原始成分文字 */}
                        <div className="mt-3 p-2.5 bg-slate-50 rounded-lg border border-slate-100 text-xs text-slate-700">
                          <span className="font-bold text-slate-500 block mb-1">
                            {sourceIngredients.length > 0 ? '成分內容原文：' : '搜尋結果摘要：'}
                          </span>
                          {src.raw_ingredient_text || '無詳細文字'}
                          {src.source_url && (
                            <a
                              href={src.source_url}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 block font-bold text-teal-700 hover:underline"
                            >
                              開啟來源核對完整內容 ↗
                            </a>
                          )}
                          {src.search_query && (
                            <div className="mt-1 break-words text-[10px] text-slate-400">
                              命中關鍵字：{src.search_query}
                            </div>
                          )}
                        </div>

                        {Array.isArray(src.warnings) && src.warnings.length > 0 && (
                          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">
                            {src.warnings.map((warning: string) => (
                              <div key={warning}>△ {warning}</div>
                            ))}
                          </div>
                        )}

                        {/* 結構化清單 */}
                        {sourceIngredients.length > 0 && (
                          <div className="mt-3 border border-slate-200 rounded-lg overflow-hidden text-xs">
                            <Table>
                              <thead>
                                <tr className="bg-slate-50 text-slate-600">
                                  <th className="p-2">成分名稱</th>
                                  <th className="p-2">標準化識別</th>
                                  <th className="p-2 text-right">含量 / 單位</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {sourceIngredients.map((ing) => (
                                  <tr key={ing.id}>
                                    <td className="p-2 font-bold text-ink">
                                      {ing.ingredient_name_raw}
                                    </td>
                                    <td className="p-2 font-mono text-teal-700 text-[11px]">
                                      {ing.ingredient_name_normalized}
                                    </td>
                                    <td className="p-2 text-right font-medium">
                                      {ing.amount !== null
                                        ? `${ing.amount} ${ing.unit || ''}`
                                        : '未載明含量'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}

                        {/* 操作按鈕 */}
                        {!isRejected && (
                          <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end gap-2 text-xs">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void handleRejectSource(src.id)}
                              loading={rejectingSourceId === src.id}
                              className="text-rose-600 hover:bg-rose-50"
                            >
                              ✕ 排除此來源
                            </Button>
                            {!isConfirmed && sourceIngredients.length > 0 && (
                              <Button
                                size="sm"
                                onClick={() => void handleConfirmSource(src.id)}
                                loading={confirmingSourceId === src.id}
                                className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold"
                              >
                                ✓ 確認採納此來源為正式商品成分
                              </Button>
                            )}
                            {!isConfirmed && sourceIngredients.length === 0 && (
                              <span className="self-center text-[11px] text-amber-700">
                                此來源只有搜尋摘要，請先開啟來源核對後再人工建檔。
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-slate-100">
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void handleSingleEnrich(
                      selectedCandidateProduct.id,
                      supplementalSearchKeyword,
                      manualSourceUrl,
                    )
                  }
                  loading={enrichingSingleId === selectedCandidateProduct.id}
                >
                  🔄 依目前條件重新查找
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    const prod = selectedCandidateProduct;
                    setSelectedCandidateProduct(null);
                    setCandidateDrawerData(null);
                    openEditModal(prod);
                  }}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs"
                >
                  ✍️ 自行填寫／編輯成分
                </Button>
              </div>
              <Button
                onClick={() => {
                  setSelectedCandidateProduct(null);
                  setCandidateDrawerData(null);
                }}
              >
                關閉
              </Button>
            </div>
          </div>
        </div>
      )}
      <SupplierRosterModal
        isOpen={rosterModalOpen}
        onClose={() => setRosterModalOpen(false)}
        params={rosterParams}
        scopeTitle={rosterScopeTitle}
        onNotify={notify}
      />
    </div>
  );
}
